// Import react elements
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

// Import context function
import { UserContext } from "global/context/UserContext";
import { AuthContext } from "global/context/AuthContext";
import { useCustomizr } from "global/hooks/useCustomizr";

// Import strich api calls
import {
    getPrintAreaRoles,
    getPodDesigns,
    getPodDesign,
    addPodDesign,
    updatePodDesignRoles,
    updatePodDesignProducts,
    updatePodDesignProductImages,
    updatePodDesignProductRoles,
    updatePodDesignProductTranslations,
    updatePodDesignTranslations,
    updatePodDesignDetails,
    deletePodDesign,
    deleteDesignProduct,
    addPodTemplate,
    updatePodTemplateRoles,
} from "api/APIStrichCalls";

// Import hooks
import usePodProductData from "../hooks/usePodDesignData";
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";
import useIsMobile from "global/hooks/useIsMobile";
// Handles popup button actions like reuse, save, and open in Customizr.
import useQueueItemPopupActions from "pages/ai_image_generation/hooks/useQueueItemPopupActions";

// Import components
import CreateButton from 'global/components/CreateButton';
import KeywordTagsInput from "global/components/KeywordTagsInput";
import RichTextInput from "global/components/RichTextInput";
import CustomDropdownInput from "global/components/CustomDropdownInput";
import ToggleSwitch from "global/components/ToggleSwitch"
import RenderImageUploader from "global/components/ImageUploader"
import LoadingScreen from 'global/components/loading_ui/LoadingScreen';
import { ColorPickerSmall } from "global/components/ColourPicker";
import  PodProductSelector  from "./PodProductsSelector";
import TemplatePreviewImagePicker from "./TemplatePreviewImagePicker";
import PodDesignAiPage from "../ai_pod_design/PodDesignAiPage";
import PodDesignUpscalePage from "../ai_pod_upscale/PodDesignUpscalePage";
import ImageCarouselPopup from "global/components/ImageCarouselPopup";
import { InfoButton } from "global/components/InfoButton";

// Import icons
import { CloseIcon, WarningIcon } from "global/strich_icons/StrichIcons";

// Import the reusable RoleVisual
import RoleVisual from "./RoleVisual";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { formatDisplayDateTime } from "global/utilities/ConvertDate";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import { deepEqual } from "global/utilities/CheckData";
import { normaliseColourwayCodes } from "../utilities/PodProductColourwayHelpers";
import { normaliseRotationConfig, removeIncompatibleMockupImages } from "../utilities/PodProductViewSettingsHelpers";

// Build the readable label used for a selectable POD template
const getPodTemplateLabel = (template) => {

    // Find the English template title
    const englishTranslation = Array.isArray(template?.translations)
        ? template.translations.find((translation) => translation?.language === "EN")
        : null;

    // Use the best available template name
    let templateLabel =
        englishTranslation?.title ||
        template?.title ||
        template?.code ||
        "Template " + template?.id;

    // Identify maintained Strich templates
    if (template?.source === "strich") { templateLabel += " (Strich)"; }

    return templateLabel;

};


// Check whether a design or template role still has an image that can be printed
const designRoleHasUsableImage = (roleData) => {

    // Stop when there is no role data
    if (!roleData) { return false; }

    // Accept saved assets, uploaded images and image URLs used by the editor
    return Boolean(
        roleData?.asset_id ||
        roleData?.image_id ||
        roleData?.print_asset_id ||
        roleData?.image_url ||
        roleData?.thumbnail_url ||
        roleData?.uploadedImage ||
        roleData?.imageDetails?.imageBucketId ||
        roleData?.imageDetails?.image_url
    );

};



// Normalise design roles to only the values controlled by the normal design Save button
const normaliseDesignRolesForEditedCheck = (roles) => {

    // Read roles safely
    const safeRoles = Array.isArray(roles) ? roles : [];

    // Build a stable comparable role list
    return safeRoles.map((roleItem) => {

        // Prefer a saved asset id, then an image bucket id held by a newly-selected image
        const assetId =
            roleItem?.asset_id ||
            roleItem?.imageDetails?.imageBucketId ||
            null;

        // Treat transparent and empty background values as the same thing
        const backgroundColour =
            !roleItem?.background_colour ||
            roleItem?.background_colour === "transparent"
                ? null
                : String(roleItem.background_colour);

        // Return only fields which belong to the design role save
        return {
            role: String(roleItem?.role || ""),
            asset_type: String(roleItem?.asset_type || "I"),
            asset_id: assetId !== null && assetId !== undefined ? String(assetId) : "",
            image_source: assetId
                ? ""
                : String(
                    roleItem?.uploadedImage ||
                    roleItem?.imageDetails?.image_url ||
                    roleItem?.image_url ||
                    ""
                ),
            fit_fill: String(roleItem?.fit_fill || "fit").toLowerCase(),
            background_colour: backgroundColour,
        };

    }).sort((firstRole, secondRole) => {

        return firstRole.role.localeCompare(secondRole.role);

    });

};



// Normalise configured product roles edited directly on the main design page
const normaliseProductGridRolesForEditedCheck = (roles) => {

    // Read roles safely
    const safeRoles = Array.isArray(roles) ? roles : [];

    // Return only role values which can be changed from the main design screen
    return safeRoles.map((roleItem) => {

        return {
            role: String(roleItem?.role || ""),
            active: String(roleItem?.active ?? "Y"),
            background_colour: roleItem?.background_colour ?? null,
            edited_background: String(roleItem?.edited_background ?? "N"),
            centre_x: roleItem?.centre_x !== null && roleItem?.centre_x !== undefined ? String(Number(roleItem.centre_x)) : null,
            centre_y: roleItem?.centre_y !== null && roleItem?.centre_y !== undefined ? String(Number(roleItem.centre_y)) : null,
            scale_x: roleItem?.scale_x !== null && roleItem?.scale_x !== undefined ? String(Number(roleItem.scale_x)) : null,
            scale_y: roleItem?.scale_y !== null && roleItem?.scale_y !== undefined ? String(Number(roleItem.scale_y)) : null,
            rotation: roleItem?.rotation !== null && roleItem?.rotation !== undefined ? String(Number(roleItem.rotation)) : null,
            initial_view_rotation: Number(roleItem?.initial_view_rotation || 0),
            fit_fill: roleItem?.fit_fill || null,
            position: roleItem?.position || null,
            edited: String(roleItem?.edited ?? "N"),
            view_settings: roleItem?.view_settings ?? null,
        };

    }).sort((firstRole, secondRole) => {

        return firstRole.role.localeCompare(secondRole.role);

    });

};



// Normalise the configured product fields controlled directly from the main design page
const normaliseProductGridForEditedCheck = (products) => {

    // Read configured products safely
    const safeProducts = Array.isArray(products) ? products : [];

    // Normalise each configured parent family
    return safeProducts.map((productItem) => {

        // Read children safely
        const children = Array.isArray(productItem?.children) ? productItem.children : [];

        return {
            id: String(productItem?.id || productItem?.client_configured_product_id || ""),
            active: String(productItem?.active ?? "Y"),
            marketplace: String(productItem?.marketplace ?? "N"),
            store: String(productItem?.store ?? "Y"),
            roles: normaliseProductGridRolesForEditedCheck(productItem?.roles),
            children: children.map((childItem) => {

                return {
                    id: String(childItem?.id || childItem?.client_configured_product_id || childItem?.pod_product_sku || ""),
                    active: String(childItem?.active ?? "Y"),
                    roles: normaliseProductGridRolesForEditedCheck(childItem?.roles),
                };

            }).sort((firstChild, secondChild) => {

                return firstChild.id.localeCompare(secondChild.id);

            }),
        };

    }).sort((firstProduct, secondProduct) => {

        return firstProduct.id.localeCompare(secondProduct.id);

    });

};



// Check whether one configured parent or child supports a currently usable design role
const configuredProductMemberHasUsableRole = (configuredProduct, usableDesignRoleKeys) => {

    // Stop when there are no usable design roles
    if (!(usableDesignRoleKeys instanceof Set) || usableDesignRoleKeys.size === 0) {
        return false;
    }

    // Store every print-area role supported by the canonical POD product
    const supportedProductRoleKeys = new Set();

    // Read canonical product surfaces safely
    const productSurfaces = Array.isArray(
        configuredProduct?.pod_product?.custom_product?.surfaces
    )
        ? configuredProduct.pod_product.custom_product.surfaces
        : [];

    // Collect the role from every canonical print area
    productSurfaces.forEach((surface) => {

        // Read the surface print areas safely
        const printAreas = Array.isArray(surface?.print_areas)
            ? surface.print_areas
            : [];

        // Add every valid print-area role
        printAreas.forEach((printArea) => {

            // Read the canonical role key
            const productRoleKey = String(printArea?.role || "").trim();

            // Ignore malformed print areas
            if (productRoleKey === "") {
                return;
            }

            // Store the supported role
            supportedProductRoleKeys.add(productRoleKey);

        });

    });

    // Check whether any canonical product role still has a usable design image
    return Array.from(supportedProductRoleKeys).some((productRoleKey) => {
        return usableDesignRoleKeys.has(productRoleKey);
    });

};



// Check whether a configured product family has at least one printable parent or child
const configuredProductFamilyHasUsableRole = (configuredProduct, usableDesignRoleKeys) => {

    // Keep the family when the configured parent can print at least one usable role
    if (configuredProductMemberHasUsableRole(configuredProduct, usableDesignRoleKeys)) { return true; }

    // Read configured children safely
    const configuredChildren = Array.isArray(configuredProduct?.children) ? configuredProduct.children : [];

    // Keep the family when any configured child can print at least one usable role
    return configuredChildren.some((configuredChild) => {
        return configuredProductMemberHasUsableRole(configuredChild, usableDesignRoleKeys);
    });

};



// Build the role-key set which is currently selected and still backed by an image
const getUsableDesignRoleKeys = (designRoles, selectedRoles) => {

    // Start with an empty design-role array
    let safeDesignRoles = [];

    // Use the supplied design roles when they are valid
    if (Array.isArray(designRoles)) {
        safeDesignRoles = designRoles;
    }

    // Start with an empty role-selection array
    let safeSelectedRoles = [];

    // Use the supplied role selection state when it is valid
    if (Array.isArray(selectedRoles)) {
        safeSelectedRoles = selectedRoles;
    }

    // Store every role key which can currently be printed
    const usableDesignRoleKeys = new Set();

    // Check each design or template role
    safeDesignRoles.forEach((designRole) => {

        // Read the role key
        const designRoleKey = String(designRole?.role || "").trim();

        // Skip malformed role rows
        if (designRoleKey === "") { return; }

        // Skip roles which no longer have usable image data
        if (!designRoleHasUsableImage(designRole)) { return; }

        // Find explicit selection state for this role
        const selectedRoleState = safeSelectedRoles.find((selectedRole) => {
            return String(selectedRole?.role || "").trim() === designRoleKey;
        });

        // Existing role rows are selected unless the selector explicitly says otherwise
        let roleIsSelected = true;

        // Respect the explicit selector state when one exists
        if (selectedRoleState) {
            roleIsSelected =
                selectedRoleState.selected === true ||
                String(selectedRoleState.selected || "").trim().toUpperCase() === "Y";
        }

        // Skip roles which are no longer selected
        if (!roleIsSelected) { return; }

        // Mark this role as usable by configured products
        usableDesignRoleKeys.add(designRoleKey);

    });

    // Return the usable role-key set
    return usableDesignRoleKeys;

};



// Resolve the clean canonical family for one PPI issue
const getPrintQualityProductFamilyDetails = (canonicalProducts, podProductSku) => {

    // Normalise the supplied canonical products and SKU
    const safeCanonicalProducts = Array.isArray(canonicalProducts)
        ? canonicalProducts
        : [];

    const safeSku = String(podProductSku || "");

    // Look through each canonical product family
    for (const canonicalParent of safeCanonicalProducts) {

        // Match a standalone purchasable parent directly
        if (String(canonicalParent?.sku || "") === safeSku) {

            return {
                familyKey: String(canonicalParent?.sku || canonicalParent?.id || safeSku),
                familyTitle: canonicalParent?.Title || "Product",
            };

        }

        // Read the canonical children safely
        const canonicalChildren = Array.isArray(canonicalParent?.children)
            ? canonicalParent.children
            : [];

        // Check whether this PPI issue belongs to one of this family's children
        const matchingChild = canonicalChildren.find((canonicalChild) => {
            return String(canonicalChild?.sku || "") === safeSku;
        });

        // Stop searching once the correct family is found
        if (matchingChild) {

            return {
                familyKey: String(canonicalParent?.sku || canonicalParent?.id || safeSku),
                familyTitle: canonicalParent?.Title || "Product",
            };

        }

    }

    // Keep a safe fallback for historical or unresolved product data
    return {
        familyKey: safeSku || "product",
        familyTitle: "Product",
    };

};



// Render the summarised PPI warning for one design print area
const PrintAreaQualityWarning = ({
    printQualitySummary,
    canonicalProducts,
    roleCode,
    roleName,
}) => {

    // Read the summary safely
    const safeSummary = Array.isArray(printQualitySummary)
        ? printQualitySummary
        : [];

    // Find the API summary for this exact print area
    const roleSummary = safeSummary.find((summaryItem) => {
        return String(summaryItem?.role || "") === String(roleCode || "");
    });

    // Stop when this print area has no PPI warning
    if (!roleSummary) { return null; }

    // Read the warning severity and affected products safely
    const status = String(roleSummary?.status || "").toLowerCase();

    const issues = Array.isArray(roleSummary?.issues)
        ? roleSummary.issues
        : [];

    // Only warning and fail states should display
    if (status !== "warning" && status !== "fail") { return null; }
    if (issues.length === 0) { return null; }

    // Group parent products and their children into product families
    const issueGroups = [];

    for (const issue of issues) {

        // Resolve only the clean product family name from canonical product data
        const displayDetails = getPrintQualityProductFamilyDetails(
            canonicalProducts,
            issue?.pod_product_sku
        );

        // Find an existing group for this canonical family
        let issueGroup = issueGroups.find((group) => {
            return group.familyKey === displayDetails.familyKey;
        });

        // Create the family group on first use
        if (!issueGroup) {

            issueGroup = {
                familyKey: displayDetails.familyKey,
                familyTitle: displayDetails.familyTitle,
                issues: [],
            };

            issueGroups.push(issueGroup);

        }

        // Add this exact PPI problem using the variant supplied by the API summary
        issueGroup.issues.push({
            ...issue,
            variantLabel: String(issue?.variant_label || ""),
        });

    }

    // Keep the introduction short because the detailed values follow underneath
    const summaryMessage =
        issues.length === 1
            ? "1 product/variant has low image resolution on this print area."
            : `${issues.length} products/variants have low image resolution on this print area.`;

    return (

        <div
            className="pod-design__print-area-quality-warning"
            onClick={(event) => event.stopPropagation()}
        >

            <InfoButton
                buttonIcon={
                    <WarningIcon
                        className={`printQualityWarningIcon printQualityWarningIcon--${status}`}
                    />
                }
                shortLabel={`${roleName || roleCode} print quality warning`}
                description={

                    <div className="pod-design__print-area-quality-popover">

                        <div className="pod-design__print-area-quality-summary">
                            {summaryMessage}
                        </div>

                        <div className="pod-design__print-area-quality-issues">

                            {issueGroups.map((issueGroup) => {

                                return (

                                    <div
                                        key={issueGroup.familyKey}
                                        className="pod-design__print-area-quality-group"
                                    >

                                        {/* Show the clean canonical product family title once */}
                                        <div className="pod-design__print-area-quality-family">
                                            {issueGroup.familyTitle}
                                        </div>

                                        {issueGroup.issues.map((issue, issueIndex) => {

                                            // Prefer the recommended value when explaining what the image should achieve
                                            const targetPpi =
                                                issue?.recommended_ppi ||
                                                issue?.low_ppi ||
                                                null;

                                            // Build the concise PPI explanation
                                            let ppiText = `${issue?.ppi} PPI`;

                                            if (targetPpi) {
                                                ppiText += ` (should be ${targetPpi}+)`;
                                            }

                                            return (

                                                <div
                                                    key={`${issue?.product_id || "product"}-${issueIndex}`}
                                                    className="pod-design__print-area-quality-issue"
                                                >

                                                    {/* Child products show only their useful variant information */}
                                                    {issue?.variantLabel &&

                                                        <div className="pod-design__print-area-quality-variant">
                                                            {issue.variantLabel}
                                                        </div>

                                                    }

                                                    <div
                                                        className={
                                                            issue?.variantLabel
                                                                ? "pod-design__print-area-quality-value pod-design__print-area-quality-value--variant"
                                                                : "pod-design__print-area-quality-value"
                                                        }
                                                    >
                                                        {ppiText}
                                                    </div>

                                                </div>

                                            );

                                        })}

                                    </div>

                                );

                            })}

                        </div>

                    </div>

                }
            />

        </div>

    );

};



// Render the occupied print-area card footer controls
const RenderDesignOptions = ({
    role,
    existingRoleData,
    setPodDesignDetails,
    handleOpenCustomizrClick,
    handleOpenUpscaleClick,
    handleUpsertRoleImage,
    isCustomizrReady,
    setLoadingCustomizrEdit,
    handleDeleteRoleImage,
    hideManagedSettings = false,
    isSubmittingRoleImage = false,
}) => {

    // Keep the settings space in the footer while hiding template-controlled options
    let settingsClassName = "pod-design__design-options-row pod-design__design-options-row--settings";

    // Hide background colour and fit/fill when the assigned template owns those settings
    if (hideManagedSettings) {
        settingsClassName += " pod-design__design-options-row--settings-hidden";
    }

    // Show the correct edit tooltip while Customizr is loading
    let editButtonTooltip = "Edit image";

    // Explain why the edit button is temporarily disabled
    if (!isCustomizrReady) {
        editButtonTooltip = "Loading image editor...";
    }

    // Check whether this role has a real image available for edit / upscale actions
    const hasEditableRoleImage = Boolean(existingRoleData?.uploadedImage || existingRoleData?.image_url);



    return (

        <div className="pod-design__design-options">

            {/* Keep delete in the familiar bottom-left card position */}
            <div className="pod-design__design-options-row pod-design__design-options-row--delete">

                <CreateButton
                    icon="/images/icon_bin.png"
                    iconTitle="Delete"
                    tooltip="Delete image"
                    size="xs"
                    isDelete={true}
                    onClick={() => {

                        // Confirm the image deletion and prune any product families which would become blank
                        handleDeleteRoleImage(role);

                    }}
                />

            </div>

            <div className={settingsClassName}>

            {/* Background Colour Picker */}    
            <div 
                onClick={(event) => {

                    // Stop click events reaching the parent
                    event.stopPropagation();

                }}
                onMouseDown={(event) => {

                    // Stop mouse down events reaching outside-click handlers
                    event.stopPropagation();

                }}
                onPointerDown={(event) => {

                    // Stop pointer events reaching outside-click handlers
                    event.stopPropagation();

                }}
            >                 
            <ColorPickerSmall
                label=""
                title="Background Colour"
                size="l"
                value={existingRoleData?.background_colour ? existingRoleData?.background_colour === "transparent" ? null : existingRoleData?.background_colour: null}
                onChange={(hexOrNull) => {
                
                    // Update podDesignDetails state
                    setPodDesignDetails((prevDetails) => {

                        // Create a safe roles array
                        const safeRoles = Array.isArray(prevDetails?.roles) ? prevDetails.roles : [];

                        // Build updated roles array
                        const updatedRoles = safeRoles.map((roleObj) => {

                            // If this is NOT the role we want, return unchanged
                            if (roleObj?.role !== role.role) {

                                // Return unchanged role object
                                return roleObj;

                            }

                            // If this IS the role we want, return updated object
                            return {

                                // Keep existing fields
                                ...roleObj,

                                // Update background colour (store null if cleared)
                                background_colour: hexOrNull,

                            };

                        });

                        // Return updated podDesignDetails
                        return {

                            // Keep existing podDesignDetails
                            ...prevDetails,

                            // Replace roles with updated roles
                            roles: updatedRoles,

                        };

                    });
                
                }}
                allowNullOrTransparent={true}
            />
            </div>

            {/* Fit / Fill Options */}
            <div>

                <select
                    name="FitorFill" 
                    value={existingRoleData?.fit_fill ? existingRoleData.fit_fill : "fit"}
                    style={{
                        padding: "5px 10px",
                        minWidth: "64px",
                        width: "auto",
                    }}
                    onChange={(event) => {

                        //const designImage = existingRoleData?.thumbnail_url || podDesignDetails?.roles?.find((r) => r?.role === role.role)?.image_url || null;

                        // Update podDesignDetails state
                        setPodDesignDetails((prevDetails) => {

                            // Create a safe roles array
                            const safeRoles = Array.isArray(prevDetails?.roles) ? prevDetails.roles : [];

                            // Build updated roles array
                            const updatedRoles = safeRoles.map((roleObj) => {

                                // If this is NOT the role we want, return unchanged
                                if (roleObj?.role !== role.role) {

                                    // Return unchanged role object
                                    return roleObj;

                                }

                                // If this IS the role we want, return updated object
                                return {

                                    // Keep existing fields
                                    ...roleObj,

                                    // Update fit_fill togle
                                    fit_fill: event.target.value,

                                    // Set scale based on fit or fill
                                    //scale_x: event.target.value === "fit" ? "1" : genericFillScale,
                                    scale_x: 1,
                                    //scale_y: event.target.value === "fit" ? "1" : genericFillScale
                                    scale_y: 1,

                                };

                            });

                            // Return updated podDesignDetails
                            return {

                                // Keep existing podDesignDetails
                                ...prevDetails,

                                // Replace roles with updated roles
                                roles: updatedRoles,

                            };

                        });

                    }}
                >                        
                    <option value="fit">Fit</option>
                    <option value="fill">Fill</option>
                                                                        
                </select>

            </div>

            </div>

            <div className="pod-design__design-options-row pod-design__design-options-row--actions">

            {/* Edit button (to open customizr and edit the image) */}
            {hasEditableRoleImage &&

                <CreateButton 
                    icon="/images/icon_edit.png"
                    iconTitle="Edit"
                    tooltip={editButtonTooltip}
                    size="xs"                   
                    onClick={async () => { 

                        // Set loading state
                        setLoadingCustomizrEdit(true);

                        // Return the edited image without saving it directly into the image bucket
                        const saveToImageBucket = false;

                        // Return the image so we can use it for the role
                        const returnImage = true;

                        // Use base64 image data first if we have it, otherwise use the existing image URL
                        const imageData = existingRoleData.uploadedImage ? existingRoleData.uploadedImage : existingRoleData.image_url;

                        // Get the image - open customizr for the user to edit the image and get the returned image
                        const editedImage = await handleOpenCustomizrClick("edit", imageData, saveToImageBucket, returnImage, setLoadingCustomizrEdit); 

                        // If Customizr was cancelled or closed without returning an image, stop without changing the role
                        if (!editedImage) { return; }

                        // Convert the returned image into a safe string
                        const editedImageValue = String(editedImage).trim();

                        // Start with the returned image data unchanged
                        let editedImageSource = editedImageValue;

                        // Customizr may return bare PNG base64 rather than a complete browser image source
                        if (
                            !editedImageValue.startsWith("data:image/") &&
                            !editedImageValue.startsWith("http://") &&
                            !editedImageValue.startsWith("https://")
                        ) {

                            // Add the data URL prefix required by image elements and the image uploader
                            editedImageSource =
                                "data:image/png;base64," + editedImageValue;

                        }

                        // Build the payload from the edited image
                        const payload = {

                            imageDetails: {

                                // Use the complete image source for the immediate portal preview
                                image_url: editedImageSource,

                                // Give the replacement image a useful name
                                name: "Edited image",

                            },

                            // Store the same complete image data ready for the bucket upload
                            uploadedImage: editedImageSource,

                        };

                        // Replace the image on this exact role
                        await handleUpsertRoleImage(role.role, payload);

                        // Clear loading state
                        setLoadingCustomizrEdit(false);

                    }}
                    className="ai-image-btn ai-edit-btn"
                    disabled={
                        // Block clicks until Customizr is ready.
                        !isCustomizrReady
                        || isSubmittingRoleImage
                        || existingRoleData?.preview_mode === "generic"
                        || existingRoleData?.preview_mode === "none"
                    }
                
                />

            }

            {/* Upscale the current design image */}
            {hasEditableRoleImage &&

                <CreateButton
                    icon="/images/icon_upscale.png"
                    iconTitle="Upscale"
                    tooltip="Upscale image"
                    size="xs"
                    type="button"
                    onClick={() => {

                        // Open the upscale popup for this exact role image
                        handleOpenUpscaleClick(role.role, existingRoleData);

                    }}
                    disabled={
                        isSubmittingRoleImage
                        || existingRoleData?.preview_mode === "generic"
                        || existingRoleData?.preview_mode === "none"
                    }
                />

            }

            {/* Reuse the existing uploader path for deliberate replacement */}
            <RenderImageUploader
                selectedImage={null}
                onImageChange={(payload) => {

                    // Replace the image on this exact role without confirmation
                    handleUpsertRoleImage(role.role, payload);

                }}
                displayMode="button"
                buttonTitle=""
                buttonIcon="/images/icon_replace.png"
                buttonIconTitle="Replace"
                buttonTooltip="Replace image"
                buttonSize="xs"
                disabled={isSubmittingRoleImage}
            />

            </div>

        </div>

    )

};



// Replace one configured parent product family in a design-details object
const replaceConfiguredProductFamilyInDetails = (
    designDetails,
    productFamily
) => {

    // Keep the current state when either input is invalid
    if (
        !designDetails ||
        typeof designDetails !== "object" ||
        !productFamily ||
        typeof productFamily !== "object"
    ) {
        return designDetails;
    }

    // Read the configured family id
    const productFamilyId =
        String(productFamily?.id || "");

    // Stop when the API result has no configured id
    if (!productFamilyId) {
        return designDetails;
    }

    // Read the current product families safely
    let products = [];

    if (Array.isArray(designDetails.products)) {
        products = designDetails.products;
    }

    // Track whether the requested family exists in this state object
    let productWasReplaced = false;

    // Replace only the exact configured parent family
    const nextProducts =
        products.map((productItem) => {

            // Keep unrelated product families unchanged
            if (
                String(productItem?.id || "") !==
                productFamilyId
            ) {
                return productItem;
            }

            // Record the replacement
            productWasReplaced = true;

            // Use the complete authoritative family returned by the API
            return productFamily;

        });

    // Keep the current state when the product family disappeared
    if (!productWasReplaced) {
        return designDetails;
    }

    // Return the design with the committed product family
    return {
        ...designDetails,
        products: nextProducts,
    };

};



const PodDesignDetailsPopUp = ({onClose, designOrTemplate, designOrTemplateId, setSelectedDesignOrTemplateId, fetchPodDesigns, existingCategories, initialDesignData}) => {

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Set isMobile from use hook 
    const isMobile = useIsMobile();
   
    // Get canonical products and connections from global user data
    const { 
        connectionsData, 
        canonicalProducts,  
    } = useContext(UserContext);
          

    // Define toast options
    const { toastError } = useToast();

    // State for full design details - ORIGINAL for comparison
    const [originalPodDesignDetails, setOriginalPodDesignDetails] = useState(null);

    // Store saved product families pruned locally so they can be deleted only when the design is saved
    const [pendingBlankProductDeletions, setPendingBlankProductDeletions] = useState([]);

    // Track product controls edited directly on the main design page
    const [productGridChangesPending, setProductGridChangesPending] = useState(false);

    // This is the logo used for generic preview images
    const strichLogoUrl = "https://www.strichsuite.com/images/thestrich.png";

    // State for storing print area roles roles
    const [printAreaRoles, setPrintAreaRoles] = useState([]);

    // State for storing the basic design role key (should normally be the front) taken from the first role in the printAreaRoles call response
    const [basicDesignRoleKey, setBasicDesignRoleKey] = useState(null);
    
    // Custom hook for fetching and managing pod design and product data
    const {

        // Function to add to image bucket
        addImageToImageBucket,

    } = usePodProductData();

    // Optional: turn it into a number if your API expects a number
    const podDesignIdNumber = Number(designOrTemplateId);

    // Create state with a safe initial shape
    const [podDesignDetails, setPodDesignDetails] = useState(() => ({

        // Ensure roles always exists with basic role key as default
        roles: [],

    }));

    // State for loading
    const [isLoading, setIsLoading] = useState(false);

    // Set a state for the chosen language, EN by default - Need to add language picker and add new language option EP 10/06/2025
    const [chosenLanguage] = useState("EN");

    // Set state for adding basic or advanced design set (more that 1 role)
    const [isAddingAdvancedDesignSet, setIsAddingAdvancedDesignSet] = useState(false);

    // Set state for adding a new design or template
    const [isAddingNewDesign, setIsAddingNewDesign] = useState(false);
    
    // Set state for showing roles list that the user can toggle on/off
    //const [showSelectedRolesList, setShowSelectedRolesList] = useState(false);

    // State for storing type of visual required for roles list
    const [rolesVisualType, setRolesVisualType] = useState("cube");
    
    // State for storing selected roles for this design
    const [selectedRoles, setSelectedRoles] = useState(null);
    
    // Loading state when saving the design
    const [designIsSaving, setDesignIsSaving] = useState(null);   

    // Track whether the first image on a new design should trigger its initial save
    const [newDesignAutoSaveRequested, setNewDesignAutoSaveRequested] = useState(false);

    // Track whether incoming image/title data has already been applied to this new design
    const [initialDesignDataApplied, setInitialDesignDataApplied] = useState(false);

    // Loading state when deleting the design
    const [isDeleting, setIsDeleting] = useState(null);   

    // State for save progress percentage
    const [saveProgress, setSaveProgress] = useState(0);

    // State for save progress message
    const [saveProgressMessage, setSaveProgressMessage] = useState("");

    // State for optional sub message
    const [saveProgressSubMessage, setSaveProgressSubMessage] = useState("");

    // Set pop up state for ai design generation
    const [showPodDesignAiPage, setShowPodDesignAiPage] = useState(false);
    
    // Set pop up state for ai design generation
    const [submittedAiDesign, setSubmittedAiDesign] = useState(null);

    // State for loading when submitting an ai design
    const [isSubmittingAiDesign, setIsSubmittingAiDesign] = useState(null);

    // Set pop up state for ai image upscaling
    const [showPodDesignUpscalePage, setShowPodDesignUpscalePage] = useState(false);

    // Track the role and returned payload for the current upscaled image
    const [submittedUpscaledImage, setSubmittedUpscaledImage] = useState(null);

    // State for loading when submitting an upscaled image
    const [isSubmittingUpscaledImage, setIsSubmittingUpscaledImage] = useState(null);

    // This tracks whether any role image replacement flow is currently active
    const isSubmittingRoleImage = Boolean(isSubmittingAiDesign || isSubmittingUpscaledImage);

    // This stores the role key currently being dragged
    const [draggedRoleKey, setDraggedRoleKey] = useState(null);

    // This stores the role key currently being hovered as a drop target
    const [dragOverRoleKey, setDragOverRoleKey] = useState(null);

    // This tracks whether a file is currently being dragged over the popup
    const [isExternalFileDragActive, setIsExternalFileDragActive] = useState(false);
    
    // State for loading customizr when editting an image
    const [loadingCustomizrEdit, setLoadingCustomizrEdit] = useState(false);

    // Store whether this is a strich template or not - need to disable lots of functionality if it is
    const [isStrichTemplate, setIsStrichTemplate] = useState(null);

    // Store the templates which can be assigned to a design
    const [availablePodTemplates, setAvailablePodTemplates] = useState([]);

    // Load templates which can be assigned to designs
    useEffect(() => {

        // Templates are only required by the design editor
        if (designOrTemplate !== "design" || !token) {
            setAvailablePodTemplates([]);
            return;
        }

        // Prevent an old async response updating a newer popup
        let cancelled = false;

        // Load the user's templates plus inheritable Strich templates
        const fetchAvailablePodTemplates = async () => {

            try {

                // Get the available template headers
                const response = await getPodDesigns(token, "template");

                // Stop if this effect has already been cleaned up
                if (cancelled) { return; }

                // Store valid template results
                if (response?.success && Array.isArray(response.results)) {
                    setAvailablePodTemplates(response.results);
                } else {
                    setAvailablePodTemplates([]);
                }

            } catch (error) {

                // Do not break the design editor if the template list cannot be loaded
                if (!cancelled) {
                    setAvailablePodTemplates([]);
                }

            }

        };

        // Load the templates
        fetchAvailablePodTemplates();

        return () => {

            // Ignore late responses after the popup changes or closes
            cancelled = true;

        };

    }, [designOrTemplate, token]);

    // Clear the main product-grid dirty flag when the user has returned everything to its saved state
    useEffect(() => {

        // Nothing needs checking until a main-grid control has actually been changed
        if (!productGridChangesPending) { return; }

        // Compare only the product fields controlled by the main design screen
        const productGridMatchesSaved = deepEqual(
            normaliseProductGridForEditedCheck(originalPodDesignDetails?.products),
            normaliseProductGridForEditedCheck(podDesignDetails?.products)
        );

        // Clear the pending flag when the current state matches the saved baseline again
        if (productGridMatchesSaved) {

            setProductGridChangesPending(false);

        }

    }, [
        productGridChangesPending,
        originalPodDesignDetails?.products,
        podDesignDetails?.products,
    ]);



    // Check if the ai design submissing has finished (this only works because it's for a single basic role atm)
    useEffect(() => {

        // If we're not submitting an ai design exit early
        if (!isSubmittingAiDesign) return;

        // If we're submitting but the roles array is empty then return early
        if (isSubmittingAiDesign && podDesignDetails.roles.length === 0) return;

        // If we're submitting and there's an image_url in the role 
        if (isSubmittingAiDesign && podDesignDetails.roles?.find((r) => r.role === submittedAiDesign.role)?.image_url) {
            
            // Then it's finished saving the AI image so clear the loading state
            setIsSubmittingAiDesign(false);

        };


    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmittingAiDesign, podDesignDetails.roles]);
    



    // *************************************************
    // **************** SET PAGE TITLE *****************
    
    // Look up the translation for the chosen language
    const chosenTranslation = originalPodDesignDetails?.translations?.find((translation) => {

        // Match the language
        return translation?.language === chosenLanguage;

    }) || null;

    // Get the title (or null)
    const designTitle = chosenTranslation?.title || null;

    // Build a title from state (changes when product changes)
    const pageTitle = designTitle
        ? `${designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}: ${designTitle}`
        : `${designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}`;

    
    // **************** SET PAGE TITLE END *****************
    // *****************************************************


    
    // Function to fetch all the roles
    const fetchRoles = async () => {

        // Make the call and get the response - Function to get all the print area roles that a user can assign a POD design to
        const response = await getPrintAreaRoles(token);

        // Set the roles in state
        setPrintAreaRoles(response.results);

        // Get the basic design role key (should normally be the front) taken from the first role in the printAreaRoles call response
        const basicDesignRole = response.results?.[0]?.role;

        // Update the state
        setBasicDesignRoleKey(basicDesignRole);

    };
    


    useEffect(() => {

        // Only run when roles changes or when the original pod deseign details changes (which should only happen once on mount)
        handleSetSelectedRoles(printAreaRoles);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printAreaRoles, originalPodDesignDetails]);



    // Create the default preview role when adding a new template
    const setNewTemplateRole = async (role) => {

        // Stop when the default role cannot be identified
        if (!role) { return; }

        // Create only the default role needed for the new template
        const payload = {

            roles: [
                {
                    role: role,
                    image_url: strichLogoUrl,
                    uploadedImage: strichLogoUrl,
                    imageDetails: {
                        name: "generic",
                        image_url: strichLogoUrl,
                    },
                    thumbnail_url: strichLogoUrl,
                    fit_fill: "fit",
                    scale_x: "1",
                    scale_y: "1",
                    preview_mode: "logo",
                    suppress_product_preview: false,
                }
            ],

        };

        // Add the dimensions of the default preview image
        const roleWithDims = await buildPodDesignDetailsWithRoleDimensions(payload);

        // Start without a valid generated role
        let nextRole = null;

        // Read the first generated role safely
        if (Array.isArray(roleWithDims?.roles)) {
            nextRole = roleWithDims.roles[0] || null;
        }

        // Stop if the role could not be generated
        if (!nextRole?.role) { return; }

        // Add the default role only if it is not already present
        setPodDesignDetails((previousDetails) => {

            // Read the current roles safely
            let previousRoles = [];

            if (Array.isArray(previousDetails?.roles)) {
                previousRoles = previousDetails.roles;
            }

            // Check whether another effect run has already added this role
            const roleAlreadyExists = previousRoles.some((roleItem) => {

                return String(roleItem?.role || "") === String(nextRole.role);

            });

            // Keep the current state when the role already exists
            if (roleAlreadyExists) { return previousDetails; }

            // Add the single default role
            return {
                ...previousDetails,
                roles: [
                    ...previousRoles,
                    nextRole,
                ],
            };

        });

    };


    // When the role key becomes available populate the default role when adding a new template (default strich logo on front role)
    useEffect(() => {

        // Only run if we have the role key
        if (!basicDesignRoleKey) return;
               
        // Only run for templates
        if (designOrTemplate !== "template") return;

        // Only run when adding new
        if (!isAddingNewDesign) return;
        
        // Set the new template role using the strich logo and ensure it has image dimensions
        setNewTemplateRole(basicDesignRoleKey);
       
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basicDesignRoleKey, designOrTemplate, strichLogoUrl, isAddingNewDesign, printAreaRoles]);

    

    // This stores the current preview image choice for the template editor
    const [templatePreviewImage, setTemplatePreviewImage] = useState({
        
        // This sets the default mode
        sourceType: "generic",
        
        // This sets the default preset
        presetId: "logo",
        
        // This sets the default shape
        shape: "logo",
        
        // This sets the default display label
        displayName: "Strich Logo",
        
        // This sets the default colour
        fillColour: "#2563EB",
        
        // This sets no uploaded image by default
        uploadedImage: null,

    });



    // This helper loads an image URL and returns its natural dimensions
    const getImageDimensionsFromUrl = (imageUrl) => {

        // Return a promise so we can await the result
        return new Promise((resolve) => {

            // If there is no url, resolve with null dimensions
            if (!imageUrl) {

                // Resolve with nulls
                resolve({ design_width: null, design_height: null });

                // Exit early
                return;

            }

            // Create a new Image instance
            const img = new Image();

            // Allow CORS-safe dimension reads when the server supports it
            img.crossOrigin = "anonymous";

            // When the image loads, resolve with natural sizes
            img.onload = () => {

                // Resolve with the image's natural width and height
                resolve({

                    // Store width
                    design_width: img.naturalWidth || null,

                    // Store height
                    design_height: img.naturalHeight || null,

                });

            };

            // If the image fails to load, resolve with null dimensions (don’t crash the UI)
            img.onerror = () => {

                // Resolve with nulls
                resolve({ design_width: null, design_height: null });

            };

            // Start loading the image
            img.src = imageUrl;

        });

    };



    // This helper returns the FINAL object (not a Promise in state)
    const buildPodDesignDetailsWithRoleDimensions = async (results) => {

        // Ensure results is an object
        const safeResults = results || {};

        // Ensure roles is an array
        const roles = Array.isArray(safeResults?.roles) ? safeResults.roles : [];

        // Update roles with dimensions
        const updatedRoles = await Promise.all(

            // Map roles
            roles.map(async (role) => {

                // Pick the main image url
                const imageUrl = role?.thumbnail_url || role.image_url || null;

                // Get dimensions
                const dims = await getImageDimensionsFromUrl(imageUrl);

                // Return updated role
                return {

                    // Keep existing role fields
                    ...role,

                    // Add width
                    design_width: dims?.design_width,

                    // Add height
                    design_height: dims?.design_height,

                };

            })

        );

        // Return the updated results object
        return {

            // Keep existing result fields
            ...safeResults,

            // Overwrite roles
            roles: updatedRoles,

        };

    };



    // Define async function
    const fetchPodDesignDetails = async (id, showLoading = true) => {

        const designOrTemplateId = id || podDesignIdNumber;

        // Start without refreshed design details
        let refreshedPodDesignDetails = null;

        // Show the full loading state only when this is not a silent refresh
        if (showLoading) {
            setIsLoading(true);
        }

        // API call  - Function to get specific pod design details by design id
        const response = await getPodDesign(token, designOrTemplate, designOrTemplateId);

        if (response.success) {

            // Check if this is a template and if so is it a stich one - we want to disable lots of stuff if it is
            const isStrichTemplate = designOrTemplate === "template" && response?.results?.source === "strich";

            // Update the state 
            setIsStrichTemplate(isStrichTemplate);

            // Map through the response.roles and get the design width and height for each image_thumbnail that exist

            // Build the final resolved object (NOT a Promise)
            const updatedResults = await buildPodDesignDetailsWithRoleDimensions(response?.results);

            // Keep the refreshed details available to calling workflows
            refreshedPodDesignDetails = updatedResults;

            // Set state with the resolved object
            setPodDesignDetails(updatedResults);

            // Store an original version of the pod design details
            setOriginalPodDesignDetails(updatedResults);

            // The fetched product hierarchy is now the saved baseline
            setProductGridChangesPending(false);

            // True if the role exists in the roles array
            const basicRoleExists = (response?.results?.roles || []).some((roleItem) => roleItem?.role === basicDesignRoleKey);

            // Other roles exist if they don't match the basicDesignRoleKey in the response - if so it's 'Advanced' 
            const otherRolesExist = (response?.results?.roles || []).some((roleItem) => roleItem?.role !== basicDesignRoleKey);

            // Set whether this is a basic or advanced design set - it is advanced if there is more than 1 role or if the basicDesignRoleKey isn't undefined and there is no data for the 'Front' role
            setIsAddingAdvancedDesignSet(response.results.roles.length > 1 || (basicDesignRoleKey && !basicRoleExists) || otherRolesExist);

        } else {

            // Toast error message
            toastError({ title: "Error", message: "Could not get POD details: " + response.error_message, autoCloseMs: 6000 });

        }

        // Stop loading only when this was not a silent refresh
        if (showLoading) {
            setIsLoading(false);
        }

        // Return the refreshed design details to calling workflows
        return refreshedPodDesignDetails;

    };



    // Effect to fetch details when podDesignId changes
    useEffect(() => {

        // If podDesignId is missing, do nothing
        if (!designOrTemplateId) return;

        // We have something on the page so we need to get all the print area roles
        fetchRoles();

        // If the basic design role key is missing, do nothing (we need it to set the basic/advanced setting)
        if (!basicDesignRoleKey) return;

        // If designOrTemplateId is 'addnew' - we're adding a new design/template
        if ( designOrTemplateId === "addnew" ) {
            
            // Set adding new design/template to true
            setIsAddingNewDesign(true);

            // A newly-created template belongs to this user and cannot be a Strich-provided template
            setIsStrichTemplate(false);

            // Return early
            return;

        }

        // Call async function to get the design/template details
        fetchPodDesignDetails();

    // Re-run if URL designOrTemplateId changes or when the basic role key becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [designOrTemplateId, basicDesignRoleKey]); 



    // Build an array of objects like: [{ role: string, selected: boolean }, ...]
    const handleSetSelectedRoles = (roles) => {

        // Guard against bad input
        const safeRoles = Array.isArray(roles) ? roles : [];

        // Map each role into its own object
        const selectedRolesArray = safeRoles

            // Remove any roles without a usable role key
            .filter((role) => {

                // Get the role key safely (prefer role.role, fallback to role.name)
                const roleKey = String(role?.role ?? role?.name ?? "").trim();

                // Keep only if it exists
                return Boolean(roleKey);

            })

            // Convert each role into one selector entry
            .map((role) => {

                // Get the role key safely
                const roleKey =
                    String(role?.role || role?.name || "").trim();

                // Check whether this existing design actually contains the role
                const roleHasData = Boolean(
                    podDesignDetails?.roles?.find((existingRole) => {
                        return existingRole?.role === roleKey;
                    })
                );

                // Existing designs select only roles which genuinely exist
                let roleIsSelected = roleHasData;

                // New designs start with their normal default printable areas
                if (isAddingNewDesign) {

                    // Select the first role by default
                    if (roleKey === safeRoles?.[0]?.role) {
                        roleIsSelected = true;
                    }

                    // Advanced new designs also select the second role
                    if (
                        isAddingAdvancedDesignSet &&
                        roleKey === safeRoles?.[1]?.role
                    ) {
                        roleIsSelected = true;
                    }

                }

                // Return one object per printable area
                return {
                    role: roleKey,
                    selected: roleIsSelected,
                };

            });

        // Set state (array of objects)
        setSelectedRoles(selectedRolesArray);

    };



    // Helper to update progress UI safely
    const updateSaveProgress = ({

        // Current progress percentage
        progress,

        // Main message
        message,

        // Optional sub message
        subMessage = "",

    }) => {

        // Update the percentage
        setSaveProgress(progress);

        // Update the main message
        setSaveProgressMessage(message);

        // Update the sub message
        setSaveProgressSubMessage(subMessage);

    };
 



    // ************************************************
    // **************** SAVE POD DESIGN ***************

    // Function to save pod design
    const savePodDesign = async (preparedData, props) => {

        // Use the latest design details supplied by the calling workflow
        let latestPodDesignDetails = podDesignDetails;

        if (
            props?.podDesignDetails &&
            typeof props.podDesignDetails === "object"
        ) {
            latestPodDesignDetails = props.podDesignDetails;
        }

        // Use the latest printable-area selection supplied by the calling workflow
        let latestSelectedRoles = selectedRoles;

        if (Array.isArray(props?.selectedRoles)) {
            latestSelectedRoles = props.selectedRoles;
        }

        // Read the latest design roles safely
        let latestDesignRoles = [];

        if (Array.isArray(latestPodDesignDetails?.roles)) {
            latestDesignRoles = latestPodDesignDetails.roles;
        }

        // Keep only role rows whose printable areas remain selected
        const latestSelectedExistingRoles = latestDesignRoles.filter(
            (roleItem) => {

                // Read the role key
                const roleKey =
                    String(roleItem?.role || "");

                // Find the matching selected printable area
                return (latestSelectedRoles || []).some(
                    (selectedRoleItem) => {

                        // Ignore other printable areas
                        if (
                            String(selectedRoleItem?.role || "") !==
                            roleKey
                        ) {
                            return false;
                        }

                        // Accept either supported selected value
                        return (
                            selectedRoleItem?.selected === true ||
                            selectedRoleItem?.selected === "Y"
                        );

                    }
                );

            }
        );

        // Check whether any latest design role is no longer selected
        const latestExistingRoleHasBeenRemoved =
            latestSelectedExistingRoles.length !==
            latestDesignRoles.length;

        // Compare the supplied latest role state with the saved role state
        const latestPodDetailsRolesIsSameAsOriginal = deepEqual(
            normaliseDesignRolesForEditedCheck(originalPodDesignDetails?.roles),
            normaliseDesignRolesForEditedCheck(latestDesignRoles)
        );

        // Start with the product data prepared by the form handler
        let latestProductsForSave = preparedData.products;

        // Prefer the latest live product state when it is available
        if (Array.isArray(latestPodDesignDetails?.products)) {
            latestProductsForSave = latestPodDesignDetails.products;
        }

        // Remove the pod_product data that we put it for ease of use
        const cleanedPreparedData = !isAddingNewDesign && latestProductsForSave ? {

            // Copy all existing prepared data
            ...preparedData,

            // Replace products with cleaned products
            products: latestProductsForSave?.map((product) => {

                // Remove pod_product from the main product
                const { pod_product, children, ...productWithoutFluff } = product;

                // Check if children exists and is an array
                const updatedChildren = Array.isArray(children)

                    // Map the childrens array
                    ? children.map((child) => {

                        // Remove pod_product from each child
                        const { pod_product, ...childWithoutFluff } = child;

                        // Return the child without pod_product
                        return childWithoutFluff;

                    })

                    // Otherwise return undefined
                    : undefined;

                // Return the updated product
                return {

                    // Spread the product data without images
                    ...productWithoutFluff,

                    // Add the updated childrens array back
                    children: updatedChildren || []

                };

            })

        } : preparedData;

        // Template-driven designs save artwork and design details only; their local configured products remain dormant
        const productsManagedByTemplates =
            designOrTemplate === "design" &&
            Array.isArray(cleanedPreparedData?.template_ids) &&
            cleanedPreparedData.template_ids.length > 0;

        // Check whether this save is restoring the design's dormant local products
        const templatesWereCleared =
            designOrTemplate === "design" &&
            Array.isArray(originalPodDesignDetails?.template_ids) &&
            originalPodDesignDetails.template_ids.length > 0 &&
            !productsManagedByTemplates;

        // Clear any ai design data - stops useEffect running
        setSubmittedAiDesign(null);

        // Set loading state
        setDesignIsSaving(true);

        // Start progress UI
        updateSaveProgress({
            progress: 2,
            message: "Preparing save...",
            subMessage: "Validating design data",
        });

        // Set the type as Design or Template
        const type = props.type === "template" ? "template" : "design";

        // Check that we have image data else return early and provide error message
        // True if every role has no image 
        const areAllRolesEmpty = latestDesignRoles.every((item) => {

            // Check preview url
            const hasPreviewUrl = Boolean(item?.imageDetails?.image_url);

            // Check base64
            const hasBase64 = Boolean(item?.uploadedImage);

            // Empty means neither exists
            return !hasPreviewUrl && !hasBase64;

        });

        // Check if there's no design image data
        if (designOrTemplate === "design") {

            if ((!isAddingNewDesign && areAllRolesEmpty && latestSelectedExistingRoles.length === 0) || (isAddingNewDesign && areAllRolesEmpty)) {

                // There isn't so we want to return early

                // Clear loading state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "Cannot add a new design without a design.", autoCloseMs: 6000 });

                // Return early
                return;

            }

        } else {

            if ((!isAddingNewDesign && latestDesignRoles.length === 0 && latestSelectedExistingRoles.length === 0) || (isAddingNewDesign && latestDesignRoles.length === 0)) {

                // There isn't so we want to return early

                // Clear loading state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "Can't add a template without generic print area image.", autoCloseMs: 6000 });

                // Return early
                return;

            }

        }

        // Update progress after validation
        updateSaveProgress({
            progress: 8,
            message: "Validation complete",
            subMessage: "Preparing design data",
        });



        //  We have a design and a title so let's:
        //  save the images into the users image bucket and get the id's back for them if they've uploaded an image
        //  OR customizr save id
        //  make sure there is an asset type and id per role

        // ********* CREATE POD DESIGN/TEMPLATE *********
        
        // Update progress before saving base record
        updateSaveProgress({
            progress: 15,
            message: isAddingNewDesign ? "Creating item..." : "Updating item...",
            subMessage: type === "template" ? "Saving template details" : "Saving design details",
        });

        // Store the ID - either the design or template id returned from the call we make when creating a new design/template
        let typeId;

        // Check if we're adding a new design or template
        if (isAddingNewDesign) {

            // api Call
            const addNewApiCall = type === "design" ? addPodDesign : addPodTemplate;

            // Save the design/template ONLY - this does not save the image, roles or products, or possibly translations
            const addNewResponse = await addNewApiCall(token, cleanedPreparedData);

            // Check if creating the pod design/template was successful
            if (addNewResponse.success) {
                
                // Get the id of the new design/template we've just saved
                typeId = addNewResponse.results.id;

            } else {

                // There was an error creating the design/template so return early

                // Clear loading state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "Could not create new " + type + ": " + addNewResponse.error_message, autoCloseMs: 6000 });
                
                // Return early
                return;

            }

        } else {

            // This is an existing design/template so use the current id
            typeId = latestPodDesignDetails.id;

            // ********* UPDATE POD DESIGN/TEMPLATE DETAILS *********

            // Make a call to update the design/template details
            const updateDetailsResponse = await updatePodDesignDetails(token, designOrTemplate, typeId, cleanedPreparedData);

            // Check if the call failed
            if (!updateDetailsResponse.success) {

                // Return early because we couldn't update the details

                // Clear loading state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "Could not update " + type + " details: " + updateDetailsResponse.error_message, autoCloseMs: 6000 });
                
                // Return early
                return;

            }

        };

        // Remove a newly-created design or template if its compulsory setup cannot be completed
        const cleanupNewItemAfterFailedSave = async () => {

            // Existing designs and templates must never be removed by this cleanup
            if (!isAddingNewDesign || !typeId) { return; }

            try {

                // Soft-delete the incomplete item so its code is released for reuse
                const cleanupResponse = await deletePodDesign(
                    token,
                    type,
                    typeId
                );

                // Log cleanup failure without hiding the original save error
                if (!cleanupResponse?.success) {
                    console.error(
                        "Could not clean up incomplete " + type + ":",
                        cleanupResponse
                    );
                }

            } catch (cleanupError) {

                // Log unexpected cleanup failure without hiding the original save error
                console.error(
                    "Could not clean up incomplete " + type + ":",
                    cleanupError
                );

            }

        };
           
        // Update progress after base record save
        updateSaveProgress({
            progress: 25,
            message: "Base details saved",
            subMessage: "Checking roles and assets",
        });


        // ********* UPDATE POD DESIGN ROLES *********

        // Update progress before role processing
        updateSaveProgress({
            progress: 30,
            message: "Saving roles...",
            subMessage: "Uploading any new images",
        });

        // Store the response
        let updateRolesResponse;

        // Store whether the design/template role rows need saving
        const designRolesNeedSaving =
            latestExistingRoleHasBeenRemoved ||
            !latestPodDetailsRolesIsSameAsOriginal;

        // Check if they have added or removed any roles - if so we want to update the roles
        if (designRolesNeedSaving) {
            
            // They have so update the roles

            // ********* ADD IMAGES TO BUCKET ********* NB: DON'T NEED THIS FOR IMAGE BUCKET IMAGES OR CUSTOMIZR SAVES

            // Start without any newly-built role rows
            let addImagesToBucketAndBuildNewRolesArray = [];

            // Build role rows only when the current roles differ from the saved roles
            if (!latestPodDetailsRolesIsSameAsOriginal) {

                // Upload images and build the payload only from selected printable areas
                const rolesArrayResponse = await newRolesArray(
                    latestSelectedExistingRoles
                );

                // Stop without leaving a hidden new item when an image upload fails
                if (!rolesArrayResponse?.success) {

                    // Remove the incomplete newly-created design or template
                    await cleanupNewItemAfterFailedSave();

                    // Clear loading state
                    setDesignIsSaving(false);

                    // Show the real image upload failure
                    toastError({
                        title: "Error",
                        message:
                            "Could not save " +
                            type +
                            " roles: " +
                            rolesArrayResponse.error_message,
                        autoCloseMs: 6000
                    });

                    return;

                }

                // Keep the completed role rows
                addImagesToBucketAndBuildNewRolesArray =
                    rolesArrayResponse.results;

            }

            // New items use only the newly-built role rows
            let allRoles = addImagesToBucketAndBuildNewRolesArray;

            // Existing items merge newly-built roles into their retained saved roles
            if (!isAddingNewDesign) {

                allRoles = addImagesToBucketAndBuildNewRolesArray.reduce(
                    (accRoles, newRole) => {

                        // Remove any old version of this role
                        const retainedRoles = accRoles.filter((roleItem) => {

                            return roleItem?.role !== newRole?.role;

                        });

                        // Return the retained rows followed by the new version
                        return [
                            ...retainedRoles,
                            newRole,
                        ];

                    },
                    [...latestSelectedExistingRoles]
                );

            }

            // Choose the correct role endpoint
            let updateApiCall = updatePodTemplateRoles;

            if (type === "design") {
                updateApiCall = updatePodDesignRoles;
            }

            // Make the call to update the roles
            updateRolesResponse = await updateApiCall(token, typeId, allRoles);

            // Check if the call failed
            if (!updateRolesResponse.success) {

                // Remove the incomplete newly-created design or template
                await cleanupNewItemAfterFailedSave();

                // Clear loading state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "Could not update " + type + " roles: " + updateRolesResponse.error_message, autoCloseMs: 6000 });
                
                // Return early
                return;

            }

        };

        // Update progress after roles save
        updateSaveProgress({
            progress: 40,
            message: "Roles saved",
            subMessage: "Checking translations",
        });
        

        // ********* UPDATE POD DESIGN/TEMPLATE TRANSLATIONS *********

        // Check if we need to update the translations by comparing orginal and current
        const translationsMatch = deepEqual(originalPodDesignDetails?.translations, preparedData.translations);

        // If the translations don't match then we need to save the updates
        const translationsNeedSaving = !translationsMatch;

        let updateTranslationsResponse;

        if (translationsNeedSaving) {

            // Make the call to update the translations
            updateTranslationsResponse = await updatePodDesignTranslations(token, designOrTemplate, typeId, preparedData.translations);

        };


        













        // Update progress after translations
        updateSaveProgress({
            progress: 50,
            message: "Translations complete",
            subMessage: "Preparing products",
        });

        // ********* UPDATE POD DESIGN/TEMPLATE PRODUCTS *********


        // Delete saved product families which were pruned locally after a role image was removed
        if (!productsManagedByTemplates) {

            // Start with an empty pending saved-family deletion list
            let pendingSavedProductDeletions = [];

            // Use the pending saved-family deletions when they are valid
            if (Array.isArray(pendingBlankProductDeletions)) {
                pendingSavedProductDeletions = pendingBlankProductDeletions;
            }

            // Delete each saved family only when the user runs the normal Save process
            for (
                let pendingDeleteIndex = 0;
                pendingDeleteIndex < pendingSavedProductDeletions.length;
                pendingDeleteIndex++
            ) {

                // Read the pending configured product family
                const pendingProductDeletion =
                    pendingSavedProductDeletions[pendingDeleteIndex];

                // Skip malformed pending rows
                if (!pendingProductDeletion?.id) { continue; }

                // Show which cleanup stage is running
                updateSaveProgress({
                    progress: 55,
                    message: "Removing blank products...",
                    subMessage:
                        "Removing product " +
                        String(pendingDeleteIndex + 1) +
                        " of " +
                        String(pendingSavedProductDeletions.length),
                });

                // Delete the saved configured product family
                const deletePendingProductResponse = await deleteDesignProduct(
                    token,
                    designOrTemplate,
                    typeId,
                    pendingProductDeletion.id
                );

                // Stop when a queued product could not be removed
                if (!deletePendingProductResponse?.success) {

                    // Clear saving state
                    setDesignIsSaving(false);

                    // Show the deletion failure
                    toastError({
                        title: "Error",
                        message:
                            "Could not remove blank product " +
                            String(
                                pendingProductDeletion?.pod_product_sku ||
                                pendingProductDeletion.id
                            ) +
                            ": " +
                            String(
                                deletePendingProductResponse?.error_message ||
                                "Unknown error"
                            ),
                        autoCloseMs: 6000
                    });

                    // Stop the complete save
                    return;

                }

                // Remove the successfully deleted family from the pending queue
                setPendingBlankProductDeletions((previousPendingDeletions) => {

                    // Start with an empty pending deletion list
                    let safePendingDeletions = [];

                    // Use the pending list when it is valid
                    if (Array.isArray(previousPendingDeletions)) {
                        safePendingDeletions = previousPendingDeletions;
                    }

                    // Remove the matching configured product family
                    return safePendingDeletions.filter((pendingProduct) => {
                        return String(pendingProduct?.id || "") !==
                            String(pendingProductDeletion.id);
                    });

                });

            }

        }


        // Remove configured product families only when this design/template owns its product configuration
        if (!productsManagedByTemplates) {

            // Build the role-key set which is currently selected and still backed by an image
            const usableDesignRoleKeys = getUsableDesignRoleKeys(
                latestDesignRoles,
                latestSelectedRoles
            );

            // Read configured product families safely
            const configuredProductFamilies = Array.isArray(latestProductsForSave) ? latestProductsForSave : [];

            // Store product families which remain printable
            const validProductFamilies = [];

            // Store product families which must be removed
            const invalidProductFamilies = [];

            // Split the configured products into valid and invalid families
            configuredProductFamilies.forEach((configuredProduct) => {

                // Keep families where the parent or at least one child can print a usable role
                if (configuredProductFamilyHasUsableRole(configuredProduct, usableDesignRoleKeys)) {
                    validProductFamilies.push(configuredProduct);
                    return;
                }

                // Remove families which would otherwise create blank products
                invalidProductFamilies.push(configuredProduct);

            });

            // Delete saved invalid families before any later product processing
            for (let invalidProductIndex = 0; invalidProductIndex < invalidProductFamilies.length; invalidProductIndex++) {

                // Read the invalid configured product family
                const invalidProductFamily = invalidProductFamilies[invalidProductIndex];

                // Unsaved product families only need removing from local save data
                if (!invalidProductFamily?.id) { continue; }

                // Show which cleanup stage is running
                updateSaveProgress({
                    progress: 55,
                    message: "Removing incompatible products...",
                    subMessage: "Removing product " + String(invalidProductIndex + 1) + " of " + String(invalidProductFamilies.length),
                });

                // Delete the complete configured product family through the dedicated endpoint
                const deleteProductResponse = await deleteDesignProduct(
                    token,
                    designOrTemplate,
                    typeId,
                    invalidProductFamily.id
                );

                // Stop when an invalid saved product could not be removed safely
                if (!deleteProductResponse?.success) {

                    // Clear saving state
                    setDesignIsSaving(false);

                    // Show the deletion failure
                    toastError({
                        title: "Error",
                        message: "Could not remove incompatible product " + String(invalidProductFamily?.pod_product_sku || invalidProductFamily.id) + ": " + String(deleteProductResponse?.error_message || "Unknown error"),
                        autoCloseMs: 6000
                    });

                    // Stop the complete save
                    return;

                }

            }

            // Use only printable product families for every remaining save stage
            latestProductsForSave = validProductFamilies;

        }



        // Only product controls edited directly on the main design page belong to this save
        const productsNeedSaving =
            Boolean(props?.productGridChangesPending) &&
            !productsManagedByTemplates;


        // ********* PREPARE POD DESIGN/TEMPLATE PRODUCT IMAGES *********

        // Product mockup image rows are now background jobs.
        // The portal saves pending rows and does not generate images during the design save.
        updateSaveProgress({
            progress: 80,
            message: "Preparing product images...",
            subMessage: "Saving selected image rows",
        });

        // Use the latest live products exactly as selected in the portal
        const nextProducts = Array.isArray(latestProductsForSave) ? latestProductsForSave : [];

        // Start with no newly-created local configured products
        let newlyCreatedProductsWithIds = [];

        // Template-driven designs must never persist dormant local product changes
        if (!productsManagedByTemplates) {

            // Find products which the Add Product picker created after the editor's original snapshot was loaded
            newlyCreatedProductsWithIds = nextProducts.filter((product) => {

                // This path only handles products which already have a real id and temporary portal id
                if (!product?.id || !product?.client_configured_product_id) { return false; }

                // Check whether this configured product existed when the editor opened
                const productExistedOriginally = (originalPodDesignDetails?.products || []).some((originalProduct) => {
                    return String(originalProduct?.id || "") === String(product.id);
                });

                // Keep only products created by the picker before this complete save began
                return !productExistedOriginally;

            });

        }

        // Build the next full pod design object without running portal-side mockup generation
        const nextPodDesignDetails = {

            // Keep existing pod design fields
            ...latestPodDesignDetails,

            // Keep the latest selected products and pending image rows
            products: nextProducts,

        };

        // Update local state with the save-time product snapshot
        setPodDesignDetails(nextPodDesignDetails);

        // This stores newly created client ids so they are not sent again in the general product update
        const individuallySavedClientProductIds = new Set();

        // This normalises product image rows for the dedicated image API
        const normaliseProductImagesForSave = (images) => {

            // Return a clean image array
            return Array.isArray(images)
                ? images.map((imageRow) => {

                    // Create the clean image row
                    const cleanImage = {};

                    // Include image id when present
                    if (imageRow?.image_id) { cleanImage.image_id = Number(imageRow.image_id); }

                    // Include the complete per-location mockup rotation configuration when present
                    if (imageRow?.mockup_id) {

                        const rotationConfig = normaliseRotationConfig(imageRow?.rotation_config);

                        cleanImage.mockup_id = Number(imageRow.mockup_id);

                        // Missing configuration stays missing so the API fails loudly rather than inventing one
                        if (rotationConfig.length > 0) {
                            cleanImage.rotation_config = rotationConfig;
                        }

                    }

                    // Return the clean image row
                    return cleanImage;

                }).filter((imageRow) => imageRow.image_id || imageRow.mockup_id)
                : [];

        };

        // Decide whether a newly-created configured product still needs its image selection persisted by the portal
        const createdProductNeedsPortalImageSave = (configuredProduct) => {

            // Template image rows are the user's curated candidate pool and must always be persisted
            if (designOrTemplate === "template") {
                return true;
            }

            let autoMockupCount = Number.parseInt(
                configuredProduct?.auto_mockup_count ?? 3,
                10
            );

            // Invalid counts fall back to the normal automatic default
            if (!Number.isInteger(autoMockupCount) || autoMockupCount < 0) {
                autoMockupCount = 3;
            }

            // Manual designs own their exact selection; automatic designs are selected by the API after roles are saved
            return autoMockupCount <= 0;

        };

        // This saves user-owned image selections for a newly-created configured product and its children
        const saveImagesForCreatedProduct = async (configuredProduct) => {

            // Read the configured product id
            const configuredProductId = String(configuredProduct?.id || "");

            // Fail if the configured product id is missing
            if (!configuredProductId) {
                return {
                    success: false,
                    error_message: "Cannot save product images because the configured product id is missing.",
                };
            }

            // Check whether this parent owns an explicit image selection which the API must not replace
            const saveParentImages =
                createdProductNeedsPortalImageSave(
                    configuredProduct
                );

            // Persist template candidate pools and manual design selections only after their configured roles exist
            if (saveParentImages) {

                // Normalise parent images
                const parentImages = normaliseProductImagesForSave(configuredProduct?.images);

                // Check that the new parent product has at least one mockup source row
                const parentHasMockupImage = parentImages.some((imageRow) => Boolean(imageRow?.mockup_id));

                // Fail instead of leaving a newly-created manually selected product without a mockup source row
                if (!parentHasMockupImage) {
                    return {
                        success: false,
                        error_message: "Cannot save new product " + String(configuredProduct?.pod_product_sku || "") + " because it has no mockup image row.",
                    };
                }

                // Save parent images when present
                if (parentImages.length > 0) {

                    // Save through the dedicated image endpoint
                    const parentImageResponse = await updatePodDesignProductImages(token, designOrTemplate, typeId, configuredProductId, parentImages);

                    // Stop on error
                    if (!parentImageResponse?.success) { return parentImageResponse; }

                }

            }

            // Get child products
            const children = Array.isArray(configuredProduct?.children) ? configuredProduct.children : [];

            // Go through children
            for (const childProduct of children) {

                // Read child configured product id
                const childConfiguredProductId = String(childProduct?.id || "");

                // Automatic design children are selected by the API after their configured roles are saved
                if (!createdProductNeedsPortalImageSave(childProduct)) {
                    continue;
                }

                // Normalise child images
                const childImages = normaliseProductImagesForSave(childProduct?.images);

                // Check that the new child product has at least one mockup source row
                const childHasMockupImage = childImages.some((imageRow) => Boolean(imageRow?.mockup_id));

                // Fail instead of leaving a newly-created manually selected child product without a mockup source row
                if (!childHasMockupImage) {
                    return {
                        success: false,
                        error_message: "Cannot save new child product " + String(childProduct?.pod_product_sku || "") + " because it has no mockup image row.",
                    };
                }

                // Fail if the child configured product id is missing
                if (!childConfiguredProductId) {
                    return {
                        success: false,
                        error_message: "Cannot save child product images because the configured product id is missing.",
                    };
                }

                // Save through the dedicated image endpoint
                const childImageResponse = await updatePodDesignProductImages(token, designOrTemplate, typeId, childConfiguredProductId, childImages);

                // Stop on error
                if (!childImageResponse?.success) { return childImageResponse; }

            }

            // Return success
            return {
                success: true,
            };

        };

        // Existing configured product image rows are saved immediately when they change.
        // Only newly-created configured products need their image rows saved during the main design save.

        // Turn every selected mockup image row back into a pending Reaper job
        const buildPendingMockupImageRowsForSave = (images) => {

            // Read image rows safely
            const safeImages = Array.isArray(images) ? images : [];

            // Clear generated data from mockup rows while preserving manual bucket images
            return safeImages.map((imageRow) => {

                // Manual bucket images are not generated by Reaper
                if (!imageRow?.mockup_id) { return imageRow; }

                // Preserve the exact mockup configuration but remove its generated result
                return {
                    ...imageRow,
                    image_id: null,
                    image_url: null,
                    thumbnail_url: null,
                    thumbUrl: null,
                    mockup_image_url: null,
                    mockup_thumbnail_url: null,
                };

            });

        };

        // Queue every existing configured parent and child mockup after a design-role change
        const queueExistingProductMockupsAfterDesignRoleChange = async (
            products,
            designRoles,
            roleSelection,
            priorityConfiguredProductId = null
        ) => {

            // Read the product array safely
            let safeProducts = [];

            if (Array.isArray(products)) {
                safeProducts = [...products];
            }

            // Move the requested configured product family to the front
            const safePriorityConfiguredProductId =
                String(priorityConfiguredProductId || "");

            if (safePriorityConfiguredProductId) {

                // Find the requested configured product family
                const priorityProductIndex =
                    safeProducts.findIndex((product) => {

                        // Match either saved or temporary configured identity
                        return (
                            String(product?.id || "") ===
                                safePriorityConfiguredProductId ||
                            String(
                                product?.client_configured_product_id ||
                                ""
                            ) === safePriorityConfiguredProductId
                        );

                    });

                // Retain the existing order of every other family
                if (priorityProductIndex > 0) {

                    const priorityProducts =
                        safeProducts.splice(
                            priorityProductIndex,
                            1
                        );

                    safeProducts.unshift(priorityProducts[0]);

                }

            }

            // Get the design roles which still have selected image data
            const usableDesignRoleKeys =
                getUsableDesignRoleKeys(
                    designRoles,
                    roleSelection
                );

            // Build the effective remaining roles for one parent or child
            const getRemainingConfiguredRoles = (
                configuredProduct,
                inheritedRoles = []
            ) => {

                // Store roles by key so an exact child role overrides its parent role
                const rolesByKey = new Map();

                inheritedRoles.forEach((configuredRole) => {

                    // Read the inherited role key
                    const roleKey =
                        String(
                            configuredRole?.role || ""
                        ).trim();

                    // Keep only roles whose design image still exists
                    if (
                        roleKey &&
                        usableDesignRoleKeys.has(roleKey)
                    ) {
                        rolesByKey.set(
                            roleKey,
                            configuredRole
                        );
                    }

                });

                // Read the exact parent or child roles
                let exactRoles = [];

                if (Array.isArray(configuredProduct?.roles)) {
                    exactRoles = configuredProduct.roles;
                }

                exactRoles.forEach((configuredRole) => {

                    // Read the exact role key
                    const roleKey =
                        String(
                            configuredRole?.role || ""
                        ).trim();

                    // Keep only roles whose design image still exists
                    if (
                        roleKey &&
                        usableDesignRoleKeys.has(roleKey)
                    ) {
                        rolesByKey.set(
                            roleKey,
                            configuredRole
                        );
                    }

                });

                // Return inherited roles with any child overrides applied
                return Array.from(
                    rolesByKey.values()
                );

            };

            // Reconcile selected sources before turning them into pending jobs
            const nextProductsWithPendingMockups =
                safeProducts.map((product) => {

                    // Get this parent's remaining configured roles
                    const parentRoles =
                        getRemainingConfiguredRoles(
                            product
                        );

                    // Read the current parent images safely
                    let parentImages = [];

                    if (Array.isArray(product?.images)) {
                        parentImages = product.images;
                    }

                    // Remove parent mockups which no longer use a remaining role
                    if (product?.pod_product) {

                        parentImages =
                            removeIncompatibleMockupImages(
                                parentImages,
                                product.pod_product,
                                parentRoles
                            );

                    }

                    // Turn the surviving parent mockups into pending jobs
                    const nextParentImages =
                        buildPendingMockupImageRowsForSave(
                            parentImages
                        );

                    // Preserve the existing children by default
                    let nextChildren =
                        product?.children;

                    // Reconcile every configured child independently
                    if (Array.isArray(product?.children)) {

                        nextChildren =
                            product.children.map((child) => {

                                // Child settings override inherited parent settings
                                const childRoles =
                                    getRemainingConfiguredRoles(
                                        child,
                                        parentRoles
                                    );

                                // Read the current child images safely
                                let childImages = [];

                                if (Array.isArray(child?.images)) {
                                    childImages = child.images;
                                }

                                // Remove child mockups which no longer use a remaining role
                                if (child?.pod_product) {

                                    childImages =
                                        removeIncompatibleMockupImages(
                                            childImages,
                                            child.pod_product,
                                            childRoles
                                        );

                                }

                                // Return the child with its surviving mockups queued
                                return {
                                    ...child,
                                    images:
                                        buildPendingMockupImageRowsForSave(
                                            childImages
                                        ),
                                };

                            });

                    }

                    // Return the parent and children with cleaned pending rows
                    return {
                        ...product,
                        images: nextParentImages,
                        children: nextChildren,
                    };

                });

            // Save pending rows for each existing configured parent and child
            for (let productIndex = 0; productIndex < nextProductsWithPendingMockups.length; productIndex++) {

                // Read the pending parent product
                const product = nextProductsWithPendingMockups[productIndex];

                // Read the configured parent product id
                const configuredProductId = String(product?.id || "");

                // Read parent pending image rows safely
                const parentImages = Array.isArray(product?.images) ? product.images : [];

                // Replace the complete parent image list, including valid empty replacements
                if (/^\d+$/.test(configuredProductId)) {

                    // Replace the parent image rows through the dedicated endpoint
                    const parentImageResponse = await updatePodDesignProductImages(
                        token,
                        designOrTemplate,
                        typeId,
                        configuredProductId,
                        normaliseProductImagesForSave(parentImages)
                    );

                    // Stop if the parent image rows could not be queued
                    if (!parentImageResponse?.success) {
                        return parentImageResponse;
                    }

                }

                // Read configured children safely
                const children = Array.isArray(product?.children) ? product.children : [];

                // Save pending rows for each existing configured child
                for (let childIndex = 0; childIndex < children.length; childIndex++) {

                    // Read the pending child product
                    const child = children[childIndex];

                    // Read the configured child product id
                    const childConfiguredProductId = String(child?.id || "");

                    // Read child pending image rows safely
                    const childImages = Array.isArray(child?.images) ? child.images : [];

                    // Skip only unsaved configured children
                    if (!/^\d+$/.test(childConfiguredProductId)) {
                        continue;
                    }

                    // Replace the child image rows through the dedicated endpoint
                    const childImageResponse = await updatePodDesignProductImages(
                        token,
                        designOrTemplate,
                        typeId,
                        childConfiguredProductId,
                        normaliseProductImagesForSave(childImages)
                    );

                    // Stop if the child image rows could not be queued
                    if (!childImageResponse?.success) {
                        return childImageResponse;
                    }

                }

            }

            // Return the pending local product state
            return {
                success: true,
                products: nextProductsWithPendingMockups,
            };

        };

        // Normalise a product payload before sending it to the product API
        const normaliseProductPayloadForSave = (product) => {

            // Copy the product so we do not mutate the live state object
            const productToSave = { ...product };

            // Remove client-only sort data before sending to the API
            delete productToSave.client_created_at;

            // Product image rows are saved through the dedicated image endpoint
            delete productToSave.images;

            // If parent colourways are an array, make sure only colourway codes are sent
            if (Array.isArray(productToSave.colourways)) {

                // Convert colourway objects or mixed values into code strings
                productToSave.colourways = normaliseColourwayCodes(productToSave.colourways);

            }

            // If child products are present, normalise their colourways too
            if (Array.isArray(productToSave.children)) {

                // Copy and normalise each child product
                productToSave.children = productToSave.children.map((child) => {

                    // Copy the child so we do not mutate the live state object
                    const childToSave = { ...child };

                    // Remove client-only sort data before sending to the API
                    delete childToSave.client_created_at;

                    // Product image rows are saved through the dedicated image endpoint
                    delete childToSave.images;

                    // If child colourways are an array, make sure only colourway codes are sent
                    if (Array.isArray(childToSave.colourways)) {

                        // Convert colourway objects or mixed values into code strings
                        childToSave.colourways = normaliseColourwayCodes(childToSave.colourways);

                    }

                    // Return the cleaned child product
                    return childToSave;

                });

            }

            // Return the cleaned parent product
            return productToSave;

        };

        // Start with no new local configured products to create
        let newProductsToCreate = [];

        // Template-driven designs must leave their dormant local products untouched
        if (!productsManagedByTemplates && Array.isArray(nextPodDesignDetails?.products)) {
            newProductsToCreate = nextPodDesignDetails.products.filter((product) => !product?.id);
        }

        // Save new configured products before saving roles
        for (let newProductIndex = 0; newProductIndex < newProductsToCreate.length; newProductIndex++) {

            // Read the new product
            let productToCreate = newProductsToCreate[newProductIndex];

            // Read the temporary client id
            const productClientConfiguredProductId = String(productToCreate?.client_configured_product_id || "");

            // Fail fast if the new product has no temporary client id
            if (!productClientConfiguredProductId) {

                // Clear saving state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "New product is missing a temporary portal reference, so its roles cannot be saved safely.", autoCloseMs: 6000 });

                // Stop save
                return;

            }

            // Ensure unsaved children also have temporary client ids before the API save
            productToCreate = {
                ...productToCreate,
                children: Array.isArray(productToCreate?.children)
                    ? productToCreate.children.map((child, childIndex) => {

                        // If the child already has an id or temporary id, keep it
                        if (child?.id || child?.client_configured_product_id) { return child; }

                        // Add a temporary child id before saving
                        return {
                            ...child,
                            client_configured_product_id: `${productClientConfiguredProductId}_child_${childIndex}`,
                        };

                    })
                    : productToCreate.children,
            };

            // Put any newly-added child temporary ids back into the pending product list
            nextPodDesignDetails.products = nextPodDesignDetails.products.map((product) => {

                // Keep non-matching products unchanged
                if (String(product?.client_configured_product_id || "") !== productClientConfiguredProductId) { return product; }

                // Return the updated product
                return productToCreate;

            });

            // Update progress for the current new product
            updateSaveProgress({
                progress: 82,
                message: "Adding new products...",
                subMessage: `Adding new product ${newProductIndex + 1} of ${newProductsToCreate.length}`,
            });

            // Save this one new product only
            const createProductResponse = await updatePodDesignProducts(token, designOrTemplate, typeId, [normaliseProductPayloadForSave(productToCreate)]);

            // Stop if the product could not be created
            if (!createProductResponse?.success) {

                // Clear saving state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "Could not add new product: " + createProductResponse?.error_message, autoCloseMs: 6000 });

                // Stop save
                return;

            }

            // Read saved products returned by the API
            const savedRows = Array.isArray(createProductResponse?.results) ? createProductResponse.results : [];

            // Build a flat list of saved parent and child products
            const flattenedSavedRows = [];

            // Go through saved top-level products
            savedRows.forEach((savedRow) => {

                // Add the parent row
                flattenedSavedRows.push(savedRow);

                // Add child rows when present
                if (Array.isArray(savedRow?.children)) {
                    savedRow.children.forEach((childRow) => {
                        flattenedSavedRows.push(childRow);
                    });
                }

            });

            // Build a lookup from temporary client id to saved API row
            const savedRowsByClientId = new Map(
                flattenedSavedRows
                    .filter((row) => row?.client_configured_product_id)
                    .map((row) => [String(row.client_configured_product_id), row])
            );

            // Find the saved parent row using the temporary client id
            const savedParentRow = savedRowsByClientId.get(productClientConfiguredProductId);

            // Stop if the API did not return the new configured product id
            if (!savedParentRow?.id) {

                // Clear saving state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "The API added a new product but did not return its configured product id.", autoCloseMs: 6000 });

                // Stop save
                return;

            }

            // Track missing child ids
            let missingChildConfiguredProductId = "";

            // Add returned child ids to the product
            const updatedChildren = Array.isArray(productToCreate?.children)
                ? productToCreate.children.map((child) => {

                    // Existing children already have ids
                    if (child?.id) { return child; }

                    // Read child temporary id
                    const childClientConfiguredProductId = String(child?.client_configured_product_id || "");

                    // Read saved child row
                    const savedChildRow = savedRowsByClientId.get(childClientConfiguredProductId);

                    // If the child id was not returned, record the problem
                    if (!savedChildRow?.id) {
                        missingChildConfiguredProductId = childClientConfiguredProductId;
                        return child;
                    }

                    // Return child with real configured id
                    return {
                        ...child,
                        id: savedChildRow.id,
                    };

                })
                : productToCreate.children;

            // Stop if a child id was not returned
            if (missingChildConfiguredProductId) {

                // Clear saving state
                setDesignIsSaving(false);

                // Toast error message
                toastError({ title: "Error", message: "The API added a new child product but did not return its configured product id.", autoCloseMs: 6000 });

                // Stop save
                return;

            }

            // Build the saved product from the local product, only taking the real saved ids from the API
            const savedProductWithIds = {
                ...productToCreate,
                id: savedParentRow.id,
                sku: savedParentRow.sku,
                product_id: savedParentRow.product_id,
                client_configured_product_id: savedParentRow.client_configured_product_id || productToCreate.client_configured_product_id,
                children: updatedChildren,
            };

            // Defer every image decision until the configured product roles have been stored
            newlyCreatedProductsWithIds.push(savedProductWithIds);

            // Update the pending save data with the real configured product id
            nextPodDesignDetails.products = nextPodDesignDetails.products.map((product) => {

                // Keep non-matching products unchanged
                if (String(product?.client_configured_product_id || "") !== productClientConfiguredProductId) { return product; }

                // Return the saved product with ids
                return savedProductWithIds;

            });

            // Track this product so the general product update does not save it again
            individuallySavedClientProductIds.add(productClientConfiguredProductId);

        }

        // Push newly assigned configured ids into local state before role saving
        if (individuallySavedClientProductIds.size > 0) {

            // Update local details with the real configured product ids
            setPodDesignDetails((prevDetails) => {

                // Return updated details
                return {
                    ...prevDetails,
                    products: nextPodDesignDetails.products,
                };

            });

        }

        

        // ********* UPDATE POD DESIGN/TEMPLATE PRODUCT TRANSLATIONS *********

        // Create a lookup of original parent products
        const originalProductsMap = new Map(
            (originalPodDesignDetails?.products || [])
                .filter((product) => product?.id)
                .map((product) => [String(product.id), product])
        );

        // Create a lookup of original child products
        const originalChildProductsMap = new Map(
            (originalPodDesignDetails?.products || [])
                .flatMap((product) => product?.children || [])
                .filter((child) => child?.id)
                .map((child) => [String(child.id), child])
        );

        // Build parent and child translation save candidates
        const productTranslationSaveCandidates = (nextPodDesignDetails.products || []).flatMap((product) => {

            // Start with the parent product
            const candidates = [product];

            // Add child products
            if (Array.isArray(product?.children)) {

                candidates.push(...product.children);

            }

            // Return parent and child products
            return candidates;

        });

        // Start with no local product translations to save
        let productsRequiringTranslationSave = [];

        // Template-driven designs must leave dormant local product translations untouched
        if (!productsManagedByTemplates) {

            // Find all parent and child products that need translations saving
            productsRequiringTranslationSave = productTranslationSaveCandidates.filter((product) => {

            // Skip products without a configured id
            if (!product?.id) {

                return false;

            }

            // Skip products without translations
            if (!Array.isArray(product.translations) || product.translations.length === 0) {

                return false;

            }

            // Find the original parent product
            const originalParentProduct = originalProductsMap.get(String(product.id));

            // Find the original child product
            const originalChildProduct = originalChildProductsMap.get(String(product.id));

            // Use whichever original product exists
            const originalProduct = originalParentProduct || originalChildProduct || null;

            // This is a newly-created parent or child product
            const isNewProduct = individuallySavedClientProductIds.has(
                String(product.client_configured_product_id || "")
            );

            // Compare translations
            const translationsChanged = !deepEqual(
                originalProduct?.translations || [],
                product.translations
            );

                // Save if new or changed
                return isNewProduct || translationsChanged;

            });

        }

        // Save parent and child product translations one by one
        for (const product of productsRequiringTranslationSave) {

            // Save this configured product's translations
            const updateProductTranslationsResponse = await updatePodDesignProductTranslations(
                token,
                designOrTemplate,
                typeId,
                product.id,
                product.translations
            );

            // Stop if the save failed
            if (!updateProductTranslationsResponse?.success) {

                setDesignIsSaving(false);

                toastError({
                    title: "Error",
                    message: "Could not update product translations: " + updateProductTranslationsResponse?.error_message,
                    autoCloseMs: 6000
                });

                return;

            }

        }

        // ********* UPDATE POD DESIGN/TEMPLATE PRODUCT TRANSLATIONS END *********



        // ********* UPDATE POD DESIGN/TEMPLATE PRODUCTS *********
        
        // Update progress before saving products
        updateSaveProgress({
            progress: 82,
            message: "Saving products...",
            subMessage: "Uploading generated mockup references",
        });

        // Store the normal product-update response
        let updateProductsResponse = {
            success: true,
            results: [],
        };

        // Save only normal product controls edited on the main design page
        if (productsNeedSaving) {

            // Build the original parent-product lookup
            const originalProductsById = new Map();

            if (Array.isArray(originalPodDesignDetails?.products)) {

                originalPodDesignDetails.products.forEach((originalProduct) => {

                    // Ignore unsaved original rows
                    if (!originalProduct?.id) {
                        return;
                    }

                    // Store the original configured product
                    originalProductsById.set(
                        String(originalProduct.id),
                        originalProduct
                    );

                });

            }

            // Store minimal parent-family update payloads
            const productsToSave = [];

            if (Array.isArray(nextPodDesignDetails.products)) {

                nextPodDesignDetails.products.forEach((product) => {

                    // Only existing configured products can be updated here
                    if (!product?.id) {
                        return;
                    }

                    // Find the matching original parent
                    const originalProduct =
                        originalProductsById.get(
                            String(product.id)
                        );

                    if (!originalProduct) {
                        return;
                    }

                    // Start the minimal parent update payload
                    const productPayload = {
                        id: product.id,
                        pod_product_sku:
                            product.pod_product_sku,
                    };

                    // Track whether this family has any normal product changes
                    let productHasChanges = false;

                    // Normalise the current parent active state
                    const currentProductActive =
                        String(product?.active || "Y");

                    // Normalise the saved parent active state
                    const originalProductActive =
                        String(originalProduct?.active || "Y");

                    // Save a changed parent active state
                    if (
                        currentProductActive !==
                        originalProductActive
                    ) {

                        productPayload.active =
                            currentProductActive;

                        productHasChanges = true;

                    }

                    // Normalise the current parent marketplace state
                    const currentProductMarketplace =
                        String(product?.marketplace || "N");

                    // Normalise the saved parent marketplace state
                    const originalProductMarketplace =
                        String(originalProduct?.marketplace || "N");

                    // Save a changed parent marketplace state
                    if (
                        currentProductMarketplace !==
                        originalProductMarketplace
                    ) {

                        productPayload.marketplace =
                            currentProductMarketplace;

                        productHasChanges = true;

                    }

                    // Normalise the current parent store state
                    const currentProductStore =
                        String(product?.store || "Y");

                    // Normalise the saved parent store state
                    const originalProductStore =
                        String(originalProduct?.store || "Y");

                    // Save a changed parent store state
                    if (
                        currentProductStore !==
                        originalProductStore
                    ) {

                        productPayload.store =
                            currentProductStore;

                        productHasChanges = true;

                    }

                    // Build the original child lookup for this family
                    const originalChildrenById =
                        new Map();

                    if (
                        Array.isArray(
                            originalProduct?.children
                        )
                    ) {

                        originalProduct.children.forEach(
                            (originalChild) => {

                                // Ignore children without configured ids
                                if (!originalChild?.id) {
                                    return;
                                }

                                // Store this original child
                                originalChildrenById.set(
                                    String(originalChild.id),
                                    originalChild
                                );

                            }
                        );

                    }

                    // Store changed child active-state payloads
                    const childPayloads = [];

                    if (Array.isArray(product?.children)) {

                        product.children.forEach((child) => {

                            // Only existing children can be updated here
                            if (!child?.id) {
                                return;
                            }

                            // Find the matching original child
                            const originalChild =
                                originalChildrenById.get(
                                    String(child.id)
                                );

                            if (!originalChild) {
                                return;
                            }

                            // Normalise the current child active state
                            const currentChildActive =
                                String(child?.active || "Y");

                            // Normalise the saved child active state
                            const originalChildActive =
                                String(
                                    originalChild?.active ||
                                    "Y"
                                );

                            // Ignore unchanged child active states
                            if (
                                currentChildActive ===
                                originalChildActive
                            ) {
                                return;
                            }

                            // Add the minimal child update
                            childPayloads.push({
                                id: child.id,
                                pod_product_sku:
                                    child.pod_product_sku,
                                active:
                                    currentChildActive,
                            });

                        });

                    }

                    // Add changed children to the family payload
                    if (childPayloads.length > 0) {

                        productPayload.children =
                            childPayloads;

                        productHasChanges = true;

                    }

                    // Ignore families with no normal product changes
                    if (!productHasChanges) {
                        return;
                    }

                    // Store this update payload
                    productsToSave.push(
                        productPayload
                    );

                });

            }

            // Save the active-state changes
            if (productsToSave.length > 0) {

                updateSaveProgress({
                    progress: 84,
                    message: "Saving products...",
                    subMessage:
                        "Saving product active states",
                });

                updateProductsResponse =
                    await updatePodDesignProducts(
                        token,
                        designOrTemplate,
                        typeId,
                        productsToSave
                    );

            }

        }



        

        // ********* UPDATE POD DESIGN/TEMPLATE PRODUCT ROLES *********
                    
        // Update progress after saving products
        updateSaveProgress({
            progress: 88,
            message: "Products saved",
            subMessage: "Saving product role data",
        });

        // Start with an empty product role save array
        let productRoleProducts = [];

        // Read the products prepared by the normal product save
        if (Array.isArray(nextPodDesignDetails?.products)) {
            productRoleProducts = nextPodDesignDetails.products;
        }

        // Restore complete role data for products already created by the Add Product picker
        productRoleProducts = productRoleProducts.map((product) => {

            // Find the complete picker-created product by its configured product id
            const pickerCreatedProduct = newlyCreatedProductsWithIds.find((newProduct) => {
                return String(newProduct?.id || "") === String(product?.id || "");
            });

            // Use the complete product so its parent and child roles can be saved
            if (pickerCreatedProduct) {
                return pickerCreatedProduct;
            }

            // Keep every other prepared product unchanged
            return product;

        });

        // Build the product role save data using the complete product rows
        const productRoleSaveData = {
            ...cleanedPreparedData,
            products: productRoleProducts,
        };

        // Save local product-specific role changes only when this design/template owns its product configuration
        if (!productsManagedByTemplates) {

            const productRoleUpdateResponse = await UpdatePodDesignProductRoles(
                designOrTemplate,
                typeId,
                productRoleSaveData,
                originalPodDesignDetails
            );

            // Stop if configured product roles could not be saved
            if (!productRoleUpdateResponse?.success) { return; }

        }

        // Save pending mockup rows only after the configured product roles have been stored
        for (const newlyCreatedProduct of newlyCreatedProductsWithIds) {

            // Save the parent and child pending mockup rows through the dedicated image endpoint
            const imageSaveResponse = await saveImagesForCreatedProduct(newlyCreatedProduct);

            // Stop if any pending mockup rows could not be saved
            if (!imageSaveResponse?.success) {

                // Clear saving state
                setDesignIsSaving(false);

                // Show the image save failure
                toastError({ title: "Error", message: "Could not save new product images: " + imageSaveResponse?.error_message, autoCloseMs: 6000 });

                // Stop the complete design save
                return;

            }

        }

        // Requeue dormant local mockups only when a template was removed without a role update
        if (templatesWereCleared && !designRolesNeedSaving && !productsManagedByTemplates) {

            // Role updates now clean and requeue affected mockups inside the same API request
            const queueResponse =
                await queueExistingProductMockupsAfterDesignRoleChange(
                    latestProductsForSave,
                    latestDesignRoles,
                    latestSelectedRoles,
                    props?.priorityConfiguredProductId
                );

            // Stop if any pending image rows could not be saved
            if (!queueResponse?.success) {

                // Clear saving state
                setDesignIsSaving(false);

                // Show the queue failure
                toastError({
                    title: "Error",
                    message: "Could not queue updated product mockups: " + (queueResponse?.error_message || "Unknown error"),
                    autoCloseMs: 6000
                });

                // Stop the design save
                return;

            }

            // Build lookups for queued existing parent and child image rows
            const queuedParentImagesById = new Map();
            const queuedChildImagesById = new Map();

            // Store queued image rows only for products with real configured ids
            queueResponse.products.forEach((queuedProduct) => {

                // Store queued parent image rows
                if (queuedProduct?.id) {
                    queuedParentImagesById.set(
                        String(queuedProduct.id),
                        Array.isArray(queuedProduct?.images) ? queuedProduct.images : []
                    );
                }

                // Read queued children safely
                const queuedChildren = Array.isArray(queuedProduct?.children)
                    ? queuedProduct.children
                    : [];

                // Store queued child image rows
                queuedChildren.forEach((queuedChild) => {

                    // Skip children without a real configured id
                    if (!queuedChild?.id) { return; }

                    // Store this configured child's queued image rows
                    queuedChildImagesById.set(
                        String(queuedChild.id),
                        Array.isArray(queuedChild?.images) ? queuedChild.images : []
                    );

                });

            });

            // Update only the image arrays so all other live product data and newly assigned ids remain intact
            setPodDesignDetails((previousDetails) => ({

                // Keep current design/template details
                ...previousDetails,

                // Update queued parent and child image arrays
                products: (Array.isArray(previousDetails?.products) ? previousDetails.products : []).map((product) => {

                    // Read queued parent image rows
                    const queuedParentImages = queuedParentImagesById.get(
                        String(product?.id || "")
                    );

                    // Update any queued configured children
                    const nextChildren = Array.isArray(product?.children)
                        ? product.children.map((child) => {

                            // Read queued child image rows
                            const queuedChildImages = queuedChildImagesById.get(
                                String(child?.id || "")
                            );

                            // Keep children which were not queued unchanged
                            if (!queuedChildImages) { return child; }

                            // Return the child with pending image rows
                            return {
                                ...child,
                                images: queuedChildImages,
                            };

                        })
                        : product?.children;

                    // Keep parents which were not queued unchanged apart from queued children
                    if (!queuedParentImages) {
                        return {
                            ...product,
                            children: nextChildren,
                        };
                    }

                    // Return the parent with pending image rows
                    return {
                        ...product,
                        images: queuedParentImages,
                        children: nextChildren,
                    };

                }),

            }));

        }





        // ********* CHECK SUCCESS OF CALLS *********
     
        // This builds a clean combined error message, only including messages that exist
        const translationErrorMessage = translationsNeedSaving && !updateTranslationsResponse?.success
            ? String(updateTranslationsResponse?.error_message)
            : "";

        // This builds a clean combined error message, only including messages that exist
        const productsErrorMessage = productsNeedSaving && !updateProductsResponse?.success
            ? String(updateProductsResponse?.error_message)
            : "";

        // This combines both error strings and trims extra spaces
        const combinedErrorMessage = `${translationErrorMessage} ${productsErrorMessage}`.trim();

        // Check if anything needed updating and if it did whether it failed
        if (combinedErrorMessage) {

            // Clear loading state
            setDesignIsSaving(false);

            // Toast fail message
            toastError({
                title: "Error",
                message: `Could not update ${type}: ${combinedErrorMessage}`,
                autoCloseMs: 6000
            });

            // Return early
            return;

        }

        // If we've added a new design or template
        if (isAddingNewDesign) {
            
            // Clear adding new flag
            setIsAddingNewDesign(false);

            // Set selected design / template
            setSelectedDesignOrTemplateId(typeId);

        }

        // Complete progress UI
        updateSaveProgress({
            progress: 100,
            message: "Save complete",
            subMessage: type === "template" ? "Template saved" : "Design saved",
        });

        // Fetch the designs so the grid/table updates
        fetchPodDesigns(designOrTemplate);

        // Refresh the saved design before another workflow continues
        const refreshedPodDesignDetails = await fetchPodDesignDetails(typeId, false);

        // Clear loading state
        setDesignIsSaving(false);

        // Return success with the refreshed design for any workflow continuing after the save
        return { success: true, results: refreshedPodDesignDetails };

    };

    // *********** SAVE POD FUNCTION END **************
    // ************************************************





    // ********* UPDATE POD DESIGN PRODUCT ROLES *********

    // Function to map through all the parent + child products' roles, check if there's any updates and then make the call to update, collect responses and toast any errors
    const UpdatePodDesignProductRoles = async (designOrTemplate, typeId, podDesignDetails, originalPodDesignDetails) => {

        // Guard: if current products is missing, treat as empty array
        const currentProducts = Array.isArray(podDesignDetails?.products) ? podDesignDetails.products : [];

        // Guard: if original products is missing, treat as empty array
        const originalProducts = Array.isArray(originalPodDesignDetails?.products) ? originalPodDesignDetails.products : [];

        // Helper: stable stringify (so object key order doesn’t break equality checks)
        const stableStringify = (value) => {

            // If null or not an object, stringify directly
            if (value === null || typeof value !== "object") {

                // Return primitive JSON
                return JSON.stringify(value);

            }

            // If it's an array, stringify each item in order
            if (Array.isArray(value)) {

                // Return array JSON with stable stringified items
                return `[${value.map(stableStringify).join(",")}]`;

            }

            // Get keys sorted alphabetically for stability
            const keys = Object.keys(value).sort();

            // Build stable object JSON
            return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;

        };

        // Helper: normalize roles for comparison (sort + stable stringify)
        const normalizeRolesForCompare = (roles) => {

            // Ensure roles is always an array
            const safeRoles = Array.isArray(roles) ? roles : [];

            // Sort by role key
            const sortedRoles = [...safeRoles].sort((a, b) => {

                // Read role keys safely
                const ar = String(a?.role || "");

                // Read role keys safely
                const br = String(b?.role || "");

                // Sort ascending
                return ar.localeCompare(br);

            });

            // Return stable string
            return stableStringify(sortedRoles);

        };

        // Helper: find original parent product by configured product id
        const findOriginalProductById = (configuredProductId) => {

            // Find by configured product id
            return originalProducts.find((product) => String(product?.id || "") === String(configuredProductId || "")) || null;

        };

        // Helper: find original child by configured product id
        const findOriginalChildById = (configuredProductId) => {

            // Go through original parent products
            for (const originalParent of originalProducts) {

                // Read original children safely
                const originalChildren = Array.isArray(originalParent?.children) ? originalParent.children : [];

                // Find matching child
                const matchingChild = originalChildren.find((child) => String(child?.id || "") === String(configuredProductId || ""));

                // Return matching child
                if (matchingChild) { return matchingChild; }

            }

            // No matching child found
            return null;

        };

        // This converts only changed roles into a safe API payload
        const buildRolesPayloadFromChangedRolesArray = (changedRolesArray) => {

            // This ensures we always have an array
            const safeRoles = Array.isArray(changedRolesArray) ? changedRolesArray : [];

            // This maps each changed role object into the correct payload shape
            return safeRoles.map((roleItem) => {

                // This normalises edited into a string we can compare safely
                const isEdited = String(roleItem?.edited ?? "N") === "Y";

                // If this role was edited in the editor, send the full data
                if (isEdited) {

                    // This returns the full role payload for saving edited placement data
                    return {

                        // This sends the role identifier
                        role: roleItem?.role ?? null,

                        // This sends the active flag
                        active: roleItem?.active ?? "N",

                        // This sends background colour
                        background_colour: roleItem?.background_colour ?? null,

                        // This sends background edited flag
                        edited_background: roleItem?.edited_background ?? null,

                        // This sends centre values
                        centre_x: roleItem?.centre_x !== undefined && roleItem?.centre_x !== null ? String(roleItem.centre_x) : null,
                        centre_y: roleItem?.centre_y !== undefined && roleItem?.centre_y !== null ? String(roleItem.centre_y) : null,

                        // This sends scale values
                        scale_x: roleItem?.scale_x !== undefined && roleItem?.scale_x !== null ? String(roleItem.scale_x) : null,
                        scale_y: roleItem?.scale_y !== undefined && roleItem?.scale_y !== null ? String(roleItem.scale_y) : null,

                        // This sends rotation
                        rotation: roleItem?.rotation !== undefined && roleItem?.rotation !== null ? String(roleItem.rotation) : null,

                        // This sends the rotation of the product view used to establish this placement
                        initial_view_rotation: roleItem?.initial_view_rotation !== undefined && roleItem?.initial_view_rotation !== null
                            ? Number(roleItem.initial_view_rotation)
                            : 0,

                        // This sends fit/fill and position
                        fit_fill: roleItem?.fit_fill ?? null,
                        position: roleItem?.position ?? null,

                        // This sends view settings
                        view_settings: roleItem?.view_settings ?? {
                            aspects: ["S", "T", "W"],
                            rotations: [0, 90, 180, 270],
                        },

                        // This tells the backend to apply these changes
                        edited: "Y",

                    };

                }

                // This returns an active-only payload without clearing placement data
                return {

                    // This sends the role identifier
                    role: roleItem?.role ?? null,

                    // This sends the active flag only
                    active: roleItem?.active ?? "N",

                    // This preserves the rotation of the product view used to establish this placement
                    initial_view_rotation: roleItem?.initial_view_rotation !== undefined && roleItem?.initial_view_rotation !== null
                        ? Number(roleItem.initial_view_rotation)
                        : 0,

                    // This sends view settings
                    view_settings: roleItem?.view_settings ?? {
                        aspects: ["S", "T", "W"],
                        rotations: [0, 90, 180, 270],
                    },

                };

            });

        };

        // This flattens parents + children into one array of update candidates
        const currentRoleOwners = currentProducts.flatMap((product) => {

            // Read parent configured product id safely
            const parentConfiguredProductId = product?.id || null;

            // Read parent sku safely for debugging/context only
            const parentSku = product?.pod_product_sku || product?.product_sku || null;

            // If no configured product id, this product has not been saved yet, so skip the separate roles endpoint
            if (!parentConfiguredProductId) {

                // Return empty array
                return [];

            }

            // Build the parent item
            const parentItem = {

                // This identifies the owner type
                ownerType: "parent",

                // This is the configured product id that the API call should use
                configuredProductId: parentConfiguredProductId,

                // This is the sku for debugging/context only
                targetSku: parentSku,

                // This is the parent sku for lookup context
                parentSku: parentSku,

                // Parent has no child sku
                childSku: null,

                // These are the current roles
                roles: Array.isArray(product?.roles) ? product.roles : [],

                // Keep product for debugging if needed
                source: product,

            };

            // Read child array safely
            const safeChildren = Array.isArray(product?.children) ? product.children : [];

            // Build child items
            const childItems = safeChildren.map((child) => {

                // Read child configured product id safely
                const childConfiguredProductId = child?.id || null;

                // Read child sku safely for debugging/context only
                const safeChildSku = child?.pod_product_sku || child?.product_sku || child?.sku || null;

                // Return child owner entry
                return {

                    // This identifies the owner type
                    ownerType: "child",

                    // This is the configured product id that the API call should use
                    configuredProductId: childConfiguredProductId,

                    // This is the sku for debugging/context only
                    targetSku: safeChildSku,

                    // This is the parent sku for original lookup context
                    parentSku: parentSku,

                    // This is the child sku
                    childSku: safeChildSku,

                    // These are the child roles
                    roles: Array.isArray(child?.roles) ? child.roles : [],

                    // Keep child for debugging if needed
                    source: child,

                };

            }).filter((item) => item?.configuredProductId);

            // Return parent + children
            return [parentItem, ...childItems];

        });

        // Build a list of parent/child items whose roles changed
        const roleOwnersToUpdate = currentRoleOwners
                .map((item) => {

                    // Read configured product id safely
                    const configuredProductId = item?.configuredProductId || null;

                    // Skip if there is no configured product id
                    if (!configuredProductId) {

                        // Not updateable
                        return null;

                    }

                    // Read current roles safely
                    const currentRoles = Array.isArray(item?.roles) ? item.roles : [];

                    // Get the matching original roles depending on owner type
                    let originalRoles = [];

                    // If this is a parent product
                    if (item?.ownerType === "parent") {

                        // Find matching original parent
                        const originalProduct = findOriginalProductById(configuredProductId);

                        // Read original roles safely
                        originalRoles = Array.isArray(originalProduct?.roles) ? originalProduct.roles : [];

                    } else {

                        // Find matching original child using configured product id
                        const originalChild = findOriginalChildById(configuredProductId);

                        // Read original roles safely
                        originalRoles = Array.isArray(originalChild?.roles) ? originalChild.roles : [];

                    }

                    // Build changed roles only
                    const changedRoles = currentRoles.filter((currentRole) => {

                        // Read role key safely
                        const roleKey = String(currentRole?.role || "");

                        // Skip roles without a key
                        if (!roleKey) { return false; }

                        // Find matching original role
                        const originalRole = originalRoles.find((roleItem) => String(roleItem?.role || "") === roleKey) || null;

                        // If the role did not exist before, it changed
                        if (!originalRole) { return true; }

                        // Compare this single role only
                        return normalizeRolesForCompare([currentRole]) !== normalizeRolesForCompare([originalRole]);

                    });

                    // If no roles changed, skip this owner
                    if (changedRoles.length === 0) {

                        // Nothing to update
                        return null;

                    }

                    // Return this owner with the complete current role set
                    return {

                        // Keep existing item data
                        ...item,

                        // The API replaces every role row, so send every current role
                        changedRoles: currentRoles,

                    };

                })

                // Remove unchanged owners
                .filter((item) => item !== null);

            // If nothing changed, skip calls
            if (roleOwnersToUpdate.length === 0 && !isAddingNewDesign) {

                // Nothing to update
                return { success: true, results: [] };

            }

            // Store the product-role update responses in save order
            const accumulatedUpdateProductRolesResponse = [];

            // Save each product-role mutation separately so its API-side mockup refresh finishes before the next one starts
            for (const item of roleOwnersToUpdate) {

                // Read configured product id safely
                const configuredProductId = item?.configuredProductId || null;

                // This guarantees changedRolesArray is always an array of changed role objects
                const changedRolesArray = Array.isArray(item?.changedRoles) ? item.changedRoles : [];

                // This creates the payload from changed roles only
                const rolesPayload = buildRolesPayloadFromChangedRolesArray(changedRolesArray);

                // Make the call to update the roles and refresh the affected mockups in the same API request
                const updateProductsResponse = await updatePodDesignProductRoles(
                    token,
                    designOrTemplate,
                    typeId,
                    configuredProductId,
                    rolesPayload
                );

                // If it failed, log the error message
                if (!updateProductsResponse?.success) {

                    // Log error if present
                    console.error(
                        `updatePodDesignProductRoles failed for ${item?.ownerType} configured product ${configuredProductId}:`,
                        updateProductsResponse?.error_message || updateProductsResponse
                    );

                }

                // Add the completed response to the accumulated results
                accumulatedUpdateProductRolesResponse.push({

                    // Include owner type
                    ownerType: item?.ownerType,

                    // Include configured product id
                    configuredProductId: configuredProductId,

                    // Include target sku for debugging only
                    targetSku: item?.targetSku || null,

                    // Include api response
                    ...updateProductsResponse,

                });

            }

            // Find the first failure (if any)
            const firstFailure = accumulatedUpdateProductRolesResponse.find((response) => !response?.success) || null;

            // If all succeeded
            if (!firstFailure) {

                // Return success + responses
                return { success: true, results: accumulatedUpdateProductRolesResponse };

            }

            // Something failed so toast error and return early

            // Clear loading state
            setDesignIsSaving(false);

            // Toast fail message
            toastError({
                title: "Error",
                message: "Could not update POD design: " + (firstFailure?.error_message || "Unknown error"),
                autoCloseMs: 6000
            });

            // Return early
        return {
            success: false,
            error_message:
                firstFailure?.error_message ||
                "Unknown error",
            results:
                accumulatedUpdateProductRolesResponse,
        };

    };

    // Get the first available Strich store for new designs
    const defaultDesignStore = connectionsData?.find((connection) => connection.conntype === "STRICH") || null;

    // Resolve the store used by the current design or the default store for a new design
    const defaultDesignStoreId = originalPodDesignDetails?.store_id || defaultDesignStore?.id || "";

    // Get the marketplace auto-add preference for one Strich store
    const getStoreMarketplaceAutoadd = (storeId) => {

        // Find the requested store, falling back to the first Strich store
        const storeConnection =
            connectionsData?.find((connection) => connection.conntype === "STRICH" && String(connection.id) === String(storeId))
            || defaultDesignStore;

        // Find the maintained or overridden marketplace setting
        const marketplaceSetting = storeConnection?.settings?.find((setting) => setting.code === "store_marketplace_autoadd");

        // The maintained default is on unless this store explicitly says otherwise
        return String(marketplaceSetting?.value || "Y").toUpperCase() === "N" ? "N" : "Y";

    };

    // Memoize the initial form values to avoid recalculating unless details change
    const initialFormValues = useMemo(() => {

        // Return the initial form values
        return {

            // Spread all existing pod product details into the form state
            ...originalPodDesignDetails,

            // Add active "Y" value as default when adding new designs
            active: podDesignDetails?.active || "Y",

            // Designs keep their publication settings and template assignments
            ...(designOrTemplate === "design" ? {
                store_id: originalPodDesignDetails?.store_id || defaultDesignStoreId,
                marketplace: isAddingNewDesign ? getStoreMarketplaceAutoadd(defaultDesignStoreId) : (originalPodDesignDetails?.marketplace || "N"),
                store: originalPodDesignDetails?.store || "Y",
                template_ids: Array.isArray(originalPodDesignDetails?.template_ids) ? [...originalPodDesignDetails.template_ids] : []
            } : {}),

            // Set EN language translation by default if we're adding a new design
            translations: isAddingNewDesign
                ? [{ language: "EN" }]
                : (originalPodDesignDetails?.translations || [{ language: "EN" }])

        };

        // Rebuild the initial design state when its details or available stores change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originalPodDesignDetails, isAddingNewDesign, connectionsData]);

    
    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        setFormValues,
        handleChange,   // Function to update form values when inputs change
        handleSubmit,   // Function to handle form submit
        isFormPartlyEdited, // Check to see if form has been partially edited
        fieldErrors,    // Object storing validation errors for individual fields
        //formError,      // General error message (e.g. API errors)

    } = useFormHandler({

        // Default values for the form fields
        initialValues: initialFormValues,

        // Enable syncing - because we're using memo to remember the initial form values before the user edits them
        syncInitialValues: true,

        // API function to be called on form submission based on adding a new product or updating an existing one
        //apiCall: isAddingNewDesign ? addPodDesign : updatePodDesign,

        submitFunction: savePodDesign,

        // We want to convert boolean to string
        convertBooleans: true,

        // We want to convert number strings to numbers
        convertNumbers: true,

        // Validation function to check the form fields before submission
        validate: (values) => {

            // Set errors to empty
            const errors = {};

            // Validation rules
            const code = values?.code;

            // Check if code is missing OR if it's a string that is empty or just whitespace
            if ( designOrTemplate === "template" && ( code === undefined || code === null || (typeof code === "string" && !code.trim())) ) {

                // Set the validation error message
                errors.code = "*Code is required";

            }

            // Find the translation object for the chosen language
            const chosenTranslation = values?.translations?.find((translation) => translation?.language === chosenLanguage);

            // Safely get the title for the chosen language (could be undefined/null/string/etc)
            const chosenTitle = chosenTranslation?.title;

            // Check if title is missing OR if it's a string that is empty or just whitespace
            if (chosenTitle === undefined || chosenTitle === null ||(typeof chosenTitle === "string" && !chosenTitle.trim()) ) {

                // Set the validation error message
                errors.title = "*Title is required";

            }

            // Return validation errors (if any)
            return errors;

        },

    });

    // Change the selected store and apply its marketplace default to a new design
    const handleDesignStoreChange = (event) => {

        // Get the newly selected store id
        const storeId = event.target.value;

        // Apply the store change to the form
        handleChange(event);

        // Existing designs retain their current marketplace publication state
        if (!isAddingNewDesign || designOrTemplate !== "design") { return; }

        // Apply the new store's marketplace default to the unsaved design
        setFormValues((previousValues) => ({
            ...previousValues,
            marketplace: getStoreMarketplaceAutoadd(storeId),
        }));

    };



    // Compare the current form values with the latest saved values
    const formIsPartlyEdited = isFormPartlyEdited();

    // Keep the current template assignments in one safe array
    const selectedTemplateIds = Array.isArray(formValues?.template_ids) ? formValues.template_ids : [];

    // Template-driven designs keep their own products dormant and do not expose local product controls
    const designProductsManagedByTemplates =
        designOrTemplate === "design" &&
        selectedTemplateIds.length > 0;

    // Add one template assignment selected from the dropdown
    const handleAddTemplate = (event) => {

        // Read the selected public template id
        const templateId = Number(event.target.value);

        // Ignore the placeholder option
        if (!templateId) { return; }

        // Ignore a template which is already assigned
        if (selectedTemplateIds.some((currentTemplateId) => Number(currentTemplateId) === templateId)) { return; }

        // Append the selected template id
        const updatedTemplateIds = [...selectedTemplateIds, templateId];

        // Update the form data which will be sent to the API
        setFormValues((previousValues) => ({
            ...previousValues,
            template_ids: updatedTemplateIds
        }));

        // Keep the live design state in step with the unsaved template selection
        setPodDesignDetails((previousDetails) => ({
            ...previousDetails,
            template_ids: updatedTemplateIds
        }));

    };

    // Remove one template assignment from the design
    const handleRemoveTemplate = (templateId) => {

        // Remove only the selected template id
        const updatedTemplateIds = selectedTemplateIds.filter((currentTemplateId) => {
            return Number(currentTemplateId) !== Number(templateId);
        });

        // Update the form data which will be sent to the API
        setFormValues((previousValues) => ({
            ...previousValues,
            template_ids: updatedTemplateIds
        }));

        // Keep the live design state in step with the unsaved template selection
        setPodDesignDetails((previousDetails) => ({
            ...previousDetails,
            template_ids: updatedTemplateIds
        }));

    };

    // Get the current translations based on the chosen language
    const currentTranslation = formValues?.translations?.find((translations) => translations.language === chosenLanguage);

    // Set category and subcategory options for dropdowns
    const categoryOptions = Object.keys(existingCategories[chosenLanguage] || []);

    // Current category
    const category = currentTranslation?.category;



    // Handle form value changes which are derivded from the translations
    const handleTranslationChange = (e) => {

        // Get the name and value from the target input
        const { name, value } = e.target;

        // Update just the relevant translation inside formValues
        const updatedTranslations = formValues?.translations?.map((translations) =>

            // Where the language matches the choseLanguage
            translations.language === chosenLanguage ? { ...translations, [name]: value } : translations

        );

        // Set formValues from useFormHandle hook with updated translations
        setFormValues({

            ...formValues,
            translations: updatedTranslations,

        });

    };



    // Create keyword list array
    const keywordList = (

        // Check currect translation and keywords exist and trim them
        currentTranslation?.keywords?.trim()

            // They do, so get the currect translation keywords
            ? currentTranslation.keywords

                // Split each keyword/phrase by comma
                .split(',')

                // Map the keywords and trim any trailing spaces
                .map(keyword => keyword.trim())

                // Filter only real values (with a length greater than 0)
                .filter(keyword => keyword.length > 0)

            // Else create an empyty array
            : []

    );



    // Handle keywords changes (adding / removing)
    const handleKeywordsChange = (newKeywordsArray) => {

        // Join keywords into a comma-separated string, or empty string if cleared
        const keywordsString = newKeywordsArray.length > 0

            // Join each keyword/phrase with a comma and space
            ? newKeywordsArray.join(', ')

            // Need to save as space when removing all keywords
            : " ";

        // Map the translations so we update the chosen language
        const updatedTranslations = formValues?.translations?.map(translation =>

            // Where language matches matches chosen language
            translation.language === chosenLanguage

                // Update the keywords
                ? { ...translation, keywords: keywordsString }

                // Return the translation as normal if it doesn't match the current chosen language
                : translation
        );

        // Update the formValues state
        setFormValues({
            ...formValues,
            translations: updatedTranslations,
        });

    };



    // Store existing subcats that match selected category
    const subCategoryOptions = (

        existingCategories[chosenLanguage]?.[category || ''] || []

    ).sort((a, b) => a.localeCompare(b));

        

    // Function to add an image to the image bucket - should return an image id
    const addImageToBucket = async (payload) => {

        const imageBucketResponse = await addImageToImageBucket(payload);

        return imageBucketResponse;

    };



    // Add any new role images to the image bucket and build the role payload for the API
    const newRolesArray = async (rolesArray) => {

        // Read the supplied roles safely
        let safeRolesArray = [];

        if (Array.isArray(rolesArray)) {
            safeRolesArray = rolesArray;
        }

        // Keep only the latest row for each role key
        const rolesByKey = new Map();

        for (const roleItem of safeRolesArray) {

            // Read the role key safely
            const roleKey = String(roleItem?.role || "");

            // Ignore invalid role rows
            if (!roleKey) { continue; }

            // A later duplicate replaces the earlier row
            rolesByKey.set(roleKey, roleItem);

        }

        // Cache uploaded image ids during this save so the same image is not uploaded repeatedly
        const uploadedAssetIdsByImageData = new Map();

        // Build the final role rows in sequence
        const updatedRoles = [];

        for (const roleItem of rolesByKey.values()) {

            // Read the role key
            const roleKey = String(roleItem?.role || "");

            // Start without an image asset id
            let imageBucketId = null;

            // Default image-based roles to bucket assets
            let assetType = "I";

            // Preserve an existing asset type when supplied
            if (roleItem?.asset_type) {
                assetType = roleItem.asset_type;
            }

            // Reuse an existing saved asset id first
            if (roleItem?.asset_id) {

                imageBucketId = roleItem.asset_id;

            } else if (roleItem?.imageDetails?.imageBucketId) {

                // Reuse an image bucket id already held in the image details
                imageBucketId = roleItem.imageDetails.imageBucketId;
                assetType = "I";

            } else {

                // Read the image data which must be uploaded
                const imageData = roleItem?.uploadedImage;

                // Every selected role must resolve to a real asset
                if (!imageData) {

                    return {
                        success: false,
                        error_message: "Role " + roleKey + " has no image data.",
                        results: [],
                    };

                }

                // Reuse an image uploaded earlier in this same save
                if (uploadedAssetIdsByImageData.has(imageData)) {

                    imageBucketId = uploadedAssetIdsByImageData.get(imageData);

                } else {

                    // Build a useful default image name
                    let imageName = "POD " + designOrTemplate + " image";

                    // Prefer the supplied image name
                    if (roleItem?.imageDetails?.name) {
                        imageName = roleItem.imageDetails.name;
                    }

                    // Build the bucket upload payload
                    const newImagePayload = {
                        image_base64: imageData,
                        name: imageName,
                        type: "POD Design",
                        category: "",
                        subcategory: "",
                        system: "Y",
                    };

                    // Upload this image before processing the next role
                    const imageBucketResponse = await addImageToBucket(
                        newImagePayload
                    );

                    // Stop immediately when the image could not be stored
                    if (
                        !imageBucketResponse?.success ||
                        !imageBucketResponse?.results?.id
                    ) {

                        // Build the most useful available error
                        let errorMessage =
                            "Could not save the image for role " + roleKey + ".";

                        if (imageBucketResponse?.error_message) {
                            errorMessage =
                                imageBucketResponse.error_message;
                        }

                        return {
                            success: false,
                            error_message: errorMessage,
                            results: [],
                        };

                    }

                    // Store and cache the newly-created image id
                    imageBucketId = imageBucketResponse.results.id;
                    uploadedAssetIdsByImageData.set(
                        imageData,
                        imageBucketId
                    );

                }

            }

            // Add the completed role row
            updatedRoles.push({
                role: roleKey,
                asset_type: assetType,
                asset_id: imageBucketId,
                fit_fill: roleItem?.fit_fill,
                background_colour: roleItem?.background_colour,
            });

        }

        // Return a successful completed role payload
        return {
            success: true,
            results: updatedRoles,
        };

    };



    // Function to handle delete PodDesign
    const handleDeletePodDesign = async (designOrTemplate, id) => {

        // Provide confirmation dialog
        DisplayConfirmationDialog(

            `<h3>Are you sure?</h3> <br/> Delete POD ${designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}: ${currentTranslation.title} <br/><br/><span class='warning-message'>WARNING: This cannot be undone.</span>`,
            "Delete",
            "Cancel",

            async function (is_confirmed) {

                // If user confirms, proceed with deletion
                if (is_confirmed) {

                    setIsDeleting(true);

                    // Delete the design and get the response
                    const response = await deletePodDesign(token, designOrTemplate, id);

                    // Check if it was successful
                    if (response?.success) {

                        fetchPodDesigns(designOrTemplate);

                        onClose();

                        return;

                    }

                    // Allow another attempt after a failed deletion
                    setIsDeleting(false);

                    // Surface dependency errors and any other API failure
                    toastError({
                        title: "Error",
                        message: response?.error_message || `Could not delete ${designOrTemplate}.`,
                        autoCloseMs: 6000,
                    });

                }

            }

        );

    };  

        

    // Get the image_url for the basic design role
    const basicDesignImageUrl =
        podDesignDetails?.roles
            ?.find((r) => r?.role === basicDesignRoleKey)
            ?.image_url || "";

    // Get the image_url for the basic design role
    const basicDesignThumbnailUrl =
        podDesignDetails?.roles
            ?.find((r) => r?.role === basicDesignRoleKey)
            ?.thumbnail_url || "";

    // Use the role key as the basic role display name by default
    let basicDesignRoleDisplayName = basicDesignRoleKey || "image";

    // Find the configured basic role details
    const basicPrintAreaRole = printAreaRoles.find((roleItem) => {
        return String(roleItem?.role || "") === String(basicDesignRoleKey || "");
    });

    // Prefer the configured display name
    if (basicPrintAreaRole?.name) {
        basicDesignRoleDisplayName = basicPrintAreaRole.name;
    }

    // Build the basic role container class without an inline conditional operator
    let basicRoleContainerClassName = "pod-design__design-set-image-container";

    // Add the drag-over class while this exact role is the active file target
    if (dragOverRoleKey === basicDesignRoleKey) {
        basicRoleContainerClassName += " pod-design__design-set-image-container--drag-over";
    }



    // Check whether every existing role in podDesignDetails.roles is still selected (true) - Return false if ANY existing role in podDesignDetails.roles is not selected (true) in selectedRoles
    const everyRoleStillSelected  =

        // Check every existing role has a matching selected role that is true
        (podDesignDetails?.roles || []).every((roleItem) =>

        // Find a matching selected role entry
        (selectedRoles || []).some((selectedRoleItem) =>

            // Match on the role key
            selectedRoleItem?.role === roleItem?.role

            &&

            // Must be true/selected (supports either `true` or "Y")
            (selectedRoleItem?.selected === true || selectedRoleItem?.selected === "Y")

    ));

    // Return false if every role has a matching selected role (otherwise true) - Invert the result of everyRoleStillSelected
    const existingRoleHasBeenRemoved = !everyRoleStillSelected;

    // Check if the roles have been updated
    const podDetailsRolesIsSameAsOriginal = deepEqual(
        normaliseDesignRolesForEditedCheck(originalPodDesignDetails?.roles),
        normaliseDesignRolesForEditedCheck(podDesignDetails?.roles)
    );

    // Check whether anything still needs the normal design Save operation
    const designHasUnsavedChanges =
        isAddingNewDesign ||
        formIsPartlyEdited ||
        existingRoleHasBeenRemoved ||
        !podDetailsRolesIsSameAsOriginal ||
        productGridChangesPending;

    // Close the design editor, confirming only when genuine unsaved work remains
    const handleCloseDesign = () => {

        // Do not close while another blocking action is running
        if (designIsSaving || isDeleting || isLoading) { return; }

        // Close immediately when everything is already saved
        if (!designHasUnsavedChanges) {

            onClose();

            return;

        }

        // Confirm before throwing away only the remaining unsaved work
        DisplayConfirmationDialog(
            "<h3>Discard unsaved changes?</h3><br/>Changes that have already been saved automatically will be kept.<br/><br/>Any remaining unsaved changes will be discarded.",
            "Discard changes",
            "Keep editing",
            function (isConfirmed) {

                // Keep editing when cancelled
                if (!isConfirmed) { return; }

                // Close without saving the remaining changes
                onClose();

            }
        );

    };

    // Save pending design-role changes before opening one configured product
    const prepareDesignForProductOpen = async (configuredProductId) => {

        // No save is required when the design role data is unchanged
        if (
            podDetailsRolesIsSameAsOriginal &&
            !existingRoleHasBeenRemoved &&
            !productGridChangesPending
        ) {
            return true;
        }

        // Do not start another save while one is already running
        if (designIsSaving) {
            return false;
        }

        // Wait for the user's decision and the resulting save
        return await new Promise((resolve) => {

            DisplayConfirmationDialog(
                "<h3>Save design changes?</h3>" +
                "<br/>The design or product settings have changed and must be saved before changing products.",
                "Save and continue",
                "Cancel",
                async function (isConfirmed) {

                    // Keep the current design open when the user cancels
                    if (!isConfirmed) {
                        resolve(false);
                        return;
                    }

                    // Save the latest role data and prioritise this product
                    const saveResponse = await handleSubmit(
                        {
                            preventDefault: () => {},
                        },
                        {
                            type: designOrTemplate,
                            podDesignDetails: podDesignDetails,
                            selectedRoles: selectedRoles,
                            priorityConfiguredProductId:
                                configuredProductId,
                            productGridChangesPending:
                                productGridChangesPending,
                        }
                    );

                    // Open only after the complete save succeeds
                    resolve(Boolean(saveResponse?.success));

                }
            );

        });

    };


    // Add image data to roles array in podDesignDetails - This upserts the role synchronously using already-known dims
    const upsertImageToRoleInPodDesignDetailsSync = (prevDetails, roleKey, payload, dims) => {

        // Fallback so we never work with undefined/null
        const safePrevDetails = prevDetails ?? {};

        // Ensure roles is always an array
        const safeRoles = Array.isArray(safePrevDetails?.roles) ? safePrevDetails.roles : [];

        // Get image url safely
        const imageUrl = payload?.imageDetails?.image_url || null;

        // Check if we have uploaded image data
        const hasUploadedImage = !!payload?.uploadedImage;

        // If there's no payload data, treat it as a reset and remove the role completely
        if (!imageUrl && !hasUploadedImage) {

            // Return updated state with the role removed
            return {

                // Keep all existing details
                ...safePrevDetails,

                // Remove the role from the roles array
                roles: safeRoles.filter((roleItem) => roleItem?.role !== roleKey),

            };

        }

        // Check if this role already exists
        const roleExists = safeRoles.some((roleItem) => roleItem?.role === roleKey);

        // Get the existing role so image replacement can preserve role settings
        const existingRole = safeRoles.find((roleItem) => roleItem?.role === roleKey) || null;

        // Build the updated role object
        const updatedRole = {

            // Set the role key
            role: roleKey,

            // Set the image data so we can use it in the mockup builder in products section
            image_url: imageUrl,
            thumbnail_url: imageUrl,

            // Keep the actual stored image type with the role
            file_type: payload?.imageDetails?.file_type || null,

            // Keep the stored image metadata with the role
            metadata: payload?.imageDetails?.metadata || null,

            centre_x: "0.50000",
            centre_y: "0.50000",
            scale_x: "1.00000",
            scale_y: "1.00000",
            rotation: "0",

            // Preserve the existing fit or fill setting when replacing the image
            fit_fill: existingRole?.fit_fill ? existingRole.fit_fill : "fit",

            // Store image details
            imageDetails: payload?.imageDetails,

            // Store uploaded image data
            uploadedImage: payload?.uploadedImage,

            // Clear the previous saved asset unless the replacement payload supplies one
            asset_type: payload?.asset_type || null,
            asset_id: payload?.asset_id || null,

            // Clear other saved image references so the old image cannot win after replacement
            image_id: payload?.image_id || null,
            print_asset_id: payload?.print_asset_id || null,

            // Store selected background colour (payload?.background_colour)
            background_colour: payload.background_colour ?? null,

            // Add image width and height from the passed dims
            design_width: dims?.design_width ?? null,

            // Add image height from the passed dims
            design_height: dims?.design_height ?? null,

            // Set a preview_mode (used for templates - generic design images)
            preview_mode: payload?.previewMode ?? null,

            // Set a suppress_product_preview (used for templates - generic design images)
            suppress_product_preview: payload?.suppressProductPreview ?? false,


        };

        // Return updated state
        return {

            // Keep all existing details (safe now)
            ...safePrevDetails,

            // Update roles array immutably
            roles: roleExists

                // If it exists, map and replace only the matching role
                ? safeRoles.map((roleItem) => {

                    // If this is the matching role, replace it
                    if (roleItem?.role === roleKey) {

                        // Return merged role (keep existing fields, overwrite updated)
                        return {

                            // Keep existing role fields
                            ...roleItem,

                            // Overwrite with latest payload data
                            ...updatedRole,

                        };

                    }

                    // Otherwise return unchanged
                    return roleItem;

                })

                // If it doesn't exist, append it
                : [...safeRoles, updatedRole],

        };

    };



    // Make sure a new design has a usable title before its automatic first save
    const ensureNewDesignHasTitle = (previousValues) => {

        // Use a safe form object
        const safeValues = previousValues || {};

        // Use a safe translations array
        const safeTranslations = Array.isArray(safeValues.translations) ? safeValues.translations : [];

        // Track whether the selected language already exists
        let selectedTranslationExists = false;

        // Add the fallback title only when the selected translation has no title
        const nextTranslations = safeTranslations.map((translation) => {

            // Keep translations for other languages unchanged
            if (translation?.language !== chosenLanguage) { return translation; }

            // Remember that the selected language exists
            selectedTranslationExists = true;

            // Keep an existing non-empty title
            if (String(translation?.title || "").trim() !== "") { return translation; }

            // Add the fallback title required by the create endpoint
            return {
                ...translation,
                title: "Untitled",
            };

        });

        // Add the selected language when it does not exist yet
        if (!selectedTranslationExists) {
            nextTranslations.push({
                language: chosenLanguage,
                title: "Untitled",
            });
        }

        // Return the complete form state with the guaranteed title
        return {
            ...safeValues,
            translations: nextTranslations,
        };

    };



    // This handles the async work, then updates state safely
    const handleUpsertRoleImage = async (roleKey, payload) => {

        // Get image url safely
        const imageUrl = payload?.imageDetails?.image_url || null;

        // Fetch dimensions (async)
        const dims = await getImageDimensionsFromUrl(imageUrl);

        // Set state synchronously (no Promise stored)
        setPodDesignDetails((prev) =>

            // Return next state (SYNC)
            upsertImageToRoleInPodDesignDetailsSync(prev, roleKey, payload, dims)

        );

        // Automatically create a new design after its first image is added
        if (isAddingNewDesign && designOrTemplate === "design") {

            // Add the fallback title without replacing a title the user or AI has already supplied
            setFormValues((previousValues) => ensureNewDesignHasTitle(previousValues));

            // Request the save after the form and role state updates have rendered
            setNewDesignAutoSaveRequested(true);

        }

    };

    // This opens the upscale popup for the chosen role image
    const handleOpenUpscalePopup = async (roleKey, roleData) => {

        // Do not start another workflow while the design is already being saved
        if (designIsSaving) { return; }

        // Start with the current role data
        let upscaleRoleData = roleData;

        // Read temporary image sources safely
        const uploadedImageSource = String(upscaleRoleData?.uploadedImage || "").trim();
        const previewImageSource = String(upscaleRoleData?.image_url || "").trim();

        // Temporary browser or inline image data must be saved before the queue can use it
        const sourceNeedsSaving =
            uploadedImageSource.startsWith("data:image/") ||
            previewImageSource.startsWith("blob:");

        // Save temporary image data before opening the upscale workflow
        if (sourceNeedsSaving) {

            // Prevent the new-design automatic save from running the same save as well
            if (isAddingNewDesign) {
                setNewDesignAutoSaveRequested(false);
            }

            // Run the normal complete design save
            const saveResponse = await handleSubmit(
                {
                    preventDefault: () => {},
                },
                {
                    type: designOrTemplate,
                    podDesignDetails: podDesignDetails,
                    selectedRoles: selectedRoles,
                    productGridChangesPending: productGridChangesPending,
                }
            );

            // Stop if the image could not be saved
            if (!saveResponse?.success) { return; }

            // Read the freshly-saved roles
            const refreshedRoles = Array.isArray(saveResponse?.results?.roles)
                ? saveResponse.results.roles
                : [];

            // Find the freshly-saved version of this role
            upscaleRoleData = refreshedRoles.find((roleItem) => {
                return String(roleItem?.role || "") === String(roleKey || "");
            }) || null;

            // Stop if the saved role could not be reloaded
            if (!upscaleRoleData) {

                toastError({
                    title: "Error",
                    message: "Could not reload the saved design image for upscaling.",
                    autoCloseMs: 6000,
                });

                return;

            }

        }

        // Prefer the full stored bucket image, then local image data, then the normal role URL
        const sourceImage =
            upscaleRoleData?.imageDetails?.imageBucketUrl ||
            upscaleRoleData?.uploadedImage ||
            upscaleRoleData?.image_url;

        // Read the stored image type directly from the role
        const sourceFileType = String(upscaleRoleData?.file_type || "").toLowerCase();

        // Read the original dimensions directly from the stored image metadata
        const sourceWidth = Number(upscaleRoleData?.metadata?.width_px);
        const sourceHeight = Number(upscaleRoleData?.metadata?.height_px);

        // Stop if there is no source image to upscale
        if (!sourceImage) { return; }

        // Stop rather than guessing when the image type is unavailable
        if (sourceFileType !== "jpg" && sourceFileType !== "png") {

            toastError({
                title: "Error",
                message: "Could not determine whether this design image is JPG or PNG.",
                autoCloseMs: 6000,
            });

            return;

        }

        // Stop if the stored image dimensions are unavailable
        if (
            !Number.isFinite(sourceWidth) ||
            !Number.isFinite(sourceHeight) ||
            sourceWidth <= 0 ||
            sourceHeight <= 0
        ) {

            toastError({
                title: "Error",
                message: "Could not read the stored dimensions for this design image.",
                autoCloseMs: 6000,
            });

            return;

        }

        // Store the exact source data needed by the upscale popup
        setSubmittedUpscaledImage({
            role: roleKey,
            sourceImage,
            sourceFileType,
            sourceWidth,
            sourceHeight,
        });

        // Set the loading state
        setIsSubmittingUpscaledImage(true);

        // Open the popup
        setShowPodDesignUpscalePage(true);

    };
    
    // Apply image/title data supplied when a new design was opened from another portal page
    useEffect(() => {

        // Only apply the incoming data once
        if (initialDesignDataApplied) { return; }

        // This shortcut only applies to new POD designs
        if (!isAddingNewDesign || designOrTemplate !== "design") { return; }

        // Wait until the first available print role has been resolved
        if (!basicDesignRoleKey) { return; }

        // Stop when no usable incoming image was supplied
        if (!initialDesignData?.image_url) { return; }

        // Prevent the data being applied again while the image is processed
        setInitialDesignDataApplied(true);

        // Build a useful title from the source page
        const initialTitle = String(initialDesignData?.title || "").trim() || "Untitled";

        // Set the supplied title on the English design translation
        setFormValues((previousValues) => {

            // Keep the current form state safely
            const safeValues = previousValues || {};

            // Keep existing translations safely
            const safeTranslations = Array.isArray(safeValues.translations)
                ? safeValues.translations
                : [];

            // Track whether the current language already exists
            let translationExists = false;

            // Update the existing translation where possible
            const updatedTranslations = safeTranslations.map((translation) => {

                // Leave other languages unchanged
                if (translation?.language !== chosenLanguage) {

                    return translation;

                }

                // Remember that this translation exists
                translationExists = true;

                // Apply the supplied design title
                return {

                    ...translation,
                    title: initialTitle,

                };

            });

            // Add the selected language if it was not already present
            if (!translationExists) {

                updatedTranslations.push({

                    language: chosenLanguage,
                    title: initialTitle,

                });

            }

            // Return the complete updated form
            return {

                ...safeValues,
                translations: updatedTranslations,

            };

        });

        // Add the supplied image to the first available design role
        handleUpsertRoleImage(

            basicDesignRoleKey,

            {

                // Use the remote image immediately in the editor
                imageDetails: {

                    image_url: initialDesignData.image_url,
                    file_type: initialDesignData.file_type || null,
                    metadata: initialDesignData.metadata || null,
                    name: initialTitle,

                },

                // Keep the source available to the normal save path so it can be added to the bucket
                uploadedImage: initialDesignData.image_url,

            }

        );

    // handleUpsertRoleImage is recreated during render; the applied flag prevents this one-off action repeating
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialDesignDataApplied,
        initialDesignData,
        isAddingNewDesign,
        designOrTemplate,
        basicDesignRoleKey,
        chosenLanguage,
        setFormValues,
    ]);



    // Confirm deletion of one role and remove product families which would become blank
    const handleDeleteRoleImage = (
        role,
        deselectPrintableArea = false
    ) => {

        // Read the role key safely
        const roleKey = String(role?.role || "").trim();

        // Stop when the role key is missing
        if (roleKey === "") { return; }

        // Start with an empty current role array
        let currentDesignRoles = [];

        // Use the current design roles when they are valid
        if (Array.isArray(podDesignDetails?.roles)) {
            currentDesignRoles = podDesignDetails.roles;
        }

        // Build the role array after this image is removed
        const nextDesignRoles = currentDesignRoles.filter((roleItem) => {
            return String(roleItem?.role || "").trim() !== roleKey;
        });

        // Start with an empty configured product array
        let configuredProductFamilies = [];

        // Use the current configured product families when they are valid
        if (Array.isArray(podDesignDetails?.products)) {
            configuredProductFamilies = podDesignDetails.products;
        }

        // Start with no affected product families
        let affectedProductFamilies = [];

        // Only inspect local product compatibility while this design owns its product configuration
        if (!designProductsManagedByTemplates) {

            // Find the roles which are usable before deleting this image
            const currentUsableDesignRoleKeys = getUsableDesignRoleKeys(
                currentDesignRoles,
                selectedRoles
            );

            // Find the roles which will remain usable after deleting this image
            const nextUsableDesignRoleKeys = getUsableDesignRoleKeys(
                nextDesignRoles,
                selectedRoles
            );

            // Find product families which are printable now but would become completely blank
            affectedProductFamilies = configuredProductFamilies.filter((configuredProduct) => {

                // Ignore families which are already invalid before this deletion
                if (!configuredProductFamilyHasUsableRole(
                    configuredProduct,
                    currentUsableDesignRoleKeys
                )) {
                    return false;
                }

                // Keep only families which have no usable parent or child after the image is removed
                return !configuredProductFamilyHasUsableRole(
                    configuredProduct,
                    nextUsableDesignRoleKeys
                );

            });

        }

        // Use the role key as the fallback display name
        let roleDisplayName = roleKey;

        // Find the configured display name for this print area
        const printAreaRole = printAreaRoles.find((roleItem) => {
            return String(roleItem?.role || "").trim() === roleKey;
        });

        // Prefer the configured display name
        if (printAreaRole?.name) {
            roleDisplayName = String(printAreaRole.name);
        }

        // Start with the normal image-deletion confirmation
        let confirmationMessage =
            "<h3>Delete the " + roleDisplayName + " image?</h3>";

        // Use the normal delete button title by default
        let confirmationButtonTitle = "Delete";

        // Describe complete printable-area removal when called by the selector
        let productRemovalReason =
            "this print area image is deleted";

        if (deselectPrintableArea) {

            confirmationMessage =
                "<h3>Remove the " +
                roleDisplayName +
                " print area?</h3>" +
                "<br/>The image and design data for this print area will be deleted.";

            confirmationButtonTitle = "Remove";

            productRemovalReason =
                "this print area is removed";

        }

        // Add the singular product-removal warning
        if (affectedProductFamilies.length === 1) {

            confirmationMessage +=
                "<br/>1 product will be removed if " +
                productRemovalReason +
                ".";

        }

        // Add the plural product-removal warning
        if (affectedProductFamilies.length > 1) {

            confirmationMessage +=
                "<br/>" +
                String(affectedProductFamilies.length) +
                " products will be removed if " +
                productRemovalReason +
                ".";

        }

        // Display confirmation before changing local state
        DisplayConfirmationDialog(
            confirmationMessage,
            confirmationButtonTitle,
            "Cancel",
            function (isConfirmed) {

                // Stop when the user cancels
                if (!isConfirmed) { return; }

                // Queue saved affected product families for deletion during the normal Save process
                setPendingBlankProductDeletions((previousPendingDeletions) => {

                    // Start with an empty pending deletion list
                    let safePendingDeletions = [];

                    // Use the existing pending list when it is valid
                    if (Array.isArray(previousPendingDeletions)) {
                        safePendingDeletions = previousPendingDeletions;
                    }

                    // Copy the pending list before adding newly affected products
                    const nextPendingDeletions = [...safePendingDeletions];

                    // Add each affected saved product family once
                    affectedProductFamilies.forEach((configuredProduct) => {

                        // Unsaved product families only need removing from local state
                        if (!configuredProduct?.id) { return; }

                        // Check whether this saved family is already queued
                        const alreadyQueued = nextPendingDeletions.some((pendingProduct) => {
                            return String(pendingProduct?.id || "") === String(configuredProduct.id);
                        });

                        // Do not add duplicate deletion rows
                        if (alreadyQueued) { return; }

                        // Queue the minimum identity needed by the save path
                        nextPendingDeletions.push({
                            id: configuredProduct.id,
                            pod_product_sku: configuredProduct.pod_product_sku,
                        });

                    });

                    // Return the updated pending deletion list
                    return nextPendingDeletions;

                });

                // Remove the role image and newly blank product families from local state
                setPodDesignDetails((previousDetails) => {

                    // Start with an empty latest role array
                    let previousRoles = [];

                    // Use the latest role data when it is valid
                    if (Array.isArray(previousDetails?.roles)) {
                        previousRoles = previousDetails.roles;
                    }

                    // Remove this role while leaving its selected editor position available
                    const updatedRoles = previousRoles.filter((roleItem) => {
                        return String(roleItem?.role || "").trim() !== roleKey;
                    });

                    // Start with an empty latest product array
                    let previousProducts = [];

                    // Use the latest configured product families when they are valid
                    if (Array.isArray(previousDetails?.products)) {
                        previousProducts = previousDetails.products;
                    }

                    // Keep dormant local products unchanged while a template controls the design
                    let updatedProducts = previousProducts;

                    // Remove product families only while this design owns its product configuration
                    if (!designProductsManagedByTemplates) {

                        // Filter the confirmed affected families out of the local product state
                        updatedProducts = previousProducts.filter((configuredProduct) => {

                            // Check whether this is one of the families affected by the image deletion
                            const productWasAffected = affectedProductFamilies.some((affectedProduct) => {

                                // Match the same local product object
                                if (affectedProduct === configuredProduct) { return true; }

                                // Match saved configured products by their configured product id
                                if (affectedProduct?.id && configuredProduct?.id) {
                                    return String(affectedProduct.id) === String(configuredProduct.id);
                                }

                                // Match unsaved configured products by their temporary portal id
                                if (
                                    affectedProduct?.client_configured_product_id &&
                                    configuredProduct?.client_configured_product_id
                                ) {
                                    return String(affectedProduct.client_configured_product_id) ===
                                        String(configuredProduct.client_configured_product_id);
                                }

                                // This is not the affected product family
                                return false;

                            });

                            // Keep product families which were not included in the warning
                            return !productWasAffected;

                        });

                    }

                    // Start with the existing print-quality summary
                    let updatedPrintQualitySummary = previousDetails?.print_quality_summary;

                    // Remove stale quality information for the deleted print area
                    if (Array.isArray(previousDetails?.print_quality_summary)) {

                        updatedPrintQualitySummary = previousDetails.print_quality_summary.filter((summaryItem) => {

                            // Keep quality information belonging to other print areas
                            return String(summaryItem?.role || "").trim() !== roleKey;

                        });

                    }

                    // Return the updated local design state
                    return {
                        ...previousDetails,
                        roles: updatedRoles,
                        products: updatedProducts,
                        print_quality_summary: updatedPrintQualitySummary,
                    };

                });

                // Remove the printable area from the selector when requested
                if (deselectPrintableArea) {

                    setSelectedRoles((previousRoles) => {

                        // Safely read the latest selector state
                        let safePreviousRoles = [];

                        if (Array.isArray(previousRoles)) {
                            safePreviousRoles = previousRoles;
                        }

                        // Deselect only the role which has just been removed
                        return safePreviousRoles.map((selectedRole) => {

                            if (selectedRole.role === roleKey) {

                                return {
                                    ...selectedRole,
                                    selected: false,
                                };

                            }

                            return selectedRole;

                        });

                    });

                }

            }
        );

    };



    // Automatically save a new design after its first image and fallback title are in state
    useEffect(() => {

        // Stop when no automatic save has been requested
        if (!newDesignAutoSaveRequested) { return; }

        // Clear an obsolete request if this is no longer a new design
        if (!isAddingNewDesign || designOrTemplate !== "design") {
            setNewDesignAutoSaveRequested(false);
            return;
        }

        // Wait for any current save to finish
        if (designIsSaving) { return; }

        // Check whether at least one design role now has image data
        const designHasImage = (Array.isArray(podDesignDetails?.roles) ? podDesignDetails.roles : []).some((roleItem) => {
            return Boolean(roleItem?.imageDetails?.image_url || roleItem?.uploadedImage || roleItem?.image_url);
        });

        // Wait until the role state contains the newly added image
        if (!designHasImage) { return; }

        // Find the selected translation
        const selectedTranslation = (Array.isArray(formValues?.translations) ? formValues.translations : []).find((translation) => {
            return translation?.language === chosenLanguage;
        });

        // Wait until the required title is present in form state
        if (String(selectedTranslation?.title || "").trim() === "") { return; }

        // Clear the request before starting the asynchronous save
        setNewDesignAutoSaveRequested(false);

        // Run the normal complete design save using the now-current form and role state
        handleSubmit(
            {
                preventDefault: () => {},
            },
            {
                type: designOrTemplate,
                podDesignDetails: podDesignDetails,
            }
        );

    }, [
        newDesignAutoSaveRequested,
        isAddingNewDesign,
        designOrTemplate,
        designIsSaving,
        podDesignDetails,
        formValues,
        chosenLanguage,
        handleSubmit,
    ]);



    // Run useEffect to update role image data whenever the submitted Ai design changes
    useEffect(() => {

        // If there's no data return early
        if (!submittedAiDesign || !submittedAiDesign.role || !submittedAiDesign.payload) return;

        // Upsert the image to podDesignDetails
        handleUpsertRoleImage(submittedAiDesign.role, submittedAiDesign.payload);

        // Update the matched translation with the AI-generated design information
        const updatedTranslations = formValues.translations.map((translation) => {

            // Leave other languages unchanged
            if (translation.language !== chosenLanguage) { return translation; }

            // Get the existing design title without surrounding whitespace
            const existingTitle = String(translation.title || "").trim();

            // Only use the AI prompt as the title when the design has not already been named
            const shouldUseAiTitle =
                existingTitle === "" ||
                existingTitle.toLowerCase() === "untitled";

            // Return the updated translation
            return {
                ...translation,
                title: shouldUseAiTitle
                    ? submittedAiDesign?.payload?.title || translation.title
                    : translation.title,
                description: submittedAiDesign?.payload?.description || "",
                keywords: submittedAiDesign?.payload?.keywords || "",
            };

        });

        // Update the form with the AI-generated information
        setFormValues({
            ...formValues,
            translations: updatedTranslations,
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submittedAiDesign]);

    // Run useEffect to update role image data whenever the submitted upscaled image changes
    useEffect(() => {

        // Stop until the popup has returned a completed payload
        if (!submittedUpscaledImage || !submittedUpscaledImage.role || !submittedUpscaledImage.payload) return;

        // Track whether this effect has been cleaned up
        let isCancelled = false;

        // Apply the returned image
        const applyUpscaledImage = async () => {

            // Upsert the image to the chosen role
            await handleUpsertRoleImage(submittedUpscaledImage.role, submittedUpscaledImage.payload);

            // Stop if the effect has already cleaned up
            if (isCancelled) { return; }

            // Clear the temporary upscale state
            setSubmittedUpscaledImage(null);

            // Clear the loading flag
            setIsSubmittingUpscaledImage(false);

        };

        // Run the async image apply step
        applyUpscaledImage();

        // Prevent state updates after unmount
        return () => {
            isCancelled = true;
        };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submittedUpscaledImage]);


    // ***** DESIGN SET IMAGE DRAG HANDLING BETWEEN ROLES *****


    // This checks whether a role currently has an image
    const RoleHasImage = (roleData) => {

        // Return true when the role has either a main image or thumbnail
        return Boolean(roleData?.image_url || roleData?.thumbnail_url);

    };


    
    // This safely removes the role key from a role object
    const RemoveRoleKey = (roleData = {}) => {

        // Destructure the role key away from the rest of the object
        const { role, ...restOfRoleData } = roleData;

        // Return everything except the role key
        return restOfRoleData;

    };



    // This upserts a full role object into the roles array
    const UpsertFullRoleInArray = ({ rolesArray, nextRoleData }) => {

        // Find the index for the matching role
        const existingIndex = rolesArray.findIndex((item) => String(item?.role ?? "") === String(nextRoleData?.role ?? ""));

        // If the role already exists then replace it
        if (existingIndex > -1) {

            // Clone the roles array
            const nextRolesArray = [...rolesArray];

            // Replace the existing role
            nextRolesArray[existingIndex] = nextRoleData;

            // Return the updated array
            return nextRolesArray;

        }

        // Otherwise append the new role object
        return [
            ...rolesArray,
            nextRoleData,
        ];

    };



    // This returns whether drag and drop between roles should be enabled
    const canDragBetweenAdvancedRoles = Boolean(
        isAddingAdvancedDesignSet &&
        selectedRoles?.filter((item) => item?.selected)?.length > 1
    );



    // This handles duplicating or swapping full role data between roles
    const HandleRoleImageDrop = useCallback((sourceRoleKey, targetRoleKey) => {

        // Ignore invalid drops
        if (!sourceRoleKey || !targetRoleKey) return;

        // Ignore drops onto the same role
        if (String(sourceRoleKey) === String(targetRoleKey)) return;

        // Only allow this in advanced mode with multiple selected roles
        if (!canDragBetweenAdvancedRoles) return;

        // Update the design details from previous state
        setPodDesignDetails((prev) => {

            // Get the current roles array safely
            const currentRoles = Array.isArray(prev?.roles) ? prev.roles : [];

            // Find the source role data
            const sourceRoleData = currentRoles.find((item) => String(item?.role ?? "") === String(sourceRoleKey));

            // Find the target role data
            const targetRoleData = currentRoles.find((item) => String(item?.role ?? "") === String(targetRoleKey));

            // If the source does not exist or has no image, do nothing
            if (!sourceRoleData || !RoleHasImage(sourceRoleData)) {

                // Return unchanged state
                return prev;

            }

            // Check whether the target already has an image
            const targetHasImage = RoleHasImage(targetRoleData);

            // Start from the current roles array
            let nextRoles = [...currentRoles];

            // If target has no image, duplicate the full source role into the target role
            if (!targetHasImage) {

                // Build a full duplicate of the source role but assign the target role key
                const duplicatedTargetRole = {
                    ...sourceRoleData,
                    role: targetRoleKey,
                };

                // Upsert the duplicated role into the array
                nextRoles = UpsertFullRoleInArray({
                    rolesArray: nextRoles,
                    nextRoleData: duplicatedTargetRole,
                });

                // Return updated state
                return {
                    ...prev,
                    roles: nextRoles,
                };

            }

            // If target has an image, swap the full payloads while keeping each role key in place
            const nextSourceRoleData = {
                ...RemoveRoleKey(targetRoleData),
                role: sourceRoleKey,
            };

            // Build the next target role using the source payload but target role key
            const nextTargetRoleData = {
                ...RemoveRoleKey(sourceRoleData),
                role: targetRoleKey,
            };

            // Upsert the source role
            nextRoles = UpsertFullRoleInArray({
                rolesArray: nextRoles,
                nextRoleData: nextSourceRoleData,
            });

            // Upsert the target role
            nextRoles = UpsertFullRoleInArray({
                rolesArray: nextRoles,
                nextRoleData: nextTargetRoleData,
            });

            // Return updated state
            return {
                ...prev,
                roles: nextRoles,
            };

        });

        // Clear drag state after drop
        setDraggedRoleKey(null);

        // Clear drag over state after drop
        setDragOverRoleKey(null);

    }, [
        canDragBetweenAdvancedRoles,
        setPodDesignDetails,
    ]);



    // This checks whether a role currently has any usable image data
    const RoleHasAnyImageData = (roleData) => {

        // Return true when a role has a stored url, thumbnail, uploaded base64 or imageDetails url
        return Boolean(
            roleData?.image_url ||
            roleData?.thumbnail_url ||
            roleData?.uploadedImage ||
            roleData?.imageDetails?.image_url
        );

    };



    // This checks whether the dragged event contains at least one file
    const DragEventHasFiles = (event) => {

        // Read dataTransfer safely
        const dataTransfer = event?.dataTransfer;

        // Return false if missing
        if (!dataTransfer) return false;

        // Some browsers expose types
        const dragTypes = Array.from(dataTransfer?.types || []);

        // Return true if files are present
        return dragTypes.includes("Files");

    };



    // This checks whether a file looks like an image
    const IsImageFile = (file) => {

        // Read mime type safely
        const mimeType = String(file?.type || "").toLowerCase();

        // Accept image mime types only
        return mimeType.startsWith("image/");

    };



    // This returns the next empty role key in the correct priority order
    const GetNextEmptyRoleKeyForDroppedImage = () => {

        // Safely read current design roles
        const currentRoleData = Array.isArray(podDesignDetails?.roles) ? podDesignDetails.roles : [];

        // Create quick lookup helper
        const getExistingRoleData = (roleKey) => currentRoleData.find((item) => String(item?.role ?? "") === String(roleKey ?? ""));

        // ADVANCED MODE: use selected roles in their displayed order
        if (isAddingAdvancedDesignSet) {

            // Build the selected role keys in order
            const selectedRoleKeysInOrder = (Array.isArray(selectedRoles) ? selectedRoles : [])
                .filter((item) => Boolean(item?.selected))
                .map((item) => item?.role)
                .filter(Boolean);

            // Find the first selected role that has no image data
            const firstEmptySelectedRole = selectedRoleKeysInOrder.find((roleKey) => {

                // Find current role data
                const existingRoleData = getExistingRoleData(roleKey);

                // Keep if empty
                return !RoleHasAnyImageData(existingRoleData);

            });

            // Return the first empty selected role or null
            return firstEmptySelectedRole || null;

        }

        // BASIC MODE: only use the basic role if it is empty
        if (!basicDesignRoleKey) {

            // No usable basic role
            return null;

        }

        // Find current basic role data
        const existingBasicRoleData = getExistingRoleData(basicDesignRoleKey);

        // If basic role already has an image, do not overwrite it
        if (RoleHasAnyImageData(existingBasicRoleData)) {

            // No empty role available
            return null;

        }

        // Return the basic role key
        return basicDesignRoleKey;

    };

    // Add one or more bucket images starting with the print area whose Browse button was used.
    const HandleBucketImagesForRole = async (startingRoleKey, payloads) => {

        // Keep the existing single-image behaviour
        if (!Array.isArray(payloads)) {

            await handleUpsertRoleImage(startingRoleKey, payloads);
            return;

        }

        // Stop when the picker returned no images
        if (payloads.length === 0) return;

        // Safely read the current design roles
        const currentRoleData = Array.isArray(podDesignDetails?.roles) ? podDesignDetails.roles : [];

        // Start with the exact print area whose Browse button was clicked
        const targetRoleKeys = [startingRoleKey];

        // Build the remaining selected print areas in their displayed order
        const selectedRoleKeysInOrder = (Array.isArray(selectedRoles) ? selectedRoles : [])
            .filter((item) => Boolean(item?.selected))
            .map((item) => item?.role)
            .filter(Boolean);

        // Add every other currently empty print area
        selectedRoleKeysInOrder.forEach((roleKey) => {

            // The starting role is already first
            if (String(roleKey) === String(startingRoleKey)) return;

            // Find any existing image data for this role
            const existingRoleData = currentRoleData.find((roleItem) => {
                return String(roleItem?.role ?? "") === String(roleKey);
            });

            // Do not overwrite an occupied print area
            if (RoleHasAnyImageData(existingRoleData)) return;

            targetRoleKeys.push(roleKey);

        });

        // Only use as many selected images as there are empty print areas
        const imagesToAdd = Math.min(payloads.length, targetRoleKeys.length);

        // Add each image into the next available print area
        for (let imageIndex = 0; imageIndex < imagesToAdd; imageIndex += 1) {

            await handleUpsertRoleImage(targetRoleKeys[imageIndex], payloads[imageIndex]);

        }

    };

    // This reads a File object as a base64 data url
    const ReadFileAsDataUrl = (file) => {

        // Return a promise so we can await it
        return new Promise((resolve, reject) => {

            // Create a file reader
            const reader = new FileReader();

            // Resolve with the base64 result
            reader.onload = () => resolve(reader.result);

            // Reject on failure
            reader.onerror = () => reject(new Error("Could not read dropped file."));

            // Start reading
            reader.readAsDataURL(file);

        });

    };



    // This converts a dropped image file into the same payload shape used elsewhere
    const BuildDroppedImagePayloadFromFile = async (file) => {

        // Create a local object url for preview
        const objectUrl = URL.createObjectURL(file);

        // Read base64 for saving / mockup generation
        const uploadedImage = await ReadFileAsDataUrl(file);

        // Return payload in your expected structure
        return {

            // Keep image details similar to your uploader payload
            imageDetails: {
                image_url: objectUrl,
                name: file?.name || "Dropped image",
            },

            // Store the base64 image data
            uploadedImage: uploadedImage,

            // Keep default background colour
            background_colour: null,

        };

    };



    // This handles an external image file being dropped directly onto one occupied role tile
    const HandleExternalImageDropForRole = (event, roleKey, roleDisplayName) => {

        // Prevent the browser opening the file
        event.preventDefault();

        // Prevent the page-level drop handler from filling another print area
        event.stopPropagation();

        // Clear all drag visuals
        setIsExternalFileDragActive(false);
        setDragOverRoleKey(null);

        // Read the dropped files immediately while the drag event is still active
        const droppedFiles = Array.from(event?.dataTransfer?.files || []);

        // Stop when there are no files
        if (droppedFiles.length === 0) return;

        // Use the first dropped image file
        const firstImageFile = droppedFiles.find((file) => IsImageFile(file));

        // Show a clear error when no image file was dropped
        if (!firstImageFile) {
            toastError({
                title: "Error",
                message: "Please drop an image file.",
                autoCloseMs: 4000,
            });
            return;
        }

        // Use the role key when no display name is available
        let safeRoleDisplayName = String(roleKey || "image");

        // Prefer the supplied display name
        if (roleDisplayName) {
            safeRoleDisplayName = String(roleDisplayName);
        }

        // Ask before replacing an occupied role through an implicit drag action
        DisplayConfirmationDialog(
            "<h3>Replace the existing " + safeRoleDisplayName + " image?</h3>",
            "Replace Image",
            "Cancel",
            async function (isConfirmed) {

                // Stop when the user cancels
                if (!isConfirmed) return;

                try {

                    // Build the normal role-image payload from the dropped file
                    const payload = await BuildDroppedImagePayloadFromFile(firstImageFile);

                    // Replace the image on the exact role tile which received the drop
                    await handleUpsertRoleImage(roleKey, payload);

                } catch (error) {

                    // Log the unexpected processing failure
                    console.error("Failed to replace dropped role image:", error);

                    // Show a clear error
                    toastError({
                        title: "Error",
                        message: "Could not process dropped image.",
                        autoCloseMs: 5000,
                    });

                }

            }
        );

    };



    // This handles an external image file being dropped onto the popup
    const HandleExternalImageDrop = useCallback(async (event) => {

        // Prevent browser opening the file
        event.preventDefault();

        // Stop bubbling
        event.stopPropagation();

        // Clear visual drag state
        setIsExternalFileDragActive(false);

        // Read files safely
        const droppedFiles = Array.from(event?.dataTransfer?.files || []);

        // If there are no files, do nothing
        if (droppedFiles.length === 0) return;

        // Find the first image file only
        const firstImageFile = droppedFiles.find((file) => IsImageFile(file));

        // If no image file was dropped, show error and exit
        if (!firstImageFile) {

            // Toast error
            toastError({
                title: "Error",
                message: "Please drop an image file.",
                autoCloseMs: 4000,
            });

            // Exit early
            return;

        }

        // Find the next empty role
        const nextEmptyRoleKey = GetNextEmptyRoleKeyForDroppedImage();

        // If no empty role is available, do not overwrite anything
        if (!nextEmptyRoleKey) {

            // Toast helpful message
            toastError({
                title: "No empty print area",
                message: isAddingAdvancedDesignSet
                    ? "All selected print areas already have an image."
                    : "The basic print area already has an image.",
                autoCloseMs: 5000,
            });

            // Exit early
            return;

        }

        try {

            // Build the payload from the dropped file
            const payload = await BuildDroppedImagePayloadFromFile(firstImageFile);

            // Upsert the image into the next empty role
            await handleUpsertRoleImage(nextEmptyRoleKey, payload);

        } catch (error) {

            // Log error
            console.error("Failed to process dropped image:", error);

            // Toast error
            toastError({
                title: "Error",
                message: "Could not process dropped image.",
                autoCloseMs: 5000,
            });

        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        handleUpsertRoleImage,
        isAddingAdvancedDesignSet,
        printAreaRoles,
        toastError,
        podDesignDetails?.roles,
        selectedRoles,
        basicDesignRoleKey,
    ]);



    // Format the created date
    const formattedCreatedDate = podDesignDetails?.created ? formatDisplayDateTime(podDesignDetails.created) : "";



    // Render the toggle switch for the advance/basic design options
    const RenderAdvancedToggle = () => {

        return (

            <div>
                    
                <div className="advanced-toggle" role="switch" aria-checked={isAddingAdvancedDesignSet}>
                    {/* Basic toggle box */}
                    <button
                        className={`advanced-toggle__option ${!isAddingAdvancedDesignSet ? 'advanced-toggle__option--active' : ''}`}
                        style={{ color: !isAddingAdvancedDesignSet ? "" : "var(--text-color-trans)" }}
                        onClick={() => {

                            // Safely store the current design roles
                            const currentDesignRoles = Array.isArray(podDesignDetails?.roles)
                                ? podDesignDetails.roles
                                : [];

                            // Check whether the basic role already exists
                            const basicRoleExists = currentDesignRoles.some((roleItem) => {

                                // Return whether this is the basic role
                                return roleItem?.role === basicDesignRoleKey;

                            });

                            // Check whether any non-basic role data exists
                            const thereIsOtherRoleData = currentDesignRoles.some((roleItem) => {

                                // Return whether this role is not the basic role
                                return roleItem?.role !== basicDesignRoleKey;

                            });

                            // Create a reusable function for switching to the basic design
                            const switchToBasicDesign = () => {

                                // Set the design mode to basic
                                setIsAddingAdvancedDesignSet(false);

                                // Remove every role except the basic role
                                setPodDesignDetails((previousDetails) => {

                                    // Safely store the previous roles
                                    const previousRoles = Array.isArray(previousDetails?.roles)
                                        ? previousDetails.roles
                                        : [];

                                    // Return the updated design details
                                    return {
                                        ...previousDetails,
                                        roles: previousRoles.filter((roleItem) => {

                                            // Keep only the basic role
                                            return roleItem?.role === basicDesignRoleKey;

                                        })
                                    };

                                });

                                // Create the basic template role when it does not already exist
                                if (
                                    designOrTemplate === "template"
                                    && !basicRoleExists
                                ) {

                                    // Create the basic template role
                                    setNewTemplateRole(basicDesignRoleKey);

                                }

                                // Update the printable area selector
                                setSelectedRoles((previousRoles) => {

                                    // Safely store the previous selected roles
                                    const safePreviousRoles = Array.isArray(previousRoles)
                                        ? previousRoles
                                        : [];

                                    // Return the updated selected roles
                                    return safePreviousRoles.map((selectedRole) => {

                                        // Return the role with its updated selected state
                                        return {
                                            ...selectedRole,
                                            selected: selectedRole?.role === basicDesignRoleKey
                                        };

                                    });

                                });

                            };

                            // Show a warning when switching will remove other role data
                            if (thereIsOtherRoleData) {

                                // Display the confirmation dialog
                                DisplayConfirmationDialog(
                                    "<h3>Are you sure?</h3><br/>Switching to a basic design will clear all other print area data",
                                    "Switch to Basic",
                                    "Cancel",
                                    async function (isConfirmed) {

                                        // Stop when the user cancels
                                        if (!isConfirmed) {

                                            // Exit without changing the design
                                            return;

                                        }

                                        // Switch to the basic design
                                        switchToBasicDesign();

                                    }
                                );

                                // Stop so the design is not changed before confirmation
                                return;

                            }

                            // Switch immediately when there is no other role data
                            switchToBasicDesign();

                        }}
                    >
                        Basic
                    </button>

                    {/* Advanced toggle box */}
                    <button
                        className={`advanced-toggle__option ${isAddingAdvancedDesignSet ? 'advanced-toggle__option--active' : ''}`}
                        style={{ color: !isAddingAdvancedDesignSet ? "var(--text-color-trans)" : "" }}
                        onClick={() => {

                            // Safely store the available printable area roles
                            const safePrintAreaRoles = Array.isArray(printAreaRoles)
                                ? printAreaRoles
                                : [];

                            // Get the first two valid printable area role keys
                            const firstTwoRoleKeys = safePrintAreaRoles

                                // Take only the first two printable areas
                                .slice(0, 2)

                                // Return the role key from each printable area
                                .map((printAreaRole) => printAreaRole?.role)

                                // Remove any missing role keys
                                .filter(Boolean);

                            // Toggle the advanced design set state
                            setIsAddingAdvancedDesignSet((previousValue) => !previousValue);

                            if (designOrTemplate === "template") {

                                // Add each of the first two roles to the template
                                firstTwoRoleKeys.forEach((roleKey) => {

                                    // Create the template role using its role key
                                    setNewTemplateRole(roleKey);

                                });

                            }

                            // Update selected roles so only the first two roles are selected
                            setSelectedRoles((previousRoles) => {

                                // Safely store the previous selected roles
                                const safePreviousRoles = Array.isArray(previousRoles)
                                    ? previousRoles
                                    : [];

                                // Return the updated selected roles
                                return safePreviousRoles.map((selectedRole) => {

                                    // Check whether this role is one of the first two printable areas
                                    const shouldBeSelected = firstTwoRoleKeys.includes(
                                        selectedRole?.role
                                    );

                                    // Return the updated role
                                    return {
                                        ...selectedRole,
                                        selected: shouldBeSelected
                                    };

                                });

                            });

                        }}
                    >
                        Advanced
                    </button>
                </div>

            </div>

        )

    };



    // Render list of roles for user to toggle
    const RenderListOfRoles = () => {
        
        return (

            <div className="pod-design__printable-areas-selector-updated">

                <br/>

                <div className="horizontal-align-content-center">

                    <select 
                        name="visualType" 
                        value={rolesVisualType}
                        onChange={(e) => setRolesVisualType(e.target.value)}
                        style={{width: "auto", padding: "2px 8px"}}
                    >

                        <option value="cube">Cube</option>
                        <option value="t-shirt">Clothing</option>

                    </select>
                </div>

                <div className="pod-design__printable-areas-selector-grid-updated">

                    {selectedRoles?.map((role) => (

                        <div 
                            key={role.role} 
                            className="choose-print-areas-container"
                            style={{cursor: "pointer"}}

                            // Handle printable area selection changes
                            onClick={() => {

                                // Store whether the role is currently selected
                                const isCurrentlySelected = Boolean(role.selected);

                                // Calculate the next selected value
                                const nextSelected = !isCurrentlySelected;

                                // Safely read the current design or template roles
                                let currentRoles = [];

                                if (Array.isArray(podDesignDetails?.roles)) {
                                    currentRoles = podDesignDetails.roles;
                                }

                                // Find any existing data for this printable area
                                const existingRole = currentRoles.find((currentRole) => {

                                    // Return whether the role keys match
                                    return currentRole.role === role.role;

                                });

                                // Handle template printable areas separately
                                if (designOrTemplate === "template") {

                                    // Handle adding a template role
                                    if (nextSelected) {

                                        // Prevent adding the same role more than once
                                        if (existingRole) {

                                            // Stop processing because the role already exists
                                            return;

                                        }

                                        // Add the role using the default template role setup
                                        setNewTemplateRole(role.role);

                                        // Update the selector state
                                        setSelectedRoles((previousRoles) => {

                                            // Safely handle invalid previous state
                                            const safePreviousRoles = Array.isArray(previousRoles)
                                                ? previousRoles
                                                : [];

                                            // Update only the selected role
                                            return safePreviousRoles.map((selectedRole) => {

                                                // Check whether this is the role being updated
                                                if (selectedRole.role === role.role) {

                                                    // Return the role as selected
                                                    return {
                                                        ...selectedRole,
                                                        selected: true
                                                    };

                                                }

                                                // Return all other roles unchanged
                                                return selectedRole;

                                            });

                                        });

                                    } else {

                                        // Start by assuming no other printable area is selected
                                        let anotherTemplateAreaIsSelected = false;

                                        // Printable-area selection is authoritative, even when an image role was deleted
                                        if (Array.isArray(selectedRoles)) {

                                            anotherTemplateAreaIsSelected =
                                                selectedRoles.some((selectedRole) => {

                                                    // Ignore the printable area currently being deselected
                                                    if (selectedRole.role === role.role) {
                                                        return false;
                                                    }

                                                    // Check whether another printable area remains selected
                                                    return Boolean(selectedRole.selected);

                                                });

                                        }

                                        // Prevent removing the final selected printable area
                                        if (!anotherTemplateAreaIsSelected) {

                                            toastError({
                                                title: "Error",
                                                message: "There must be at least one printable area selected"
                                            });

                                            return;

                                        }

                                        // Remove the corresponding image role when it still exists
                                        if (existingRole) {

                                            setPodDesignDetails((previousDetails) => {

                                                // Safely read the previous template roles
                                                let previousTemplateRoles = [];

                                                if (Array.isArray(previousDetails?.roles)) {

                                                    previousTemplateRoles =
                                                        previousDetails.roles;

                                                }

                                                // Remove only the matching template role
                                                return {
                                                    ...previousDetails,
                                                    roles: previousTemplateRoles.filter(
                                                        (templateRole) => {

                                                            return templateRole.role !== role.role;

                                                        }
                                                    )
                                                };

                                            });

                                        }

                                        // Deselect the printable area whether or not its image role still exists
                                        setSelectedRoles((previousRoles) => {

                                            // Safely read the previous selector state
                                            let safePreviousRoles = [];

                                            if (Array.isArray(previousRoles)) {

                                                safePreviousRoles =
                                                    previousRoles;

                                            }

                                            // Update only the printable area being deselected
                                            return safePreviousRoles.map((selectedRole) => {

                                                if (selectedRole.role === role.role) {

                                                    return {
                                                        ...selectedRole,
                                                        selected: false
                                                    };

                                                }

                                                return selectedRole;

                                            });

                                        });

                                    }

                                    // Prevent the standard design logic from also running
                                    return;

                                }

                                // Safely read the current printable-area selection
                                let currentSelectedRoles = [];

                                if (Array.isArray(selectedRoles)) {
                                    currentSelectedRoles = selectedRoles;
                                }

                                // Handle removing a printable area
                                if (!nextSelected) {

                                    // Check whether another printable area will remain selected
                                    const anyOtherSelected =
                                        currentSelectedRoles.some((selectedRole) => {

                                            // Ignore the printable area currently being removed
                                            if (selectedRole.role === role.role) {
                                                return false;
                                            }

                                            // Check whether this other area remains selected
                                            return Boolean(selectedRole.selected);

                                        });

                                    // Prevent removing the final printable area
                                    if (!anyOtherSelected) {

                                        toastError({
                                            title: "Error",
                                            message: "There must be at least one printable area selected"
                                        });

                                        return;

                                    }

                                    // Existing role data must be confirmed and removed
                                    if (existingRole) {

                                        handleDeleteRoleImage(
                                            existingRole,
                                            true
                                        );

                                        return;

                                    }

                                }

                                // Select a new empty area or deselect one with no stored role data
                                setSelectedRoles((previousRoles) => {

                                    // Safely read the latest selector state
                                    let safePreviousRoles = [];

                                    if (Array.isArray(previousRoles)) {
                                        safePreviousRoles = previousRoles;
                                    }

                                    // Update only the clicked printable area
                                    return safePreviousRoles.map((selectedRole) => {

                                        if (selectedRole.role === role.role) {

                                            return {
                                                ...selectedRole,
                                                selected: nextSelected,
                                            };

                                        }

                                        return selectedRole;

                                    });

                                });

                            }}
                        > 

                            <div 
                                style={{opacity: role.selected ? "1" : "0.3"}}
                                
                            >

                                <RoleVisual
                                    roleKey={role.role}
                                    productFamily={rolesVisualType}
                                    //productFamily={"shirt"}
                                    title={role.name}
                                />

                            </div>

                            <label>

                                {printAreaRoles.find((roleItem) => roleItem.role === role.role).name}

                            </label>

                        </div>

                    ))}

                </div>

            </div>

        )

    };



    // Get the open function and the ready flag for Customizr.
    const { openCustomizr, isReady: isCustomizrReady } = useCustomizr();

    const {
        handleOpenCustomizrClick
    } = useQueueItemPopupActions({
        openCustomizr
    });





    // Render the create ai design pop up
    const RenderDesignAiPage = ({isOpen, onClose}) => {


        // If isOpen is false return early (don't show the pop up)
        if (!isOpen) return;


        return (

            <div className="popup-overlay">
            
                <div className="popup-container popup-container--large popup-container--ai-design">

                    {/* This is the modal header */}
                    <div className="popup__header">

                        {/* This is the title area */}
                        <div className="popup__headerLeft">

                            {/* This is the parent title */}
                            <h2 className="popup__title">

                                Create AI Design

                            </h2>

                            {/* This is the parent subtitle */}
                            <p className="popup__subtitle">

                                Write a prompt, generate an image, optimise it for print, review it, then use it as your new POD design.

                            </p>

                        </div>

                        {/* This is the header actions */}
                        <div className="popup__headerRight">

                            {/* This is the close button */}
                            <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close" disabled={isDeleting}>
                                <CloseIcon />
                            </button>

                        </div>

                    </div>


                    <div className="popup-content popup-content__body popup-content__body--ai-design">

                        <PodDesignAiPage setSubmittedAiDesign={setSubmittedAiDesign} setShowPodDesignAiPage={setShowPodDesignAiPage} />

                    </div>

                </div>

            </div>


        )

    };

    // Render the upscale design pop up
    const renderDesignUpscalePage = ({isOpen, onClose}) => {

        // If isOpen is false return early (don't show the pop up)
        if (!isOpen) return;

        return (

            <div className="popup-overlay">

                <div className="popup-container popup-container--large popup-container--ai-design">

                    {/* This is the modal header */}
                    <div className="popup__header">

                        {/* This is the title area */}
                        <div className="popup__headerLeft">

                            {/* This is the parent title */}
                            <h2 className="popup__title">
                                Upscale Design Image
                            </h2>

                            {/* This is the parent subtitle */}
                            <p className="popup__subtitle">
                                Choose an upscaler, preview the result, then submit it to replace the current design image.
                            </p>

                        </div>

                        {/* This is the header actions */}
                        <div className="popup__headerRight">

                            {/* This is the close button */}
                            <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close" disabled={isDeleting}>
                                <CloseIcon />
                            </button>

                        </div>

                    </div>

                    <div className="popup-content popup-content__body popup-content__body--ai-design">

                        <PodDesignUpscalePage
                            sourceImage={submittedUpscaledImage?.sourceImage}
                            sourceFileType={submittedUpscaledImage?.sourceFileType}
                            sourceWidth={submittedUpscaledImage?.sourceWidth}
                            sourceHeight={submittedUpscaledImage?.sourceHeight}
                            setSubmittedUpscaledImage={setSubmittedUpscaledImage}
                            setShowPodDesignUpscalePage={setShowPodDesignUpscalePage}
                        />

                    </div>

                </div>

            </div>

        );

    };

    const [imagePreviewPopupOpen, setImagePreviewPopupOpen] = useState(null);
    const [selectedImageToZoom, setSelectedImageToZoom] = useState(null);
    const [zoomCarouselImages, setZoomCarouselImages] = useState(null);

    // Handle click to zoom on design image
    const openImagePreviewPopup = (mainImageUrl) => {

        setZoomCarouselImages([{image_url: mainImageUrl}]);

        // Set the selected image to the one we've passed in (clicked on)
        setSelectedImageToZoom(mainImageUrl);

        // Open the large image preview pop up
        setImagePreviewPopupOpen(true);

    };

    return (


        <div className="popup-overlay">
        
            <div className="popup-container popup-container--large">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            {pageTitle}

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            {!isAddingNewDesign && podDesignDetails.created ? ( <>Created: {formattedCreatedDate}</> ) : ""}

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {designOrTemplate === "design" &&

                            <>
                                <ToggleSwitch
                                    name="marketplace"
                                    size={isMobile ? "s" : "m"}
                                    label="Marketplace"
                                    title={formValues.marketplace === "Y" || formValues.marketplace === true ? "Remove Design From Marketplace" : "Publish Design To Marketplace"}
                                    value={formValues.marketplace === "Y" ? true : formValues.marketplace === "N" ? false : formValues.marketplace}
                                    disabled={isLoading}
                                    onChange={(event) => {

                                        handleChange(event);

                                    }}
                                />

                                <ToggleSwitch
                                    name="store"
                                    size={isMobile ? "s" : "m"}
                                    label="Store"
                                    title={formValues.store === "Y" || formValues.store === true ? "Hide Design From Store" : "Show Design On Store"}
                                    value={formValues.store === "Y" ? true : formValues.store === "N" ? false : formValues.store}
                                    disabled={isLoading}
                                    onChange={(event) => {

                                        handleChange(event);

                                    }}
                                />

                                <ToggleSwitch
                                    name="active"
                                    size={isMobile ? "s" : "m"}
                                    label="Active"
                                    title={formValues.active === "Y" || formValues.active === true ? "Deactivate Design" : "Activate Design"}
                                    value={formValues.active === "Y" ? true : formValues.active === "N" ? false : formValues.active}
                                    disabled={isLoading}
                                    onChange={(event) => {

                                        handleChange(event);

                                    }}
                                />
                            </>

                        }

                        {/* This is the close button */}
                        <button
                            type="button"
                            className="popup__close"
                            onClick={handleCloseDesign}
                            title="Close"
                            disabled={designIsSaving || isDeleting || isLoading}
                        >
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div
                    className={`popup-content popup-content__body ${isExternalFileDragActive ? "popup-content__body--file-drag-active" : ""}`}
                    onDragEnter={(event) => {

                        // Ignore file dragging while another design modal is open
                        if (showPodDesignAiPage || showPodDesignUpscalePage) return;

                        // Ignore non-file drags
                        if (!DragEventHasFiles(event)) return;

                        // Prevent browser behaviour
                        event.preventDefault();

                        // Stop bubbling
                        event.stopPropagation();

                        // Show drag UI
                        setIsExternalFileDragActive(true);

                    }}
                    onDragOver={(event) => {

                        // Ignore file dragging while another design modal is open
                        if (showPodDesignAiPage || showPodDesignUpscalePage) return;

                        // Ignore non-file drags
                        if (!DragEventHasFiles(event)) return;

                        // Prevent browser behaviour
                        event.preventDefault();

                        // Stop bubbling
                        event.stopPropagation();

                        // Keep visual state active
                        setIsExternalFileDragActive(true);

                        // Show copy cursor for external files
                        event.dataTransfer.dropEffect = "copy";

                    }}
                    onDragLeave={(event) => {

                        // Only clear when fully leaving the popup body
                        if (!event.currentTarget.contains(event.relatedTarget)) {

                            // Clear drag UI
                            setIsExternalFileDragActive(false);

                        }

                    }}
                    onDrop={(event) => {

                        // Ignore drops while another design modal is open
                        if (showPodDesignAiPage || showPodDesignUpscalePage) return;

                        // Handle normal external image drops
                        HandleExternalImageDrop(event);

                    }}
                >


                    {/*/ If it's loading OR the basicDesignRoleKey hasn't loaded yet - show loading */}
                    {isLoading || !basicDesignRoleKey || isDeleting ?

                        <LoadingSpinner />

                        :

                        // If we're not adding a new design and there's no podDesignDetails or error message yet because they're still loading - show loading
                        !isAddingNewDesign && !podDesignDetails && isStrichTemplate === null ?

                            <LoadingSpinner />
                            
                            :

                            <>

                                <div className="design-details">

                                    {/* This is the design details */}
                                    <div className="card">

                                        <div className="card__header">

                                            <h3>Details</h3>

                                            <span className="hintText">

                                                {designOrTemplate === "template"
                                                    ? "Configure reusable template settings"
                                                    : "Add / edit design details"}

                                            </span>

                                        </div>


                                        <div className="card__content artwork-details-div">

                                            <form onSubmit={handleSubmit} id="designDetailsForm">

                                                <div className="pod-design-details">
                                                    
                                                    <div className="details-container">

                                                        
                                                        <div className="pod-design-details__title">
                                                            <label>Title</label>
                                                            <input
                                                                name="title"
                                                                type="text"
                                                                value={currentTranslation?.title || ""}
                                                                onChange={handleTranslationChange}
                                                                placeholder="Enter a title"
                                                                required
                                                                disabled={isStrichTemplate}
                                                            />
                                                        </div>

                                                        {fieldErrors.title && <p style={{ color: "red" }}>{fieldErrors.title}</p>}


                                                        {/* Template-specific details */}
                                                        {designOrTemplate === "template" &&

                                                            <>

                                                                <div className="pod-design-details__title">
                                                                    <label>Code</label>
                                                                    <input
                                                                        name="code"
                                                                        type="text"
                                                                        defaultValue={formValues?.code}
                                                                        onChange={handleChange}
                                                                        placeholder="Enter a code"
                                                                        required
                                                                        disabled={isStrichTemplate}
                                                                    />
                                                                </div>

                                                                {fieldErrors.code && <p style={{ color: "red" }}>{fieldErrors.code}</p>}

                                                                <div className="pod-template-explanation">

                                                                    <p>
                                                                        Create a template of products, print areas and pricing here to use again and again. Either assign your template to designs to quickly add your preferred products or use with the inline shopping system.
                                                                    </p>

                                                                    <p>
                                                                        For more information, see the help files that Ian's writing.
                                                                    </p>

                                                                </div>

                                                            </>

                                                        }


                                                        {/* Design-specific details */}
                                                        {designOrTemplate === "design" &&

                                                            <>

                                                                <div className="pod-design-details__title">

                                                                    <label>Store</label>

                                                                    <select
                                                                        name="store_id"

                                                                        // Find the first Strich connection and use it as the default store
                                                                        value={formValues.store_id || connectionsData?.find((con) => con.conntype === "STRICH")?.id || ""}
                                                                        required
                                                                        onChange={handleDesignStoreChange}
                                                                        disabled={isLoading}
                                                                    >

                                                                        {connectionsData?.map((conn) => {

                                                                            // Only include Strich stores
                                                                            if (conn.conntype !== "STRICH") { return null; }

                                                                            return (

                                                                                <option key={conn.id} value={conn.id}>
                                                                                    {conn.connectionname}
                                                                                </option>

                                                                            );

                                                                        })}

                                                                    </select>

                                                                </div>


                                                                <div className="pod-design-details__title">

                                                                    <label>Templates</label>

                                                                    <div className="pod-design-details__template-selector">

                                                                        <select
                                                                            value=""
                                                                            onChange={handleAddTemplate}
                                                                            disabled={isLoading || designIsSaving}
                                                                        >

                                                                            <option value="">Add template...</option>

                                                                            {availablePodTemplates

                                                                                // Only offer active templates which are not already assigned
                                                                                .filter((template) => {
                                                                                    return (
                                                                                        template?.active === "Y" &&
                                                                                        !selectedTemplateIds.some((templateId) => Number(templateId) === Number(template?.id))
                                                                                    );
                                                                                })

                                                                                // Build the available template options
                                                                                .map((template) => (

                                                                                    <option
                                                                                        key={(template?.source || "user") + "-" + template.id}
                                                                                        value={template.id}
                                                                                    >
                                                                                        {getPodTemplateLabel(template)}
                                                                                    </option>

                                                                                ))

                                                                            }

                                                                        </select>

                                                                        {selectedTemplateIds.length > 0 &&

                                                                            <div className="pod-design-details__template-list">

                                                                                {selectedTemplateIds.map((templateId) => {

                                                                                    // Find the complete template details for this assignment
                                                                                    const template = availablePodTemplates.find((availableTemplate) => {
                                                                                        return Number(availableTemplate?.id) === Number(templateId);
                                                                                    });

                                                                                    return (

                                                                                        <div key={templateId} className="pod-design-details__template-chip">

                                                                                            <span>{template ? getPodTemplateLabel(template) : "Template " + templateId}</span>

                                                                                            <button
                                                                                                type="button"
                                                                                                className="pod-design-details__template-remove"
                                                                                                onClick={() => handleRemoveTemplate(templateId)}
                                                                                                disabled={isLoading || designIsSaving}
                                                                                                title="Remove template"
                                                                                            >
                                                                                                <CloseIcon />
                                                                                            </button>

                                                                                        </div>

                                                                                    );

                                                                                })}

                                                                            </div>

                                                                        }

                                                                    </div>

                                                                </div>


                                                                <label>Description</label>

                                                                <RichTextInput
                                                                    value={currentTranslation?.description || ""}
                                                                    onChange={(inputValue) => {

                                                                        // Only update the matched translation
                                                                        const updatedTranslations = formValues.translations.map(t =>

                                                                            t.language === chosenLanguage

                                                                                ? { ...t, description: inputValue }

                                                                                : t

                                                                        );

                                                                        setFormValues({
                                                                            ...formValues,
                                                                            translations: updatedTranslations,
                                                                        });

                                                                    }}
                                                                    placeholder="Enter a description"
                                                                    disabled={isStrichTemplate}
                                                                />

                                                                <br/>

                                                                <div className="artwork-details-div__two-columns">

                                                                    <CustomDropdownInput
                                                                        label="Category"
                                                                        placeholder="Enter a category"
                                                                        value={currentTranslation?.category || ''}
                                                                        setValue={(inputValue) => {

                                                                            // Only update the matched translation
                                                                            const updatedTranslations = formValues?.translations?.map(t =>

                                                                                t.language === chosenLanguage

                                                                                    ? {
                                                                                        ...t,
                                                                                        category: inputValue,

                                                                                        // Reset subcategory when category changes
                                                                                        subcategory: ""
                                                                                    }

                                                                                    : t

                                                                            );

                                                                            setFormValues({
                                                                                ...formValues,
                                                                                translations: updatedTranslations,
                                                                            });

                                                                        }}
                                                                        options={categoryOptions}
                                                                        disabled={isStrichTemplate}
                                                                    />

                                                                    <CustomDropdownInput
                                                                        label="Sub Category"
                                                                        placeholder="Enter a sub category"
                                                                        value={currentTranslation?.subcategory || ''}
                                                                        setValue={(inputValue) => {

                                                                            // Only update the matched translation
                                                                            const updatedTranslations = formValues?.translations?.map(t =>

                                                                                t.language === chosenLanguage

                                                                                    ? { ...t, subcategory: inputValue }

                                                                                    : t

                                                                            );

                                                                            setFormValues({
                                                                                ...formValues,
                                                                                translations: updatedTranslations,
                                                                            });

                                                                        }}
                                                                        options={subCategoryOptions}
                                                                        disabled={isStrichTemplate}
                                                                    />

                                                                </div>

                                                                <br/>

                                                                <div className="pod-design-details__keywords">

                                                                    <KeywordTagsInput
                                                                        title="Keywords"
                                                                        maxListHeight={67}
                                                                        value={keywordList || ""}
                                                                        onChange={handleKeywordsChange}
                                                                        disabled={isStrichTemplate}
                                                                    />

                                                                </div>

                                                            </>

                                                        }
                                            
                                                    </div>
                                                    
                                                    
                                                    <div>
                                                    
                                                        {/* Need to add something for linking with other designs - pop up with thumbnails and titles maybe */}
                                                        {/* <label>Group: </label><span>List of Ids?..</span> */}

                                                    </div>
                                                                                                            
                                                </div>
                                                                        
                                            </form>

                                        </div>

                                    </div>

                                    
                                    
                                    {/* This is the print areas */}
                                    <div className="card">

                                        <div className="card__header">

                                            <h3>Print Areas</h3>

                                            <span className="hintText">

                                                {designOrTemplate === "template"
                                                    ? "Configure reusable print areas and preview artwork"
                                                    : isAddingAdvancedDesignSet
                                                        ? "Add / edit the design image on each selected print area"
                                                        : "Add / edit the design image"}

                                            </span>

                                        </div>
                                
                                        {/* ADVANCED -- Check if we're adding an advanced design OR if there is more than 1 print area (role) in the podDesignDetails */}
                                        { isAddingAdvancedDesignSet ?

                                            <div className="pod-design__design-set">

                                                <div className="pod-design__design-set--left">

                                                    <div>
                                                        {/* Toggle for switching to basic (front only) design */}
                                                        <RenderAdvancedToggle />
                                                        <span>    
                                                            (Multiple Print Areas)
                                                        </span>
                                                    </div>
                                            
                                                    <div className="horizontal-align-content-center">

                                                        <div className="choose-print-areas-btn">

                                                            <RenderListOfRoles />

                                                        </div>

                                                    </div>

                                                </div>

                                                <div className='pod-design__design-set-images'>

                                                    {/* Map through the selected roles and display each image and role type for the ones that are selected - if there is existing design data for it then show that */}
                                                    {selectedRoles?.map((role) => {
                                                    
                                                        // If not selected and there's no existing data for it, render nothing
                                                        if (!role?.selected) return null;

                                                        // Find existing saved role data (if any)
                                                        const existingRoleData = podDesignDetails?.roles?.find((existingRole) => {

                                                            // Match by role key
                                                            return String(existingRole?.role ?? "") === String(role?.role ?? "");

                                                        });

                                                        // Check whether this role currently has an image
                                                        const roleHasImage = RoleHasImage(existingRoleData);

                                                        // Set the default controls class
                                                        let imageControlsClassName =
                                                            "pod-design__design-set-image-controls";

                                                        // Give empty design roles a fixed lower control area
                                                        if (
                                                            designOrTemplate === "design" &&
                                                            !roleHasImage
                                                        ) {

                                                            imageControlsClassName +=
                                                                " pod-design__design-set-image-controls--empty";

                                                        }

                                                        // Check whether this tile can be dragged
                                                        const isDraggableRole = canDragBetweenAdvancedRoles && roleHasImage;

                                                        // Check whether this tile is the current drag-over target
                                                        const isDragOverTarget = dragOverRoleKey === role.role;

                                                        return (

                                                            <div
                                                                key={role.role}
                                                                className={`pod-design__design-set-image-container ${isDraggableRole ? "pod-design__design-set-image-container--draggable" : ""} ${isDragOverTarget ? "pod-design__design-set-image-container--drag-over" : ""}`}
                                                                draggable={isDraggableRole}
                                                                onDragEnterCapture={(event) => {

                                                                    // Only intercept external files over an occupied role
                                                                    if (!roleHasImage || !DragEventHasFiles(event)) return;

                                                                    // Prevent the page-level fill-next-empty handler seeing this drag
                                                                    event.preventDefault();
                                                                    event.stopPropagation();

                                                                    // Hide the general popup overlay and highlight this exact role
                                                                    setIsExternalFileDragActive(false);
                                                                    setDragOverRoleKey(role.role);

                                                                }}
                                                                onDragOverCapture={(event) => {

                                                                    // Only intercept external files over an occupied role
                                                                    if (!roleHasImage || !DragEventHasFiles(event)) return;

                                                                    // Prevent the page-level fill-next-empty handler seeing this drag
                                                                    event.preventDefault();
                                                                    event.stopPropagation();

                                                                    // Show the replacement cursor and highlight this exact role
                                                                    event.dataTransfer.dropEffect = "copy";
                                                                    setIsExternalFileDragActive(false);
                                                                    setDragOverRoleKey(role.role);

                                                                }}
                                                                onDropCapture={(event) => {

                                                                    // Only intercept external files over an occupied role
                                                                    if (!roleHasImage || !DragEventHasFiles(event)) return;

                                                                    // Replace the image on this exact role
                                                                    HandleExternalImageDropForRole(
                                                                        event,
                                                                        role.role,
                                                                        role.name || role.role
                                                                    );

                                                                }}
                                                                onDragStart={(event) => {

                                                                    // Do nothing if this role is not draggable
                                                                    if (!isDraggableRole) return;

                                                                    // Store the source role key in local state
                                                                    setDraggedRoleKey(role.role);

                                                                    // Also place it onto the native drag payload
                                                                    event.dataTransfer.setData("text/plain", String(role.role));

                                                                    // Make this a move interaction
                                                                    event.dataTransfer.effectAllowed = "move";

                                                                }}
                                                                onDragEnd={() => {

                                                                    // Clear drag state when dragging finishes
                                                                    setDraggedRoleKey(null);

                                                                    // Clear hovered target state
                                                                    setDragOverRoleKey(null);

                                                                }}
                                                                onDragOver={(event) => {

                                                                    // External files are handled by the uploader or occupied-role replacement path
                                                                    if (DragEventHasFiles(event)) return;

                                                                    // Only allow drag over when advanced multi-role drag is enabled
                                                                    if (!canDragBetweenAdvancedRoles) return;

                                                                    // Ignore drag-over on the same role
                                                                    if (String(draggedRoleKey ?? "") === String(role.role ?? "")) return;

                                                                    // Allow dropping here
                                                                    event.preventDefault();

                                                                    // Show move cursor
                                                                    event.dataTransfer.dropEffect = "move";

                                                                    // Mark this role as the current hover target
                                                                    setDragOverRoleKey(role.role);

                                                                }}
                                                                onDragEnter={(event) => {

                                                                    // External files are handled by the uploader or occupied-role replacement path
                                                                    if (DragEventHasFiles(event)) return;

                                                                    // Only allow drag enter when advanced multi-role drag is enabled
                                                                    if (!canDragBetweenAdvancedRoles) return;

                                                                    // Ignore drag-enter on the same role
                                                                    if (String(draggedRoleKey ?? "") === String(role.role ?? "")) return;

                                                                    // Prevent default so drop is allowed
                                                                    event.preventDefault();

                                                                    // Mark this role as active
                                                                    setDragOverRoleKey(role.role);

                                                                }}
                                                                onDragLeave={(event) => {

                                                                    // Only clear if we are leaving this container fully
                                                                    if (!event.currentTarget.contains(event.relatedTarget)) {

                                                                        // Remove the hover state
                                                                        setDragOverRoleKey(null);

                                                                    }

                                                                }}
                                                                onDrop={(event) => {

                                                                    // Prevent browser default handling
                                                                    event.preventDefault();

                                                                    // Read the dragged role key from the drag payload first
                                                                    const sourceRoleKey = event.dataTransfer.getData("text/plain") || draggedRoleKey;

                                                                    // Run duplicate-or-swap logic
                                                                    HandleRoleImageDrop(sourceRoleKey, role.role);

                                                                }}

                                                            >

                                                                {/* Keep the print-area name and its quality warning at the top of its card */}
                                                                <div className="pod-design__design-set-image-title">

                                                                    {/* Always reserve the same warning space so titles never shift */}
                                                                    <div className="pod-design__quality-warning-slot">

                                                                        <PrintAreaQualityWarning
                                                                            printQualitySummary={podDesignDetails?.print_quality_summary}
                                                                            canonicalProducts={canonicalProducts}
                                                                            roleCode={role.role}
                                                                            roleName={printAreaRoles.find((roleItem) => roleItem.role === role.role)?.name || role.name || role.role}
                                                                        />

                                                                    </div>

                                                                    <span>
                                                                        {printAreaRoles.find((roleItem) => roleItem.role === role.role)?.name || role.name || role.role}
                                                                    </span>

                                                                </div>

                                                                <div className="pod-design__design-set-image pod-design__design-set-image--advanced">

                                                                    {/* Show image if it's a design OR the choose generic image option if it's a template */}

                                                                    {/* If this is a template show 'Choose Generic Image' button */}
                                                                    {designOrTemplate === "template" ? 

                                                                        <TemplatePreviewImagePicker
                                                                            value={templatePreviewImage}
                                                                            logoUrl={strichLogoUrl}
                                                                            onChange={({ previewConfig, resolvedPayload }) => {
                                                                                
                                                                                // Store picker state if you still want it locally
                                                                                setTemplatePreviewImage(previewConfig);
                                                                                
                                                                                // Upsert the resolved image payload into the role data
                                                                                handleUpsertRoleImage(role.role, resolvedPayload);
                                                                                
                                                                            }}
                                                                            currentImage={existingRoleData?.thumbnail_url || null}
                                                                        />


                                                                    // It's a design so check if there's an image url
                                                                    : existingRoleData?.thumbnail_url ? (

                                                                        <>
                                                                        
                                                                            <img
                                                                                src={existingRoleData.thumbnail_url}
                                                                                alt={existingRoleData.role || role.role}
                                                                                title="🔍 Click to Zoom" 
                                                                                onClick={() => openImagePreviewPopup(existingRoleData.image_url)}
                                                                            />
                                                                        
                                                                        </>

                                                                    ) : (

                                                                        <>

                                                                        {/* Otherwise show uploader */}

                                                                            {/* If this is a design show the uploader */}                                                                            
                                                                            <RenderImageUploader
                                                                                label={role.name}
                                                                                selectedImage={podDesignDetails?.roles?.find((r) => r?.role === role.role)?.image_url || null}
                                                                                bucketSelectionMode="multiple"
                                                                                onUploaderDragActiveChange={() => {

                                                                                    // A direct uploader drag must never leave the page-level overlay active
                                                                                    setIsExternalFileDragActive(false);

                                                                                    // Clear any role-to-role drag highlight left while entering the uploader
                                                                                    setDragOverRoleKey(null);

                                                                                }} 
                                                                                 onImageChange={async (payloads) => {

                                                                                    // Fill this role first, then continue through the remaining empty print areas
                                                                                    await HandleBucketImagesForRole(role.role, payloads);

                                                                                }}
                                                                                disabled={isSubmittingRoleImage}
                                                                            />

                                                                        </>

                                                                    )}

                                                                </div>

                                                                <div className={imageControlsClassName}>

                                                                    {/* Show the AI image button in the card footer while this design role is empty */}
                                                                    {designOrTemplate === "design" && !roleHasImage &&

                                                                        <CreateButton
                                                                            title="Create AI Image"
                                                                            size="m"
                                                                            fullWidth={true}
                                                                            onClick={() => {

                                                                                // Store the role which should receive the generated image
                                                                                setSubmittedAiDesign({
                                                                                    role: role.role,
                                                                                });

                                                                                // Set the loading state
                                                                                setIsSubmittingAiDesign(true);

                                                                                // Open the AI image popup
                                                                                setShowPodDesignAiPage(true);

                                                                            }}
                                                                            disabled={isSubmittingRoleImage}
                                                                        />

                                                                    }
                                                                
                                                                    {roleHasImage && 

                                                                        <RenderDesignOptions
                                                                            role={role}
                                                                            existingRoleData={existingRoleData}
                                                                            setPodDesignDetails={setPodDesignDetails}
                                                                            handleOpenCustomizrClick={handleOpenCustomizrClick}
                                                                            handleUpsertRoleImage={handleUpsertRoleImage}
                                                                            isCustomizrReady={isCustomizrReady}
                                                                            setLoadingCustomizrEdit={setLoadingCustomizrEdit}
                                                                            handleDeleteRoleImage={handleDeleteRoleImage}
                                                                            handleOpenUpscaleClick={handleOpenUpscalePopup}
                                                                            isSubmittingRoleImage={isSubmittingRoleImage}

                                                                            // Hide settings which are inherited from an assigned template
                                                                            hideManagedSettings={designProductsManagedByTemplates}
                                                                        />

                                                                    }

                                                                </div>

                                                            </div>

                                                        );
                                                    
                                                    })}
                                                    
                                                </div>

                                            </div>
                                        
                                            :

                                            ""

                                        }


                                        {/* BASIC -- Check if we're adding a basic design AND if there is only 1 print area (role) in the podDesignDetails */}
                                        {!isAddingAdvancedDesignSet || (!isAddingAdvancedDesignSet && (podDesignDetails?.roles.length === 1)) ?

                                            <div className="pod-design__design-set">

                                                <div className="pod-design__design-set--left">

                                                    <div>
                                                        {/* Toggle for switching to advanced (multi role) design */}
                                                        <RenderAdvancedToggle />
                                                        <span>    
                                                            (Front Print Only)
                                                        </span>
                                                    </div>

                                                </div>

                                                <div className='pod-design__design-set-images'>

                                                    <div
                                                        className={basicRoleContainerClassName}
                                                        onDragEnterCapture={(event) => {

                                                            // Only intercept external files over the occupied basic role
                                                            if (!basicDesignImageUrl || !DragEventHasFiles(event)) return;

                                                            // Prevent the page-level fill-next-empty handler seeing this drag
                                                            event.preventDefault();
                                                            event.stopPropagation();

                                                            // Hide the general popup overlay and highlight this exact role
                                                            setIsExternalFileDragActive(false);
                                                            setDragOverRoleKey(basicDesignRoleKey);

                                                        }}
                                                        onDragOverCapture={(event) => {

                                                            // Only intercept external files over the occupied basic role
                                                            if (!basicDesignImageUrl || !DragEventHasFiles(event)) return;

                                                            // Prevent the page-level fill-next-empty handler seeing this drag
                                                            event.preventDefault();
                                                            event.stopPropagation();

                                                            // Show the replacement cursor and highlight this exact role
                                                            event.dataTransfer.dropEffect = "copy";
                                                            setIsExternalFileDragActive(false);
                                                            setDragOverRoleKey(basicDesignRoleKey);

                                                        }}
                                                        onDragLeave={(event) => {

                                                            // Clear the highlight only when fully leaving the basic role tile
                                                            if (!event.currentTarget.contains(event.relatedTarget)) {
                                                                setDragOverRoleKey(null);
                                                            }

                                                        }}
                                                        onDropCapture={(event) => {

                                                            // Only intercept external files over the occupied basic role
                                                            if (!basicDesignImageUrl || !DragEventHasFiles(event)) return;

                                                            // Replace the image on this exact basic role
                                                            HandleExternalImageDropForRole(
                                                                event,
                                                                basicDesignRoleKey,
                                                                basicDesignRoleDisplayName
                                                            );

                                                        }}
                                                    >

                                                        {/* Keep the print-area name and its quality warning at the top of its card */}
                                                        <div className="pod-design__design-set-image-title">

                                                            {/* Always reserve the same warning space so titles never shift */}
                                                            <div className="pod-design__quality-warning-slot">

                                                                <PrintAreaQualityWarning
                                                                    printQualitySummary={podDesignDetails?.print_quality_summary}
                                                                    canonicalProducts={canonicalProducts}
                                                                    roleCode={basicDesignRoleKey}
                                                                    roleName={basicDesignRoleDisplayName}
                                                                />

                                                            </div>

                                                            <span>
                                                                {basicDesignRoleDisplayName}
                                                            </span>

                                                        </div>

                                                        <div className="pod-design__design-set-image pod-design__design-set-image--basic"> 

                                                            {/* Display / Upload image */}

                                                            {/* Show image if it's a design OR the choose generic image option if it's a template */}

                                                            {/* If this is a template show 'Choose Generic Image' button */}
                                                            {designOrTemplate === "template" ? 

                                                                <TemplatePreviewImagePicker
                                                                    value={templatePreviewImage}
                                                                    logoUrl={strichLogoUrl}
                                                                    onChange={({ previewConfig, resolvedPayload }) => {

                                                                        // Store picker state if you still want it locally
                                                                        setTemplatePreviewImage(previewConfig);

                                                                        // Upsert the resolved image payload into the role data
                                                                        handleUpsertRoleImage(basicDesignRoleKey, resolvedPayload);
                                                                        
                                                                    }}
                                                                    currentImage={basicDesignImageUrl || null}
                                                                />

                                                            // It's a design so check if there's an image for this basic role 
                                                            : basicDesignImageUrl ?

                                                                <>
                                                                
                                                                    <img
                                                                        src={basicDesignThumbnailUrl}
                                                                        alt={basicDesignRoleKey}
                                                                        title="🔍 Click to Zoom" 
                                                                        onClick={() => openImagePreviewPopup(basicDesignImageUrl)}
                                                                    />
                                                                    
                                                                </>
                                                                
                                                                :
                                                                
                                                                    <>
                                                                        
                                                                        <RenderImageUploader
                                                                            label={basicDesignRoleKey}
                                                                            selectedImage={basicDesignImageUrl || null}
                                                                                onUploaderDragActiveChange={() => {

                                                                                // A direct uploader drag must never leave the page-level overlay active
                                                                                setIsExternalFileDragActive(false);

                                                                            }}
                                                                            onImageChange={(payload) => {

                                                                                // Set the image for the basic design role
                                                                                handleUpsertRoleImage(basicDesignRoleKey, payload);

                                                                            }}
                                                                            disabled={isSubmittingRoleImage}
                                                                        />

                                                                        <br/>

                                                                        <CreateButton 
                                                                            title="Create AI Image" 
                                                                            size="m" 
                                                                            fullWidth={true} 
                                                                            onClick={() => { 

                                                                                // Set the Ai design state with the role
                                                                                setSubmittedAiDesign({role: basicDesignRoleKey});

                                                                                // Set the loading state
                                                                                setIsSubmittingAiDesign(true);

                                                                                // Show the pop up
                                                                                setShowPodDesignAiPage(true);

                                                                            }}
                                                                            disabled={isSubmittingRoleImage}
                                                                        />
                                                                                                                        
                                                                    </>

                                                            }

                                                        </div>

                                                        <div className="pod-design__design-set-image-controls">
                                                        
                                                            {basicDesignImageUrl &&

                                                                <RenderDesignOptions 

                                                                    // Pass role data in the same format as design set (advanced) does
                                                                    role={{ role: basicDesignRoleKey, selected: true }}

                                                                    existingRoleData={

                                                                        // Find role details in podDesignDetails
                                                                        podDesignDetails?.roles?.find((existingRole) => {

                                                                            // Match by basic role key
                                                                            return String(existingRole?.role ?? "") === String(basicDesignRoleKey ?? "");

                                                                        })
                                                                    } 
                                                                    setPodDesignDetails={setPodDesignDetails}
                                                                    handleOpenCustomizrClick={handleOpenCustomizrClick}
                                                                    handleUpsertRoleImage={handleUpsertRoleImage}
                                                                    isCustomizrReady={isCustomizrReady}
                                                                                                                                        setLoadingCustomizrEdit={setLoadingCustomizrEdit}
                                                                    handleDeleteRoleImage={handleDeleteRoleImage}
                                                                    handleOpenUpscaleClick={handleOpenUpscalePopup}
                                                                    isSubmittingRoleImage={isSubmittingRoleImage}

                                                                    // Hide settings which are inherited from an assigned template
                                                                    hideManagedSettings={designProductsManagedByTemplates}
                                                                />

                                                            }
                                                            
                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                            :

                                            ""

                                        }

                                    </div>

                                </div>


                                <div className="design-products"> 

                                    {/* This is the products */}
                                    <div className="card">

                                        <div className="card__header">

                                            <h3>Products</h3>

                                            {designProductsManagedByTemplates &&

                                                <span className="hintText">
                                                    Products for this design are managed by the assigned templates
                                                </span>

                                            }

                                            {!designProductsManagedByTemplates &&

                                                <span className="hintText">
                                                    {designOrTemplate === "template"
                                                        ? "Configure the products and settings inherited by designs using this template"
                                                        : "Select and configure the products you would like assigned to the design"}
                                                </span>

                                            }

                                        </div>

                                        <div className="card__content">

                                            {designProductsManagedByTemplates &&

                                                <div className="pod-design-template-products-managed">

                                                    <strong>Products for this design are managed by the assigned templates.</strong>

                                                    <span>Remove all templates to edit this design's own product settings.</span>

                                                </div>

                                            }

                                            {/* Products List */}
                                            {!designProductsManagedByTemplates &&

                                            <PodProductSelector 
                                                canonicalProducts={canonicalProducts || []}
                                                originalRoles={originalPodDesignDetails?.roles || []}
                                                currentProducts={podDesignDetails?.products || []}
                                                setCurrentProducts={setPodDesignDetails}
                                                currentRoles={podDesignDetails?.roles || []}
                                                basicDesignRoleKey={basicDesignRoleKey}
                                                allRoles={printAreaRoles}
                                                selectedRoles={selectedRoles}
                                                printAreaRoles={printAreaRoles}
                                                designOrTemplate={designOrTemplate}
                                                podDesignDetails={podDesignDetails}
                                                isAddingAdvancedDesignSet={isAddingAdvancedDesignSet}
                                                formValues={formValues}
                                                storeMarketplaceAutoadd={getStoreMarketplaceAutoadd(formValues?.store_id || defaultDesignStoreId)}
                                                isAddingNewDesign={isAddingNewDesign}
                                                prepareDesignForProductOpen={prepareDesignForProductOpen}
                                                productGridChangesPending={productGridChangesPending}
                                                markProductGridChangesPending={() => {

                                                    // Mark only product controls edited on this page
                                                    setProductGridChangesPending(true);

                                                }}
                                                saveDesignAfterAddingProducts={async (savedProducts) => {

                                                    // Use the products returned by the product API so the save cannot receive stale state
                                                    const productsForSave = Array.isArray(savedProducts)
                                                        ? savedProducts
                                                        : (Array.isArray(podDesignDetails?.products) ? podDesignDetails.products : []);

                                                    // Build the latest complete design details
                                                    const nextPodDesignDetails = {
                                                        ...podDesignDetails,
                                                        products: productsForSave,
                                                    };

                                                    // Keep the live design state aligned with the products being saved
                                                    setPodDesignDetails(nextPodDesignDetails);

                                                    // Run the normal complete design save and return its result
                                                    return await handleSubmit(
                                                        {
                                                            preventDefault: () => {},
                                                        },
                                                        {
                                                            type: designOrTemplate,
                                                            podDesignDetails: nextPodDesignDetails,
                                                            productGridChangesPending:
                                                                productGridChangesPending,
                                                        }
                                                    );

                                                }}
                                                onProductFamilyCommitted={(productFamily) => {

                                                    // Keep the live design product family aligned with the API
                                                    setPodDesignDetails((previousDetails) => {

                                                        return replaceConfiguredProductFamilyInDetails(
                                                            previousDetails,
                                                            productFamily
                                                        );

                                                    });

                                                    // Treat the saved product family as the new comparison baseline
                                                    setOriginalPodDesignDetails((previousDetails) => {

                                                        return replaceConfiguredProductFamilyInDetails(
                                                            previousDetails,
                                                            productFamily
                                                        );

                                                    });

                                                }}
                                                isStrichTemplate={isStrichTemplate}
                                            />

                                            }

                                        </div>

                                    </div>

                                </div>


                                {isExternalFileDragActive &&

                                    <div className="pod-design__file-drop-overlay">
                                        <div className="pod-design__file-drop-overlay-inner">
                                            <p>Drop image to fill next empty print area</p>
                                        </div>
                                    </div>

                                }
                                     

                                {/* If the design is being saved show the full loading screen - disables everything else */}
                                {designIsSaving &&

                                    <LoadingScreen 
                                        loadingMessage={saveProgressMessage}
                                        progressPercent={saveProgress}
                                        subMessage={saveProgressSubMessage} 
                                    />

                                }

                                {/* Show the create AI image pop up on the pod design page */}
                                {showPodDesignAiPage && isSubmittingAiDesign &&
                                
                                    <RenderDesignAiPage 
                                        isOpen={showPodDesignAiPage}
                                        onClose={() => {

                                            // Clear the loading state
                                            setIsSubmittingAiDesign(false);

                                            // Close the pop up
                                            setShowPodDesignAiPage(false);

                                        }}

                                    />
                                
                                }

                                {/* Show the upscale image pop up on the pod design page */}
                                {showPodDesignUpscalePage && isSubmittingUpscaledImage &&

                                    renderDesignUpscalePage({
                                        isOpen: showPodDesignUpscalePage,
                                        onClose: () => {

                                            // Clear the temporary upscale data
                                            setSubmittedUpscaledImage(null);

                                            // Clear the loading state
                                            setIsSubmittingUpscaledImage(false);

                                            // Close the pop up
                                            setShowPodDesignUpscalePage(false);

                                        },
                                    })

                                }

                                {/* Show loading screen if we're waiting for the upscaled image to upsert to the design role */}
                                {!showPodDesignUpscalePage && isSubmittingUpscaledImage &&

                                    <LoadingScreen loadingMessage={"Saving Upscaled Image"} subMessage={"We're replacing the design image with the upscaled version."} />

                                }

                                {/* Show loading screen if we're waiting for the ai image to upsert to the design role - we don't want the user to be able to click on anything else while waiting */}
                                {!showPodDesignAiPage && isSubmittingAiDesign &&

                                    <LoadingScreen loadingMessage={"Saving AI Image"} subMessage={"We're uploading your AI image to the design role."} />

                                }

                            </>

                    }

                </div>

                {/* Footer actions */}
                <div className="tabs-footer tabs-footer--no-background horizontal-align-content-center flex-justify-left-right">

                    <div>

                        {!isAddingNewDesign && isStrichTemplate !== null && !isStrichTemplate && 

                            <CreateButton 
                                tooltip={`Delete ${designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}`} 
                                title={isDeleting ? "Deleting..." : `Delete ${designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}`} 
                                size="s" 
                                isDelete={true}
                                icon="/images/icon_bin.png" 
                                onClick={() => handleDeletePodDesign(designOrTemplate, podDesignDetails.id)} 
                                disabled={isDeleting || isLoading || isStrichTemplate}
                            />
                        }

                    </div>

                    <div className="horizontal-align-content-center">

                        {/* Check if there's any real (non-empty) errors on the form and if so display an error message next to the save button */}
                        {Object.values(fieldErrors ?? {}).some((value) => {

                            // If value is a string, only treat it as an error if it has non-whitespace text
                            if (typeof value === "string") {

                                // This checks for a real message
                                return value.trim().length > 0;

                            }

                            // For non-strings (rare), treat truthy values as an error
                            return Boolean(value);

                        }) && (

                            <div style={{ color: "red", alignContent: "center" }} >
                                <p>There is an error with the form<br/> {Object.values(fieldErrors ?? {}).join(", ")}</p>
                            </div>

                        )}

                        {isStrichTemplate &&
                        
                            <div style={{ color: "red", alignContent: "center" }} >
                                <p>This is a Strich template and cannot be edited.</p>
                            </div>

                        }

                        {isStrichTemplate === false && (designHasUnsavedChanges || designIsSaving) &&

                            <CreateButton 
                                title={designIsSaving ? "Saving..." : "Save changes"}
                                tooltip={`Save ${designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}`}
                                type="submit" 
                                form="designDetailsForm" 
                                onClick={(e) => handleSubmit(e, {
                                    type: designOrTemplate,
                                    podDesignDetails:
                                        podDesignDetails,
                                    productGridChangesPending:
                                        productGridChangesPending,
                                })}
                                disabled={designIsSaving || isDeleting || isLoading} 
                            />

                        }
                          
                        <CreateButton
                            title="Close"
                            isCancel={true}
                            invert
                            size="m"
                            onClick={handleCloseDesign}
                            disabled={designIsSaving || isDeleting || isLoading}
                        />

                    </div>
                        
                </div>

            </div>


            {/* Pop up to show carousel of print area images */}
            {imagePreviewPopupOpen && (

                <ImageCarouselPopup
                    images={zoomCarouselImages}
                    initialImage={selectedImageToZoom}
                    onClose={() => setImagePreviewPopupOpen(false)}
                />

            )}


            {loadingCustomizrEdit &&
            
                <LoadingScreen loadingMessage="Loading customizr..." />
                
            }

        </div>

    );

};

export default PodDesignDetailsPopUp;
